import axios from "axios";
import { API_URL, SEMANTIC_RULE_PACKS_URL } from "../constants/index";
import { useAuthStore } from "../stores/authStore";
import { useToastStore } from "../stores/toastStore";
import { extractApiErrors } from "./errorMap";
import {
  normalizeDriftDetailStatus,
  deriveScanCountsFromHistory,
} from "../app/pages/grc/driftNormalization";

axios.defaults.withCredentials = true;

declare global {
  interface Window {
    BE_URL?: string;
    ENCRYPTION_SECRET?: string;
  }
}

let _sessionExpiredAlertShown = false;

export function resetSessionExpiredAlert() {
  _sessionExpiredAlertShown = false;
}

export function handleSessionExpiration() {
  if (_sessionExpiredAlertShown) return;

  _sessionExpiredAlertShown = true;

  // Clear auth store
  useAuthStore.getState().logout();
  sessionStorage.removeItem("accessTokenRefreshCount");

  // Dispatch events to notify AuthGuard and listeners
  window.dispatchEvent(new CustomEvent("user-logout"));

  // Redirect to login if not already on /login
  if (typeof window !== "undefined" && !window.location.pathname.includes("/login")) {
    window.location.href = "/login";
  }
}

window.addEventListener("user-logout", () => {
  _sessionExpiredAlertShown = false;
});
window.addEventListener("session-expired-reset", () => {
  _sessionExpiredAlertShown = false;
});

export function isSessionTokenExpired(): boolean {
  return isSessionTokenExpiringSoon(0);
}

export function isSessionTokenExpiringSoon(thresholdSeconds = 60): boolean {
  const token = useAuthStore.getState().getToken();
  if (!token) return !useAuthStore.getState().isAuthenticated;
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    const expiresAt = (payload.exp || 0) * 1000;
    return Date.now() >= expiresAt - thresholdSeconds * 1000;
  } catch {
    return false;
  }
}

export function isRefreshTokenExpired(): boolean {
  const token = useAuthStore.getState().getRefreshToken();
  if (!token) return !useAuthStore.getState().isAuthenticated;
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    const expiresAt = (payload.exp || 0) * 1000;
    return Date.now() >= expiresAt;
  } catch {
    return false;
  }
}

const BASE_URL = (window.BE_URL || "") + "/api/v1";

export function briefHttpError(
  status: number,
  body?: string,
  statusText?: string,
): string {
  const raw = (body ?? "").trim();
  const isHtml = /<html|<!doctype|<head|<body|<center/i.test(raw);
  switch (status) {
    case 413:
      return "The file is too large to upload. Please choose a smaller file.";
    case 408:
      return "The request timed out. Please try again.";
    case 429:
      return "Too many requests. Please wait a moment and try again.";
    case 405:
      return "This request was rejected by the server (method not allowed). Please report the failing request.";
    case 500:
      return "Something went wrong on the server. Please try again.";
    case 502:
    case 503:
    case 504:
      return "The server is temporarily unavailable. Please try again.";
  }
  if (raw && !isHtml && raw.length <= 300) return raw;
  return `Request failed (${status}${statusText ? ` ${statusText}` : ""}).`;
}

export function getAuthHeaders(): Record<string, string> {
  const token = useAuthStore.getState().getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export const PAG_BASE_URL = (window.BE_URL || "") + "/pag/api/v1";
export const VM_BASE_URL = (window.BE_URL || "") + "/vm/api/v1";
export const CM_BASE_URL = (window.BE_URL || "") + "/cm/api/v1";

let _refreshPromise: Promise<string | null> | null = null;


export async function refreshAccessToken(): Promise<string | null> {
  const currentRefreshToken = useAuthStore.getState().getRefreshToken();
  if (!currentRefreshToken || isRefreshTokenExpired()) {
    return null;
  }

  if (_refreshPromise) {
    return _refreshPromise;
  }

  _refreshPromise = (async () => {
    try {
      const response = await axios.post(
        API_URL.REFRESH,
        { refreshToken: currentRefreshToken },
        { withCredentials: true }
      );

      const respData = response.data;
      const newAccessToken =
        respData?.accessToken ||
        respData?.token ||
        respData?.data?.accessToken ||
        respData?.data?.token;

      const newRefreshToken =
        respData?.refreshToken ||
        respData?.data?.refreshToken ||
        currentRefreshToken;

      if (newAccessToken) {
        useAuthStore.getState().updateTokens(newAccessToken, newRefreshToken);
        resetSessionExpiredAlert();
        return newAccessToken;
      }
      return null;
    } catch (err: any) {
      console.warn("[refreshAccessToken] Refresh request failed:", err?.message);
      return null;
    } finally {
      _refreshPromise = null;
    }
  })();

  return _refreshPromise;
}

export async function apiFetch<T>(
  path: string,
  options?: RequestInit,
  baseUrl: string = BASE_URL,
): Promise<T> {
  const { headers: optHeaders, ...rest } = options ?? {};

  const defaultHeaders: Record<string, string> =
    optHeaders && Object.keys(optHeaders).length === 0
      ? {}
      : { "Content-Type": "application/json" };

  const mergedHeaders: Record<string, string> = {
    ...defaultHeaders,
    ...getAuthHeaders(),
    ...(optHeaders as Record<string, string>),
  };

  let res = await fetch(`${baseUrl}${path}`, {
    credentials: "include",
    headers: mergedHeaders,
    ...rest,
  });

  if (res.status === 401 && !path.includes("/login") && !path.includes("/refresh")) {
    const refreshedToken = await refreshAccessToken();
    if (refreshedToken) {
      // Retry original request silently with the newly received token
      const retryHeaders = {
        ...mergedHeaders,
        Authorization: `Bearer ${refreshedToken}`,
      };
      res = await fetch(`${baseUrl}${path}`, {
        credentials: "include",
        headers: retryHeaders,
        ...rest,
      });
    }
  }

  if (res.status === 401) {
    console.warn(`[401] Unauthorized on ${baseUrl}${path} - session expired`);
    handleSessionExpiration();
    const err = new Error("Session expired. Please login again.");
    err.name = "Unauthorized";
    throw err;
  }

  if (!res.ok) {
    const body = await res.text().catch(() => "");
    let title = res.status === 401 ? "Access Denied" : "Request Failed";
    let detail = briefHttpError(res.status, body, res.statusText);
    let errorCode: string | undefined;
    let parsedJson: any = null;
    try {
      const parsed = JSON.parse(body);
      parsedJson = parsed;
      const firstError = parsed?.errors?.[0] || parsed;
      if (firstError?.title || firstError?.detail || firstError?.message) {
        title = firstError.title || title;
        errorCode = firstError.code;
        detail = firstError.detail || firstError.message || detail;
      }
    } catch {
    }
    const err = new Error(detail);
    err.name = title;
    (err as any).errorCode = errorCode;
    (err as any).status = res.status;
    (err as any).response = { status: res.status, data: parsedJson };
    (err as any).responseBody = parsedJson;
    (err as any).errors = parsedJson?.errors;
    throw err;
  }
  if (res.status === 204 || res.headers.get("content-length") === "0") {
    return undefined as T;
  }
  return res.json() as Promise<T>;
}

export interface ApiEnvelope<T> {
  status: string;
  apiVersion: string;
  requestId: string;
  timestamp: string;
  data: T;
  pagination: { page: number; pageSize: number; total: number } | null;
  errors: { code: string; message: string }[] | null;
}

export interface FrameworkCatalog {
  id: string;
  name: string;
  version: string;
  source: string;
  controlCount: number;
  assessedControlCount: number;
}

export interface InsightDetail {
  id: string;
  label: string;
  metric: string;
  previousMetric?: string;
  status?: string;
  sspName?: string;
}
export interface InsightItem {
  title: string;
  subtitle: string;
  metric: string;
  previousMetric?: string;
  priority?: string;
  category?: string;
  businessImpact?: string;
  recommendedAction?: string;
  details: InsightDetail[];
}
export interface InsightSection {
  type: "worse" | "improved" | "stable" | "worsePoams" | "inactiveUsers";
  label: string;
  count: number;
  items: InsightItem[];
  emptyMessage?: string;
}
export interface TopPoamItem {
  poamItemId: string;
  title: string;
  severity?: string;
  status?: string;
  progressPercent?: number;
  scheduledCompletionDate?: string;
  remediationPlanTitle?: string;
  assignees?: Array<string | { id?: string; name?: string; displayName?: string }>;
  applicationName?: string;
  sspSourceName?: string;
  sspName?: string;
  finding?: string;
  findingTitle?: string;
  findingDescription?: string;
  action?: string;
  actionTitle?: string;
  description?: string;
  milestones?: Array<{ id?: string; title?: string; completed?: boolean }>;
}
export interface ApplicationInsightsData {
  generatedBy?: string;
  worse: InsightSection;
  improved: InsightSection;
  stable: InsightSection;
  topPoamItems?: TopPoamItem[];
  worsePoams?: InsightSection;
  inactiveUsers?: InsightSection;
  complianceScore?: number;
  criticalPoamCount?: number;
}

export interface InsightsFilter {
  groupIds?: string[];
  orgIds?: string[];
}
function insightsFilterQuery(filter?: InsightsFilter): string {
  const params = new URLSearchParams();
  (filter?.groupIds ?? []).forEach((id) => id && params.append("groupId", id));
  (filter?.orgIds ?? []).forEach((id) => id && params.append("orgId", id));
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

export function fetchApplicationInsights(filter?: InsightsFilter): Promise<ApiEnvelope<ApplicationInsightsData>> {
  return apiFetch<ApiEnvelope<ApplicationInsightsData>>(
    `/dashboard/insights/applications${insightsFilterQuery(filter)}`,
  );
}

export interface InsightsCountField {
  value: number;
  description?: string;
  tooltip?: string;
}
export interface InsightsCountGroup {
  title?: string;
  description?: string;
  [field: string]: InsightsCountField | string | undefined;
}
export type InsightsCountsResponse = Record<string, InsightsCountGroup | undefined>;

export function fetchInsightsCounts(filter?: InsightsFilter): Promise<ApiEnvelope<InsightsCountsResponse>> {
  return apiFetch<ApiEnvelope<InsightsCountsResponse>>(
    `/dashboard/insights/counts${insightsFilterQuery(filter)}`,
  );
}

export interface DocumentIngestCounts {
  title?: string;
  description?: string;
  draftDocumentsCount: InsightsCountField;
  inReviewDocumentsCount: InsightsCountField;
  approvedDocumentsCount: InsightsCountField;
  retiredDocumentsCount?: InsightsCountField;
  publishedDocumentsCount?: InsightsCountField;
}

export function fetchDocumentIngestCounts(
  groupId: string,
): Promise<ApiEnvelope<DocumentIngestCounts>> {
  const params = new URLSearchParams({ groupId });
  return apiFetch<ApiEnvelope<DocumentIngestCounts>>(
    `/content-registry/docengine/ingest/counts?${params.toString()}`,
  );
}

export interface GrcSspLink {
  sspId: string;
  name: string;
  shortCode: string;
  passedControls: number;
  failedControls: number;
  notReviewedControls?: number;
  compliancePercent: number;
  totalControlsInSsp: number;
  coveredControls: number;
  coveragePercent: number;
}
export interface GrcControlComplianceTotals {
  passedControls: number;
  failedControls: number;
  notReviewedControls?: number;
  compliancePercent: number;
  totalControlsInSsp: number;
  coveredControls: number;
  coveragePercent: number;
}
export interface GrcControlComplianceSection {
  totals: GrcControlComplianceTotals;
  ssps: GrcSspLink[];
}

export interface GrcRemediationItemRef {
  poamItemId: string;
  sspId: string;
  controlId: string | null;
  controlTitle: string | null;
  failedSince: string;
  dueDate: string | null;
  daysUntilDue: number | null;
}
export interface GrcRemediationBucket {
  count: number;
  items: GrcRemediationItemRef[];
}
export interface GrcRemediationBucketCounts {
  overdue: GrcRemediationBucket;
  noRemediationPlan: GrcRemediationBucket;
  inActiveRemediation: GrcRemediationBucket;
}
export interface GrcSspRemediationRow {
  sspId: string;
  name: string;
  shortCode: string;
  overdue: GrcRemediationBucket;
  noRemediationPlan: GrcRemediationBucket;
  inActiveRemediation: GrcRemediationBucket;
}
export interface GrcRemediationStatusSection {
  totals: GrcRemediationBucketCounts;
  ssps: GrcSspRemediationRow[];
}

export interface GrcPlanRef {
  planId: string;
  planTitle: string;
}
export interface GrcFailedInProfileRow {
  profileId: string | null;
  profileTitle: string | null;
  plans: GrcPlanRef[];
}
export interface GrcControlFailureRow {
  controlId: string;
  controlTitle: string | null;
  evaluations: number;
  failures: number;
  failureRate: number;
  failedInProfiles: GrcFailedInProfileRow[];
}
export interface GrcControlFailureRatesSection {
  controls: GrcControlFailureRow[];
}

export interface GrcComplianceTrendPoint {
  month: string;
  passedControls: number;
  failedControls: number;
  compliancePercent: number;
  diffFromPreviousMonth: number | null;
}
export interface GrcComplianceTrendSection {
  months: GrcComplianceTrendPoint[];
}

export interface GrcUnassessedControlRow {
  controlId: string;
  sspCount: number;
  missingFromPlans: GrcPlanRef[];
}
export interface GrcUnassessedControlsSection {
  totalCount: number;
  controls: GrcUnassessedControlRow[];
}

export interface ApplicationSspsRow {
  applicationId: string;
  applicationName: string;
  sspCount: number;
  controlCompliance: GrcControlComplianceSection;
  remediationStatus: GrcRemediationStatusSection;
  controlFailureRates: GrcControlFailureRatesSection;
  complianceTrend: GrcComplianceTrendSection;
  unassessedControls: GrcUnassessedControlsSection;
}

export interface UnassessedPlanRef {
  planId: string;
  planTitle: string | null;
  sspId: string | null;
  sspName: string | null;
}

export interface UnassessedControlRow {
  controlId: string;
  controlTitle: string | null;
  sspCount: number;
  planCount: number;
  missingFromPlans: UnassessedPlanRef[];
}
export interface UnassessedControlsResponse {
  totalCount: number;
  affected: {
    sspCount: number;
    applicationCount: number;
  };
  controls: UnassessedControlRow[];
}
export interface UnassessedControlsFilter {
  orgIds?: string[];
  applicationId?: string;
  sspId?: string;
}

export function fetchUnassessedControls(
  filter?: UnassessedControlsFilter,
): Promise<ApiEnvelope<UnassessedControlsResponse>> {
  const params = new URLSearchParams();
  (filter?.orgIds ?? []).forEach((id) => id && params.append("org_id", id));
  if (filter?.applicationId) params.set("applicationId", filter.applicationId);
  if (filter?.sspId) params.set("sspId", filter.sspId);
  const qs = params.toString();
  return apiFetch<ApiEnvelope<UnassessedControlsResponse>>(
    `/dashboard/grc/system-owner/unassessed-controls${qs ? `?${qs}` : ""}`,
  );
}

export interface SystemOwnerRemediationAppRow {
  applicationId: string;
  applicationName: string;
  sspCount: number;
  totals: GrcRemediationBucketCounts;
  ssps: GrcSspRemediationRow[];
}
export interface SystemOwnerRemediationResponse {
  applications: SystemOwnerRemediationAppRow[];
}

export function fetchRemediationStatus(
  orgIds?: string[],
): Promise<ApiEnvelope<SystemOwnerRemediationResponse>> {
  const params = new URLSearchParams();
  (orgIds ?? []).forEach((id) => id && params.append("org_id", id));
  const qs = params.toString();
  return apiFetch<ApiEnvelope<SystemOwnerRemediationResponse>>(
    `/dashboard/grc/system-owner/remediation-status${qs ? `?${qs}` : ""}`,
  );
}

export type SystemOwnerControlSeverity = "CRITICAL" | "HIGH" | "MEDIUM" | "LOW";

export interface SystemOwnerControlFailureRow {
  controlId: string;
  controlTitle: string | null;
  severity: SystemOwnerControlSeverity | null;
  evaluations: number;
  failures: number;
  failureRate: number;
  failedInProfiles: GrcFailedInProfileRow[];
}
export interface SystemOwnerControlFailureRatesResponse {
  affected: {
    sspCount: number;
    applicationCount: number;
  };
  controls: SystemOwnerControlFailureRow[];
}
export interface ControlFailureRatesFilter {
  orgIds?: string[];
  applicationId?: string;
  sspId?: string;
}

export function fetchControlFailureRates(
  filter?: ControlFailureRatesFilter,
): Promise<ApiEnvelope<SystemOwnerControlFailureRatesResponse>> {
  const params = new URLSearchParams();
  (filter?.orgIds ?? []).forEach((id) => id && params.append("org_id", id));
  if (filter?.applicationId) params.set("applicationId", filter.applicationId);
  if (filter?.sspId) params.set("sspId", filter.sspId);
  const qs = params.toString();
  return apiFetch<ApiEnvelope<SystemOwnerControlFailureRatesResponse>>(
    `/dashboard/grc/system-owner/control-failure-rates${qs ? `?${qs}` : ""}`,
  );
}

export interface SystemOwnerControlComplianceTotals {
  passedControls: number;
  failedControls: number;
  compliancePercent: number;
  totalControlsInSsp: number;
  coveredControls: number;
  coveragePercent: number;
  notReviewedControls: number;
}
export interface SystemOwnerSspComplianceLink extends SystemOwnerControlComplianceTotals {
  sspId: string;
  name: string;
  shortCode: string | null;
}
export interface SystemOwnerControlComplianceAppRow {
  applicationId: string;
  applicationName: string;
  sspCount: number;
  totals: SystemOwnerControlComplianceTotals;
  ssps: SystemOwnerSspComplianceLink[];
}
export interface SystemOwnerControlComplianceResponse {
  applications: SystemOwnerControlComplianceAppRow[];
}

export function fetchControlCompliance(
  orgIds?: string[],
): Promise<ApiEnvelope<SystemOwnerControlComplianceResponse>> {
  const params = new URLSearchParams();
  (orgIds ?? []).forEach((id) => id && params.append("org_id", id));
  const qs = params.toString();
  return apiFetch<ApiEnvelope<SystemOwnerControlComplianceResponse>>(
    `/dashboard/grc/system-owner/control-compliance${qs ? `?${qs}` : ""}`,
  );
}

export interface SystemOwnerComplianceTrendPoint {
  month: string;
  passedControls: number;
  failedControls: number;
  compliancePercent: number;
  diffFromPreviousMonth: number | null;
}
export interface SystemOwnerComplianceTrendAppRow {
  applicationId: string;
  applicationName: string;
  sspCount: number;
  months: SystemOwnerComplianceTrendPoint[];
}
export interface SystemOwnerComplianceTrendResponse {
  applications: SystemOwnerComplianceTrendAppRow[];
}

export function fetchComplianceTrend(
  trendDays?: number,
  orgIds?: string[],
): Promise<ApiEnvelope<SystemOwnerComplianceTrendResponse>> {
  const params = new URLSearchParams();
  (orgIds ?? []).forEach((id) => id && params.append("org_id", id));
  if (trendDays) params.set("trendDays", String(trendDays));
  const qs = params.toString();
  return apiFetch<ApiEnvelope<SystemOwnerComplianceTrendResponse>>(
    `/dashboard/grc/system-owner/compliance-trend${qs ? `?${qs}` : ""}`,
  );
}

export interface AccessibleApplicationOrg {
  id: string;
  name: string;
  shortName?: string;
}

export async function fetchAccessibleApplications(): Promise<AccessibleApplicationOrg[]> {
  const res = await getAPI(`${API_URL.ACCESSIBLE_ORGS}?module=cmdb&action=view&org_type=APPLICATION`);
  return res?.data?.organizations ?? [];
}

export interface ReviewVelocityWindowCounts {
  last7Days: number;
  last30Days: number;
  last90Days: number;
}
export interface ReviewVelocityAppCounts extends ReviewVelocityWindowCounts {
  lastReviewedAt: string | null;
}
export interface ReviewVelocityAppRow {
  applicationId: string;
  applicationName: string;
  sspCount: number;
  counts: ReviewVelocityAppCounts;
}
export interface ReviewVelocityResponse {
  portfolio: ReviewVelocityWindowCounts;
  applications: ReviewVelocityAppRow[];
}

export function fetchReviewVelocity(
  orgIds?: string[],
): Promise<ApiEnvelope<ReviewVelocityResponse>> {
  const params = new URLSearchParams();
  (orgIds ?? []).forEach((id) => id && params.append("org_id", id));
  const qs = params.toString();
  return apiFetch<ApiEnvelope<ReviewVelocityResponse>>(
    `/dashboard/grc/system-owner/review-velocity${qs ? `?${qs}` : ""}`,
  );
}

export interface AuditorScope {
  groupId?: string;
  orgId?: string;
}

function auditorScopeQuery(scope: AuditorScope): string {
  const params = new URLSearchParams();
  if (scope.groupId) params.set("group_id", scope.groupId);
  else if (scope.orgId) params.set("org_id", scope.orgId);
  return params.toString() ? `?${params.toString()}` : "";
}

export interface AuditorPriorityDriver {
  key: "complianceGap" | "criticalFindings" | "failedControlDensity" | "overdueRemediation";
  label: string;
  weight: number;
  raw: string;
  normalized: number;
  contribution: number;
}

export interface AuditorAttentionPriority {
  score: number;
  drivers: AuditorPriorityDriver[];
  coveragePct: number | null;
  lowCoverage: boolean;
  mustAct: boolean;
  needsAttention: boolean;
  explanation: string;
}

export interface AuditorEntityRiskRow {
  id: string;
  name: string;
  complianceScore: number | null;
  criticalFindings: number;
  criticalFindingsUnmanaged: number;
  failedControls: number;
  totalApplicableControls: number;
  controlsUnmonitored: number;
  overdueRemediation: number;
  pendingAssessments: number;
  completedAssessments: number;
  pendingAssessmentNames: string[];
  completedAssessmentNames: string[];
  totalFindings: number;
  criticalControlsFailed: number;
  failedControlsPrevQuarter: number | null;
  driftDelta: number | null;
  lastAuditDaysAgo: number | null;
  riskScore: number;
  riskSharePct: number;
  priority: AuditorAttentionPriority;
  active: boolean;
}

export interface AuditorUnattributedSource {

  reference: string | null;
  label: string;
  reason:
  | "MISSING_SSP"
  | "NOT_AN_APPLICATION"
  | "UNPUBLISHED_OR_OUT_OF_SCOPE"
  | "NO_REFERENCE"
  | "OUTSIDE_EVERY_ORGANIZATION";
  findings: number;
  assessments: number;
}

export interface AuditorAttribution {
  totalFindings: number;
  attributedFindings: number;
  totalAssessments: number;
  attributedAssessments: number;
  sources: AuditorUnattributedSource[];
}

export interface AuditorEntityRiskResponse {
  scope: "GROUP" | "ORGANIZATION";
  scopeId: string;
  scopeName: string;
  totalEntities: number;
  rows: AuditorEntityRiskRow[];
  attribution: AuditorAttribution | null;
}

export interface AuditorControlFailureRow {
  id: string;
  controlId: string;
  title: string;
  category: string;

  severity: "Critical" | "High" | "Medium" | "Low" | "Unrated";

  orgsAffected: number;
  failureCount: number;
  failureRatePct: number;
  affectedOrgNames: string[];
}

export interface AuditorControlFailureResponse {
  scope: "GROUP" | "ORGANIZATION";
  scopeId: string;
  scopeName: string;
  totalControls: number;
  rows: AuditorControlFailureRow[];
}

export function fetchAuditorEntityRisk(
  scope: AuditorScope,
): Promise<ApiEnvelope<AuditorEntityRiskResponse>> {
  return apiFetch<ApiEnvelope<AuditorEntityRiskResponse>>(
    `/dashboard/auditor/entity-risk${auditorScopeQuery(scope)}`,
  );
}

export function fetchAuditorControlFailures(
  scope: AuditorScope,
): Promise<ApiEnvelope<AuditorControlFailureResponse>> {
  return apiFetch<ApiEnvelope<AuditorControlFailureResponse>>(
    `/dashboard/auditor/control-failures${auditorScopeQuery(scope)}`,
  );
}

export interface StageComment {
  comments?: string;
  fromStage?: string;
  toStage?: string;
  time?: string;
  by?: string;
}

export interface CatalogResponse {
  id: string;
  documentId: string;
  title?: string;
  owningOrgUnitId?: string | null;
  owningOrgUnitName?: string | null;
  owningOrgUnitType?: string | null;
  catalogType?: CatalogType;
  countryName?: string | null;
  countryCode?: string | null;
  status?: "DRAFT" | "ACTIVE" | "DEPRECATED";
  lifecycleStatus?: string;
  version?: string;
  oscalVersion?: string;
  versionNumber?: number;
  createdAt: string;
  updatedAt: string;
  groups?: GroupResponse[];
  controls?: ControlResponse[];
  groupCount?: number;
  controlCount?: number;
  totalGroupCount?: number;
  totalControlCount?: number;
  assessedControl?: number;
  stageComments?: StageComment[];
}

export interface GroupResponse {
  id: string;
  groupId: string;
  title: string;
  clazz: string;
  sortOrder: number;
  groups: GroupResponse[];
  controls: ControlResponse[];
}

export interface PartPayload {
  partId: string;
  name: string;
  ns?: string;
  clazz?: string;
  title?: string | null;
  prose: string;
  resolved_prose?: string;
  sortOrder: number;
  parts: PartPayload[];
}

export interface PartResponse extends PartPayload {
  id: string;
}

export interface ConstraintTest {
  expression: string | null;
  remarks: string | null;
}

export interface ParameterConstraint {
  description: string | null;
  tests: ConstraintTest[] | null;
}

export interface ParameterGuideline {
  prose: string;
}

export interface ParameterSelection {
  "how-many": "one" | "one-or-more";
  choice: string[];
}

export interface ParamPayload {
  paramId: string;
  label: string;
  usage?: string;
  clazz?: string | null;
  constraints?: ParameterConstraint[];
  guidelines?: ParameterGuideline[];
  values?: string[];
  selectConfig?: ParameterSelection | null;
  sortOrder: number;
}

export interface ParamResponse extends ParamPayload {
  id: string;
}

export interface ControlResponse {
  id: string;
  controlId: string;
  title: string;
  clazz: string;
  sortOrder: number;
  parts: PartResponse[];
  params: ParamResponse[];
  enhancements: ControlResponse[];
}

export type CatalogType = "REGULATORY" | "TECHNOLOGY" | "INTEGRATION";

export interface CreateCatalogPayload {
  title: string;
  owningOrgUnitId?: string | null;
  catalogType?: CatalogType;
  countryName?: string | null;
  countryCode?: string | null;
}

export interface CreateGroupPayload {
  groupId: string;
  title: string;
  clazz?: string;
  sortOrder: number;
  parentGroupId?: string;
  parentId?: string;
}

export interface CreateControlPayload {
  controlId: string;
  title: string;
  clazz?: string | null;
  groupId?: string | null;
  parentControlId?: string | null;
  sortOrder: number;
  parts?: (PartPayload & { id?: string })[] | null;
  params?: (ParamPayload & { id?: string })[] | null;
}

export interface MappingPair {
  sourceControlId: string;
  targetControlId: string;
  relationship: "equivalent" | "subset" | "superset" | "related" | "none";
  rationale?: string;
}

export interface ControlMapping {
  id: string;
  title: string;
  sourceCatalogId: string;
  sourceCatalogName: string;
  targetCatalogId: string;
  targetCatalogName: string;
  pairCount: number;
  status: "active" | "draft" | "deprecated";
  createdAt: string;
  updatedAt: string;
  pairs?: MappingPair[];
}

export interface CreateMappingPayload {
  title: string;
  sourceCatalogId: string;
  targetCatalogId: string;
}

export interface ImportMappingPayload {
  oscalJson: string;
  format: "json" | "xml";
  overwrite?: boolean;
}

export interface ImportResult {
  mappingId: string;
  pairsImported: number;
  validationWarnings: string[];
}

export interface LicenseInfo {
  userName: string;
  startDate: string;
  expiryDate: string;
  sspLimit: number;
}

export interface LicenseDetails {
  userName: string;
  licenseKey: string;
  startDate: string;
  expiryDate: string;
  sspLimit: number;
  currentSspCount: number;
  remainingSspQuota: number;
  status: string;
  activatedAt: string;
}

export interface LicenseValidationResponse {
  valid: boolean;
  expired: boolean;
  notYetActive: boolean;
  message: string;
  license: LicenseInfo;
  currentSspCount: number;
}

/* ─────────────────────────────────────────────────────────
   Catalog endpoints
───────────────────────────────────────────────────────── */

/** List all available framework catalogs */
export interface CatalogComparisonMatch {
  controlId?: string;
  sourceControlId?: string;
  sourceImplementationStatus?: string;
  bridgeControlId?: string;
  targetControlId?: string;
}

export interface CatalogComparisonResponse {
  sourceSspId: string;
  targetCatalogId: string;
  bridgeCatalogId?: string;
  totalSourceControls: number;
  totalTargetControls: number;
  targetCatalogComplianceScore?: number;
  summary: {
    totalCommonControls: number;
    totalTargetOnlyControls: number;
    implementedCommonControls: number;
    notImplementedCommonControlsByStatus: Record<string, number>;
    targetCatalogComplianceScore?: number;
  };
  matchesByStatus: Record<string, CatalogComparisonMatch[]>;
  unmappedTargetControls: CatalogComparisonMatch[];
}

export interface CatalogComparisonSummaryRequest {
  sspId: string;
  targetCatalogId: string;
  totalSourceControls: number;
  totalTargetControls: number;
  totalCommonControls: number;
  totalTargetOnlyControls: number;
  implementedCommonControls: number;
  notImplementedCommonControlsByStatus: Record<string, number>;
}

export interface CatalogComparisonSummaryResponse {
  summary?: string;
  narrative?: string;
  text?: string;
  keyFindings?: string[];
  insights?: string[];
  recommendations?: string[];
  [key: string]: unknown;
}

export function summarizeCatalogComparison(
  payload: CatalogComparisonSummaryRequest,
): Promise<ApiEnvelope<CatalogComparisonSummaryResponse | string>> {
  return apiFetch<ApiEnvelope<CatalogComparisonSummaryResponse | string>>(
    "/catalog-comparisons/summary",
    { method: "POST", body: JSON.stringify(payload) },
  );
}

export function compareCatalogs(
  sspId: string,
  targetCatalogId: string,
): Promise<ApiEnvelope<CatalogComparisonResponse>> {
  return apiFetch<ApiEnvelope<CatalogComparisonResponse>>(
    "/catalog-comparisons",
    {
      method: "POST",
      body: JSON.stringify({ sspId, targetCatalogId }),
    },
  );
}

export function listCatalogs(
  catalogType?: CatalogType,
): Promise<ApiEnvelope<CatalogResponse[]>> {
  const qs = catalogType
    ? `?catalog_type=${encodeURIComponent(catalogType)}`
    : "";
  return apiFetch<ApiEnvelope<CatalogResponse[]>>(`/catalogs${qs}`);
}

export interface ListSearchPayload {
  keyword: string;
  sortByColumn?: string | null;
  sortingOrder?: "asc" | "desc" | "";
  filters?: Record<string, string | string[] | null | undefined>;
  draw?: number;
}

export interface PagedSearchResult<T> {
  items: T[];
  totalItems: number;
  typeCounts?: Record<string, number>;
  statusCounts?: Record<string, number>;
  draw: number;
  recordsTotal: number;
  recordsFiltered: number;
  rawData: unknown;
}

function pickListItems<T>(body: unknown): T[] {
  const seek = (v: unknown): T[] | null => {
    if (Array.isArray(v)) return v as T[];
    if (!v || typeof v !== "object") return null;
    const o = v as Record<string, unknown>;
    for (const k of [
      "content",
      "items",
      "records",
      "results",
      "catalogs",
      "profiles",
      "collections",
      "documents",
      "evidence",
      "controls",
      "baselineControls",
      "data",
    ]) {
      const hit = seek(o[k]);
      if (hit) return hit;
    }
    return null;
  };
  return seek(body) ?? [];
}

const LIST_DATA_KEYS = [
  "content",
  "items",
  "records",
  "results",
  "catalogs",
  "profiles",
  "collections",
  "documents",
  "evidence",
  "controls",
  "baselineControls",
  "data",
] as const;

function pickRawData(body: unknown): unknown {
  if (Array.isArray(body)) return body;
  if (!body || typeof body !== "object") return undefined;
  const o = body as Record<string, unknown>;
  for (const k of LIST_DATA_KEYS) {
    if (k in o) return o[k];
  }
  for (const k of ["data", "page", "result"]) {
    const nested = o[k];
    if (nested && typeof nested === "object") {
      const n = nested as Record<string, unknown>;
      for (const dk of LIST_DATA_KEYS) {
        if (dk in n) return n[dk];
      }
    }
  }
  return undefined;
}

function pickNumber(body: unknown, key: string): number | undefined {
  if (!body || typeof body !== "object") return undefined;
  const o = body as Record<string, unknown>;
  if (typeof o[key] === "number") return o[key] as number;
  for (const k of ["pagination", "page", "meta", "data"]) {
    const nested = o[k];
    if (nested && typeof nested === "object") {
      const v = (nested as Record<string, unknown>)[key];
      if (typeof v === "number") return v;
    }
  }
  return undefined;
}

function pickTotalCount(body: unknown, fallback: number): number {
  const keys = [
    "total",
    "totalItems",
    "totalRecords",
    "totalElements",
    "totalCount",
    "count",
  ];
  const seek = (v: unknown, depth: number): number | null => {
    if (!v || typeof v !== "object" || depth > 3) return null;
    const o = v as Record<string, unknown>;
    for (const k of keys) {
      if (typeof o[k] === "number") return o[k] as number;
    }
    for (const k of ["pagination", "page", "meta", "data"]) {
      const hit = seek(o[k], depth + 1);
      if (hit !== null) return hit;
    }
    return null;
  };
  return seek(body, 0) ?? fallback;
}

function pickTypeCounts(body: unknown): Record<string, number> | undefined {
  if (!body || typeof body !== "object") return undefined;
  const o = body as Record<string, unknown>;
  const data = (o.data && typeof o.data === "object" ? o.data : undefined) as Record<string, unknown> | undefined;
  const pagination = (o.pagination && typeof o.pagination === "object" ? o.pagination : undefined) as Record<string, unknown> | undefined;

  const raw = (o.typeCounts ??
    o.catalogTypeCounts ??
    o.statusCounts ??
    data?.typeCounts ??
    data?.statusCounts ??
    data?.summary) as
    | Record<string, unknown>
    | undefined;

  const out: Record<string, number> = {};
  if (raw && typeof raw === "object") {
    for (const [k, v] of Object.entries(raw)) {
      if (typeof v === "number") out[k.toUpperCase().replace(/[\s-]+/g, "_")] = v;
    }
  }

  if (pagination) {
    if (typeof pagination.satisfiedCount === "number") out["SATISFIED"] = pagination.satisfiedCount;
    if (typeof pagination.notSatisfiedCount === "number") out["NOT_SATISFIED"] = pagination.notSatisfiedCount;
    if (typeof pagination.allCount === "number") out["ALL"] = pagination.allCount;
    if (typeof pagination.total === "number") out["TOTAL"] = pagination.total;
    if (typeof pagination.totalCount === "number") out["TOTAL"] = pagination.totalCount;
  }

  return Object.keys(out).length ? out : undefined;
}

function pickStatusCounts(body: unknown): Record<string, number> | undefined {
  if (!body || typeof body !== "object") return undefined;
  const o = body as Record<string, unknown>;
  const data = (o.data && typeof o.data === "object" ? o.data : undefined) as Record<string, unknown> | undefined;
  const pagination = (o.pagination && typeof o.pagination === "object" ? o.pagination : undefined) as Record<string, unknown> | undefined;

  const raw = (o.statusCounts ??
    data?.statusCounts ??
    o.typeCounts ??
    data?.typeCounts ??
    data?.summary) as
    | Record<string, unknown>
    | undefined;

  const out: Record<string, number> = {};
  if (raw && typeof raw === "object") {
    for (const [k, v] of Object.entries(raw)) {
      if (typeof v === "number") {
        out[k] = v;
      }
    }
  }

  if (pagination) {
    if (typeof pagination.satisfiedCount === "number") out["satisfied"] = pagination.satisfiedCount;
    if (typeof pagination.notSatisfiedCount === "number") out["not_satisfied"] = pagination.notSatisfiedCount;
    if (typeof pagination.allCount === "number") out["total"] = pagination.allCount;
    if (typeof pagination.total === "number") out["total"] = pagination.total;
    if (typeof pagination.totalCount === "number") out["total"] = pagination.totalCount;
  }

  return Object.keys(out).length ? out : undefined;
}

function collectFilters(
  raw: Record<string, string | string[] | null | undefined> | undefined,
): Record<string, string | string[]> {
  const out: Record<string, string | string[]> = {};
  for (const [k, v] of Object.entries(raw ?? {})) {
    if (Array.isArray(v)) {
      if (v.length > 0) out[k] = v;
    } else if (v) {
      out[k] = v;
    }
  }
  return out;
}

export async function postPagedSearch<T>(
  resourcePath: string,
  page: number,
  size: number,
  payload: ListSearchPayload,
): Promise<PagedSearchResult<T>> {
  const filters = collectFilters(payload.filters);
  const body = await apiFetch<unknown>(`${resourcePath}/${page}/${size}`, {
    method: "POST",
    body: JSON.stringify({
      keyword: payload.keyword ?? "",
      sortByColumn: payload.sortByColumn ?? "",
      sortingOrder: payload.sortingOrder ?? "",
      ...(payload.draw !== undefined ? { draw: payload.draw } : {}),
      ...filters,
    }),
  });
  const items = pickListItems<T>(body);
  const totalItems = pickTotalCount(body, page * size + items.length);
  const recordsFiltered = pickNumber(body, "recordsFiltered") ?? totalItems;
  return {
    items,
    totalItems,
    typeCounts: pickTypeCounts(body),
    statusCounts: pickStatusCounts(body),
    draw: pickNumber(body, "draw") ?? payload.draw ?? 0,
    recordsTotal: pickNumber(body, "recordsTotal") ?? totalItems,
    recordsFiltered,
    rawData: pickRawData(body),
  };
}

export async function postPagedSearchAbsolute<T>(
  absoluteUrl: string,
  page: number,
  size: number,
  payload: ListSearchPayload,
): Promise<PagedSearchResult<T>> {
  const filters = collectFilters(payload.filters);
  const body = await postAPI(`${absoluteUrl}/${page}/${size}`, {
    keyword: payload.keyword ?? "",
    sortByColumn: payload.sortByColumn ?? "",
    sortingOrder: payload.sortingOrder ?? "",
    ...(payload.draw !== undefined ? { draw: payload.draw } : {}),
    ...filters,
  });
  const items = pickListItems<T>(body);
  const totalItems = pickTotalCount(body, page * size + items.length);
  const recordsFiltered = pickNumber(body, "recordsFiltered") ?? totalItems;
  return {
    items,
    totalItems,
    typeCounts: pickTypeCounts(body),
    statusCounts: pickStatusCounts(body),
    draw: pickNumber(body, "draw") ?? payload.draw ?? 0,
    recordsTotal: pickNumber(body, "recordsTotal") ?? totalItems,
    recordsFiltered,
    rawData: pickRawData(body),
  };
}

export interface CatalogSearchPayload extends ListSearchPayload {
  lifecycleStatus?: string | string[] | null;
  catalogType?: CatalogType | null;
}

export type CatalogSearchResult = PagedSearchResult<CatalogResponse>;

export function searchCatalogs(
  page: number,
  size: number,
  payload: CatalogSearchPayload,
): Promise<CatalogSearchResult> {
  return postPagedSearch<CatalogResponse>("/catalogs", page, size, {
    ...payload,
    filters: {
      ...payload.filters,
      lifecycleStatus: payload.lifecycleStatus,
      catalogType: payload.catalogType,
    },
  });
}

export function searchProfiles(
  page: number,
  size: number,
  payload: ListSearchPayload & { lifecycleStatus?: string | string[] | null },
): Promise<PagedSearchResult<ProfileResponse>> {
  return postPagedSearch<ProfileResponse>("/profiles", page, size, {
    ...payload,
    filters: { ...payload.filters, lifecycleStatus: payload.lifecycleStatus },
  });
}

export function searchMappingCollections(
  page: number,
  size: number,
  payload: ListSearchPayload & { lifecycleStatus?: string | string[] | null },
): Promise<PagedSearchResult<MappingCollectionSummary>> {
  return postPagedSearch<MappingCollectionSummary>(
    "/mapping-collections",
    page,
    size,
    {
      ...payload,
      filters: { ...payload.filters, lifecycleStatus: payload.lifecycleStatus },
    },
  );
}

export function searchComponentDefinitions(
  page: number,
  size: number,
  payload: ListSearchPayload & { lifecycleStatus?: string | string[] | null },
): Promise<PagedSearchResult<ComponentDefinitionSummaryResponse>> {
  return postPagedSearch<ComponentDefinitionSummaryResponse>(
    "/component-definitions",
    page,
    size,
    { ...payload, filters: { lifecycleStatus: payload.lifecycleStatus } },
  );
}

export function searchSSPs(
  page: number,
  size: number,
  payload: ListSearchPayload & { lifecycleStatus?: string | string[] | null },
): Promise<PagedSearchResult<SspSummaryResponse>> {
  return postPagedSearch<SspSummaryResponse>("/ssps", page, size, {
    ...payload,
    filters: { lifecycleStatus: payload.lifecycleStatus },
  });
}

export function searchPoams(
  page: number,
  size: number,
  payload: ListSearchPayload & { hasItems?: boolean },
): Promise<PagedSearchResult<ApiPoam>> {
  return postPagedSearch<ApiPoam>("/poams", page, size, {
    ...payload,
    filters: { hasItems: payload.hasItems ? "true" : null },
  });
}

export function searchAssessmentPlans(
  page: number,
  size: number,
  payload: ListSearchPayload & {
    status?: string | string[] | null;
    reviewCompleted?: boolean;
  },
): Promise<PagedSearchResult<ApiAssessmentPlan>> {
  return postPagedSearch<ApiAssessmentPlan>("/assessment-plans", page, size, {
    ...payload,
    filters: {
      status: payload.status,
      reviewCompleted:
        payload.reviewCompleted === undefined
          ? null
          : String(payload.reviewCompleted),
    },
  });
}

export const ASSESSMENT_PLAN_SORT_COLUMNS: Record<string, string> = {
  name: "title",
  ssp: "sspTitle",
  controls: "controlCount",
  startDate: "startDate",
  endDate: "endDate",
  status: "status",
};

export function searchCMDBApplications(
  page: number,
  size: number,
  payload: ListSearchPayload & {
    orgId?: string | string[] | null;
    divisionId?: string | string[] | null;
    departmentId?: string | string[] | null;
  },
): Promise<PagedSearchResult<CMDBApplication>> {
  return postPagedSearch<CMDBApplication>("/cmdb/applications", page, size, {
    ...payload,
    filters: {
      orgId: payload.orgId,
      divisionId: payload.divisionId,
      departmentId: payload.departmentId,
    },
  });
}

export function searchCiDevices(
  page: number,
  size: number,
  payload: ListSearchPayload & {
    type?: string | string[] | null;
    orgId?: string | string[] | null;
    applicationId?: string | string[] | null;
    application?: string | string[] | null;
    environment?: string | string[] | null;
    osName?: string | string[] | null;
  },
): Promise<PagedSearchResult<DeviceResponse>> {
  return postPagedSearch<DeviceResponse>("/cmdb/devices", page, size, {
    ...payload,
    filters: {
      type: payload.type,
      orgId: payload.orgId,
      applicationId: payload.applicationId,
      application: payload.application,
      environment: payload.environment,
      osName: payload.osName,
      ...payload.filters,
    },
  });
}

export function searchServices(
  page: number,
  size: number,
  payload: ListSearchPayload & { status?: string | null },
): Promise<PagedSearchResult<ServiceResponse>> {
  return postPagedSearch<ServiceResponse>("/cmdb/services", page, size, {
    ...payload,
    filters: { status: payload.status },
  });
}

export function searchCloudProviders(
  page: number,
  size: number,
  payload: ListSearchPayload & {
    provider?: string | null;
    applicationId?: string | null;
  },
): Promise<PagedSearchResult<CloudProviderResponse>> {
  return postPagedSearch<CloudProviderResponse>("/cmdb/ci/cloud", page, size, {
    ...payload,
    filters: {
      provider: payload.provider,
      applicationId: payload.applicationId,
    },
  });
}

export function getCatalog(id: string): Promise<ApiEnvelope<CatalogResponse>> {
  return apiFetch<ApiEnvelope<CatalogResponse>>(`/catalogs/${id}`);
}

export function updateCatalog(
  id: string,
  payload: {
    title?: string;
    catalogType?: CatalogType;
    countryName?: string | null;
    countryCode?: string | null;
  },
): Promise<ApiEnvelope<CatalogResponse>> {
  return apiFetch<ApiEnvelope<CatalogResponse>>(`/catalogs/${id}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export function createCatalog(
  payload: CreateCatalogPayload,
): Promise<ApiEnvelope<CatalogResponse>> {
  return apiFetch<ApiEnvelope<CatalogResponse>>("/catalogs", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function deleteCatalog(id: string, reason?: string): Promise<void> {
  const qs = reason ? `?reason=${encodeURIComponent(reason)}` : "";
  return apiFetch<void>(`/catalogs/${id}${qs}`, { method: "DELETE" });
}

export interface ExportJobResponse {
  jobId: string;
  status: "pending" | "completed" | "failed";
  documentId: string;
  documentTitle?: string;
  format: string;
  downloadUrl: string | null;
  startedAt: string;
  completedAt: string | null;
}

export function exportCatalogDoc(
  documentId: string,
  format: "json" | "xml" | "yaml" = "json",
): Promise<ApiEnvelope<ExportJobResponse>> {
  return apiFetch<ApiEnvelope<ExportJobResponse>>(`/export/${documentId}`, {
    method: "POST",
    body: JSON.stringify({ format }),
  });
}

export function getExportJob(
  jobId: string,
): Promise<ApiEnvelope<ExportJobResponse>> {
  return apiFetch<ApiEnvelope<ExportJobResponse>>(`/export/${jobId}/download`);
}

export function getExportDownloadUrl(jobId: string): string {
  return `${BASE_URL}/export/${jobId}/download/file`;
}

export function listGroups(
  catalogId: string,
): Promise<ApiEnvelope<GroupResponse[]>> {
  return apiFetch<ApiEnvelope<GroupResponse[]>>(
    `/catalogs/${catalogId}/groups`,
  );
}

export function createGroup(
  catalogId: string,
  payload: CreateGroupPayload,
): Promise<ApiEnvelope<GroupResponse>> {
  return apiFetch<ApiEnvelope<GroupResponse>>(`/catalogs/${catalogId}/groups`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function deleteGroup(catalogId: string, groupId: string): Promise<void> {
  return apiFetch<void>(`/catalogs/${catalogId}/groups/${groupId}`, {
    method: "DELETE",
  });
}

export function listControls(
  catalogId: string,
  params?: { flat?: boolean; group_id?: string },
): Promise<ApiEnvelope<ControlResponse[]>> {
  const qs = new URLSearchParams();
  if (params?.flat) qs.set("flat", "true");
  if (params?.group_id) qs.set("group_id", params.group_id);
  const query = qs.toString();
  return apiFetch<ApiEnvelope<ControlResponse[]>>(
    `/catalogs/${catalogId}/controls${query ? `?${query}` : ""}`,
  );
}

export function getControl(
  catalogId: string,
  controlId: string,
): Promise<ApiEnvelope<ControlResponse>> {
  return apiFetch<ApiEnvelope<ControlResponse>>(
    `/catalogs/${catalogId}/controls/${controlId}`,
  );
}

export function createControl(
  catalogId: string,
  payload: CreateControlPayload,
): Promise<ApiEnvelope<ControlResponse>> {
  return apiFetch<ApiEnvelope<ControlResponse>>(
    `/catalogs/${catalogId}/controls`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
  );
}

export function deleteControlApi(
  catalogId: string,
  controlId: string,
  reason?: string,
): Promise<void> {
  const params = new URLSearchParams();
  if (reason?.trim()) params.set("reason", reason.trim());
  const query = params.toString();
  return apiFetch<void>(`/catalogs/${catalogId}/controls/${controlId}${query ? `?${query}` : ""}`, {
    method: "DELETE",
  });
}

export interface ImportJobResponse {
  id: string;
  jobStatus: string;
  importMode: string;
  documentType: string;
  sourceFilename?: string;
  groupCount: string;
  startedAt: string;
  createdAt: string;
}

export interface ValidateResponse {
  documentType: string;
  oscalVersion: string;
  valid?: boolean;
  message?: string;
  findings: {
    code?: string;
    severity: string;
    message: string;
    title?: string;
    detail?: string;
  }[];
}

export function validateImport(
  file: File,
  catalogType?: CatalogType,
  country?: { countryName: string; countryCode: string },
): Promise<ApiEnvelope<ValidateResponse>> {
  const formData = new FormData();
  formData.append("file", file);
  if (catalogType) formData.append("catalogType", catalogType);
  if (country) {
    formData.append("countryName", country.countryName);
    formData.append("countryCode", country.countryCode);
  }
  return fetch(`${BASE_URL}/import/validate`, {
    method: "POST",
    headers: { Accept: "*/*", ...getAuthHeaders() },
    body: formData,
  }).then(async (res) => {
    const json = await res.json();
    if (!res.ok || json.status === "error") {
      const errMsg =
        json.errors?.[0]?.detail ??
        json.errors?.[0]?.title ??
        "Validation failed";
      throw new Error(errMsg);
    }
    return json as ApiEnvelope<ValidateResponse>;
  });
}

export function validateImportUrl(
  url: string,
  country?: { countryName: string; countryCode: string },
): Promise<ApiEnvelope<ValidateResponse>> {
  const formData = new FormData();
  formData.append("url", url);
  if (country) {
    formData.append("countryName", country.countryName);
    formData.append("countryCode", country.countryCode);
  }
  return fetch(`${BASE_URL}/import/validate`, {
    method: "POST",
    headers: { Accept: "*/*", ...getAuthHeaders() },
    body: formData,
  }).then(async (res) => {
    const json = await res.json();
    if (!res.ok || json.status === "error") {
      const errMsg =
        json.errors?.[0]?.detail ??
        json.errors?.[0]?.title ??
        "Validation failed";
      throw new Error(errMsg);
    }
    return json as ApiEnvelope<ValidateResponse>;
  });
}

export function importCatalog(
  file: File,
  mode: "full" | "delta" = "full",
  owningOrgUnitId?: string,
): Promise<ApiEnvelope<ImportJobResponse>> {
  const formData = new FormData();
  formData.append("file", file);
  let url = `${BASE_URL}/import?resource=catalog&mode=${mode}&auto_migrate=true`;
  if (owningOrgUnitId) {
    url += `&owning_org_unit_id=${owningOrgUnitId}`;
  }
  return fetch(url, {
    method: "POST",
    headers: { Accept: "*/*", ...getAuthHeaders() },
    body: formData,
  }).then(async (res) => {
    if (!res.ok) {
      const body = await res.text().catch(() => "");
      let title = "Import Failed";
      let detail = res.statusText;
      try {
        const parsed = JSON.parse(body);
        const src = parsed?.errors?.[0] ?? parsed;
        if (src?.title) title = src.title;
        if (src?.detail || src?.message) detail = src.detail || src.message;
      } catch {
        if (body) detail = body;
      }
      const err = new Error(detail);
      err.name = title;
      throw err;
    }
    return res.json() as Promise<ApiEnvelope<ImportJobResponse>>;
  });
}

export interface ImportSessionResponse {
  id: string;
  jobStatus: string;
  importMode: string;
  documentType: string;
  sourceFilename: string;
  sourceContentHash?: string;
  sourceOscalVersion?: string;
  initiatedBy?: string;
  startedAt: string;
  findings: {
    id: string;
    severity: string;
    code: string;
    title: string;
    detail: string;
    autoRepairable: boolean;
    repaired: boolean;
    sortOrder: number;
    createdAt: string;
  }[];
  references: unknown[];
  createdAt: string;
  updatedAt: string;
}

export function getImportSession(
  sessionId: string,
): Promise<ApiEnvelope<ImportSessionResponse>> {
  return apiFetch<ApiEnvelope<ImportSessionResponse>>(
    `/import/sessions/${sessionId}`,
  );
}

export function abortImportSession(
  sessionId: string,
): Promise<ApiEnvelope<ImportSessionResponse>> {
  return apiFetch<ApiEnvelope<ImportSessionResponse>>(
    `/import/sessions/${sessionId}/abort`,
    {
      method: "POST",
    },
  );
}

export function commitImportSession(
  sessionId: string,
  owningOrgUnitId?: string | null,
  catalogType?: CatalogType,
  country?: { countryName: string; countryCode: string },
): Promise<ApiEnvelope<ImportJobResponse>> {
  const payload: {
    owningOrgUnitId?: string;
    catalogType?: CatalogType;
    countryName?: string;
    countryCode?: string;
  } = {};
  if (owningOrgUnitId) payload.owningOrgUnitId = owningOrgUnitId;
  if (catalogType) payload.catalogType = catalogType;
  if (country) {
    payload.countryName = country.countryName;
    payload.countryCode = country.countryCode;
  }
  return apiFetch<ApiEnvelope<ImportJobResponse>>(
    `/import/sessions/${sessionId}/commit`,
    {
      method: "POST",
      body: Object.keys(payload).length
        ? JSON.stringify(payload)
        : undefined,
    },
  );
}

export function validateMappingImport(
  file: File,
  opts?: { sourceCatalogId?: string; targetCatalogId?: string },
): Promise<ApiEnvelope<ValidateResponse>> {
  const formData = new FormData();
  formData.append("file", file);
  const params = new URLSearchParams();
  if (opts?.sourceCatalogId)
    params.set("source_catalog_id", opts.sourceCatalogId);
  if (opts?.targetCatalogId)
    params.set("target_catalog_id", opts.targetCatalogId);
  const qs = params.toString();
  return fetch(`${BASE_URL}/import/validate${qs ? `?${qs}` : ""}`, {
    method: "POST",
    headers: { Accept: "*/*", ...getAuthHeaders() },
    body: formData,
  }).then(async (res) => {
    const json = await res.json();
    if (!res.ok || json.status === "error") {
      const errMsg =
        json.error?.detail ??
        json.errors?.[0]?.detail ??
        json.errors?.[0]?.title ??
        "Validation failed";
      throw new Error(errMsg);
    }
    return json as ApiEnvelope<ValidateResponse>;
  });
}

export interface MappingImportStageResponse {
  id: string;
  jobStatus: string;
  documentType: string;
  sourceFilename: string;
}

export function stageMappingImport(
  file: File,
  opts: {
    owningOrgUnitId?: string;
    mode?: "full" | "delta";
    sourceCatalogId?: string;
    targetCatalogId?: string;
  },
): Promise<ApiEnvelope<MappingImportStageResponse>> {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("resource", "control_mapping");
  formData.append("mode", opts.mode ?? "full");
  if (opts.owningOrgUnitId)
    formData.append("owning_org_unit_id", opts.owningOrgUnitId);
  if (opts.sourceCatalogId)
    formData.append("source_catalog_id", opts.sourceCatalogId);
  if (opts.targetCatalogId)
    formData.append("target_catalog_id", opts.targetCatalogId);
  return fetch(`${BASE_URL}/import`, {
    method: "POST",
    headers: { Accept: "*/*", ...getAuthHeaders() },
    body: formData,
  }).then(async (res) => {
    if (!res.ok) {
      const body = await res.text().catch(() => "");
      let title = "Import Failed";
      let detail = res.statusText;
      try {
        const parsed = JSON.parse(body);
        const src = parsed?.error ?? parsed?.errors?.[0] ?? parsed;
        if (src?.title) title = src.title;
        if (src?.detail || src?.message) detail = src.detail || src.message;
      } catch {
        if (body) detail = body;
      }
      const err = new Error(detail);
      err.name = title;
      throw err;
    }
    return res.json() as Promise<ApiEnvelope<MappingImportStageResponse>>;
  });
}

export interface MappingCommitResponse {
  id: string;
  jobStatus: string;
  targetDocumentId?: string;
  documentType: string;
}

export function commitMappingImportSession(
  sessionId: string,
  payload: {
    owningOrgUnitId?: string | null;
    sourceCatalogId?: string;
    targetCatalogId?: string;
  },
): Promise<ApiEnvelope<MappingCommitResponse>> {
  const body: Record<string, string> = {};
  if (payload.owningOrgUnitId) body.owningOrgUnitId = payload.owningOrgUnitId;
  if (payload.sourceCatalogId) body.sourceCatalogId = payload.sourceCatalogId;
  if (payload.targetCatalogId) body.targetCatalogId = payload.targetCatalogId;
  return apiFetch<ApiEnvelope<MappingCommitResponse>>(
    `/import/sessions/${sessionId}/commit`,
    { method: "POST", body: JSON.stringify(body) },
  );
}

export interface DocumentResponse {
  id: string;
  title: string;
  documentType: string;
  lifecycleStatus: string;
  owningOrgUnitName?: string;
  owningOrgUnitType?: string;
  createdAt: string;
  updatedAt: string;
  stageComments?: StageComment[];
}

export function getDocument(
  documentId: string,
): Promise<ApiEnvelope<DocumentResponse>> {
  return apiFetch<ApiEnvelope<DocumentResponse>>(`/documents/${documentId}`);
}

export function renameDocument(
  documentId: string,
  title: string,
  extra?: { catalogType?: CatalogType },
): Promise<ApiEnvelope<DocumentResponse>> {
  return apiFetch<ApiEnvelope<DocumentResponse>>(`/documents/${documentId}`, {
    method: "PATCH",
    body: JSON.stringify({ title, ...extra }),
  });
}

export interface TransitionPayload {
  targetStatus: string;
  comments?: string;
}

export interface TransitionResponse {
  id: string;
  documentId: string;
  lifecycleStatus: string;
  previousStatus: string;
  comments: string | null;
  createdAt: string;
}

export function getDocumentTransitions(
  documentId: string,
): Promise<ApiEnvelope<TransitionResponse[]>> {
  return apiFetch<ApiEnvelope<TransitionResponse[]>>(
    `/documents/${documentId}/transitions`,
  );
}

export function transitionDocument(
  documentId: string,
  payload: TransitionPayload,
): Promise<ApiEnvelope<TransitionResponse>> {
  return apiFetch<ApiEnvelope<TransitionResponse>>(
    `/documents/${documentId}/transitions`,
    { method: "POST", body: JSON.stringify(payload) },
  );
}

export function importCatalogFromUrl(
  url: string,
  owningOrgUnitId?: string,
): Promise<ApiEnvelope<ImportJobResponse>> {
  let importUrl = "/import?resource=catalog";
  if (owningOrgUnitId) {
    importUrl += `&owning_org_unit_id=${owningOrgUnitId}`;
  }
  return apiFetch<ApiEnvelope<ImportJobResponse>>(importUrl, {
    method: "POST",
    body: JSON.stringify({ url }),
  });
}

export function importProfile(
  file: File,
  mode: "full" | "delta" = "full",
  owningOrgUnitId?: string,
): Promise<ApiEnvelope<ImportJobResponse>> {
  const formData = new FormData();
  formData.append("file", file);
  let url = `${BASE_URL}/import?resource=profile&mode=${mode}&auto_migrate=true`;
  if (owningOrgUnitId) {
    url += `&owning_org_unit_id=${owningOrgUnitId}`;
  }
  return fetch(url, {
    method: "POST",
    headers: { Accept: "*/*", ...getAuthHeaders() },
    body: formData,
  }).then(async (res) => {
    if (!res.ok) {
      const body = await res.text().catch(() => "");
      let title = "Import Failed";
      let detail = res.statusText;
      try {
        const parsed = JSON.parse(body);
        const src = parsed?.errors?.[0] ?? parsed;
        if (src?.title) title = src.title;
        if (src?.detail || src?.message) detail = src.detail || src.message;
      } catch {
        if (body) detail = body;
      }
      const err = new Error(detail);
      err.name = title;
      throw err;
    }
    return res.json() as Promise<ApiEnvelope<ImportJobResponse>>;
  });
}

export function importProfileFromUrl(
  url: string,
  owningOrgUnitId?: string,
): Promise<ApiEnvelope<ImportJobResponse>> {
  let importUrl = "/import?resource=profile";
  if (owningOrgUnitId) {
    importUrl += `&owning_org_unit_id=${owningOrgUnitId}`;
  }
  return apiFetch<ApiEnvelope<ImportJobResponse>>(importUrl, {
    method: "POST",
    body: JSON.stringify({ url }),
  });
}

export function importComponent(
  file: File,
  mode: "full" | "delta" = "full",
  owningOrgUnitId?: string,
): Promise<ApiEnvelope<ImportJobResponse>> {
  const formData = new FormData();
  formData.append("file", file);
  let url = `${BASE_URL}/import?resource=component&mode=${mode}&auto_migrate=true`;
  if (owningOrgUnitId) {
    url += `&owning_org_unit_id=${owningOrgUnitId}`;
  }
  return fetch(url, {
    method: "POST",
    headers: { Accept: "*/*", ...getAuthHeaders() },
    body: formData,
  }).then(async (res) => {
    if (!res.ok) {
      const body = await res.text().catch(() => "");
      let title = "Import Failed";
      let detail = res.statusText;
      try {
        const parsed = JSON.parse(body);
        const firstError = parsed?.errors?.[0] || parsed;
        if (firstError?.title || firstError?.detail) {
          title = firstError.title || title;
          detail = firstError.detail || firstError.message || detail;
        }
      } catch {
        if (body) detail = body;
      }
      const err = new Error(detail);
      err.name = title;
      throw err;
    }
    return res.json() as Promise<ApiEnvelope<ImportJobResponse>>;
  });
}

export function importSSP(
  file: File,
  mode: "full" | "delta" = "full",
  owningOrgUnitId?: string,
): Promise<ApiEnvelope<ImportJobResponse>> {
  const formData = new FormData();
  formData.append("file", file);
  let url = `${BASE_URL}/import?resource=ssp&mode=${mode}&auto_migrate=true`;
  if (owningOrgUnitId) {
    url += `&owning_org_unit_id=${owningOrgUnitId}`;
  }
  return fetch(url, {
    method: "POST",
    headers: { Accept: "*/*", ...getAuthHeaders() },
    body: formData,
  }).then(async (res) => {
    if (!res.ok) {
      const body = await res.text().catch(() => "");
      let title = "Import Failed";
      let detail = res.statusText;
      try {
        const parsed = JSON.parse(body);
        const firstError = parsed?.errors?.[0] || parsed;
        if (firstError?.title || firstError?.detail) {
          title = firstError.title || title;
          detail = firstError.detail || firstError.message || detail;
        }
      } catch {
        if (body) detail = body;
      }
      const err = new Error(detail);
      err.name = title;
      throw err;
    }
    return res.json() as Promise<ApiEnvelope<ImportJobResponse>>;
  });
}

export interface ProfileResponse {
  id: string;
  documentId: string;
  title: string;
  oscalVersion: string;
  lifecycleStatus: string;
  resolved: boolean;
  versionNumber: number;
  owningOrgUnitId?: string | null;
  owningOrgUnitName?: string | null;
  owningOrgUnitType?: string | null;
  importCount: number;
  groupCount?: number;
  controlCount?: number;
  assessedControlCount?: number;
  createdAt: string;
  updatedAt: string;
}

export interface ProfileImport {
  id: string;
  href: string;
  sourceType?: "CATALOG" | "PROFILE";
  sourceId?: string;
  sourceTitle?: string;
  includeControls: { "with-ids"?: string[]; "with-child-controls"?: string }[];
  excludeControls: { "with-ids"?: string[]; "with-child-controls"?: string }[];
  sortOrder: number;
  controlDetails?: ProfileControlDetail[];
  catalogId?: string;
}

export interface ProfileControlDetail {
  controlId: string;
  controlName: string;
  groupId: string;
  groupName: string;
  hasParameters?: boolean;
}

export interface ProfileDetailResponse {
  id: string;
  documentId: string;
  title: string;
  oscalVersion: string;
  lifecycleStatus: string;
  versionNumber: number;
  owningOrgUnitId?: string | null;
  owningOrgUnitName?: string | null;
  owningOrgUnitType?: string | null;
  imports: ProfileImport[];
  merge: {
    id: string;
    combineMethod: string;
    structuringDirective: string;
    custom: unknown | null;
  } | null;
  modify: unknown | null;
  createdAt: string;
  updatedAt: string;
  stageComments?: StageComment[];
}

export interface CreateProfilePayload {
  title: string;
  owningOrgUnitId?: string | null;
  imports: {
    href: string;
    sourceType?: "CATALOG" | "PROFILE";
    includeControls: { "with-ids": string[] }[];
    excludeControls: { "with-ids": string[] }[];
    sortOrder: number;
  }[];
}

export function createProfile(
  payload: CreateProfilePayload,
): Promise<ApiEnvelope<ProfileDetailResponse>> {
  return apiFetch<ApiEnvelope<ProfileDetailResponse>>("/profiles", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function listProfiles(): Promise<ApiEnvelope<ProfileResponse[]>> {
  return apiFetch<ApiEnvelope<ProfileResponse[]>>("/profiles");
}
export function listCompProfiles(): Promise<ApiEnvelope<ProfileResponse[]>> {
  return apiFetch<ApiEnvelope<ProfileResponse[]>>(
    `/component-definitions/profiles`,
  );
}

export function getProfile(
  id: string,
): Promise<ApiEnvelope<ProfileDetailResponse>> {
  return apiFetch<ApiEnvelope<ProfileDetailResponse>>(`/profiles/${id}`);
}

export function getProfileByDocumentId(
  documentId: string,
): Promise<ApiEnvelope<ProfileDetailResponse>> {
  return apiFetch<ApiEnvelope<ProfileDetailResponse>>(
    `/profiles/by-document/${documentId}`,
  );
}

export interface SetParameter {
  "param-id": string;
  label?: string;
  usage?: string;
  class?: string;
  values: string[];
  constraints?: {
    description: string;
    tests?: { expression: string; remarks?: string }[];
  }[];
  guidelines?: { prose: string }[];
  select?: {
    "how-many": string;
    choice: string[];
  } | null;
}

export interface UpdateProfilePayload {
  title?: string;
  imports?: {
    href: string;
    sourceType?: "CATALOG" | "PROFILE";
    includeControls: { "with-ids": string[] }[];
    excludeControls: { "with-ids": string[] }[];
    sortOrder: number;
  }[];
  merge?: {
    combineMethod: "use-first" | "keep";
    structuringDirective: "flat" | "as-is" | "custom";
    custom?: {
      "insert-controls"?: {
        order?: "keep" | "ascending" | "descending";
        "include-all"?: Record<string, unknown>;
        "include-controls"?: { "with-id": string }[];
        "exclude-controls"?: { "with-id": string }[];
      }[];
    } | null;
  };
  modify?: {
    setParameters?: SetParameter[];
    alters?: {
      "control-id": string;
      adds?: {
        position?: string;
        "by-id"?: string;
        name: string;
        title?: string;
        prose: string;
        ns?: string;
        class?: string;
      }[];
      removes?: {
        "by-id"?: string;
        name?: string;
        ns?: string;
        class?: string;
      }[];
    }[];
  };
}

export function updateProfile(
  id: string,
  payload: UpdateProfilePayload,
): Promise<ApiEnvelope<ProfileDetailResponse>> {
  return apiFetch<ApiEnvelope<ProfileDetailResponse>>(`/profiles/${id}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export interface OverlappingControlSource {
  importId: string;
  sourceType: "CATALOG" | "PROFILE";
  sourceTitle: string;
}

export interface OverlappingControl {
  controlId: string;
  controlTitle: string;
  sourceCount: number;
  sources: OverlappingControlSource[];
}

export interface ImportSummary {
  importId: string;
  sourceTitle: string;
  totalControls: number;
  uniqueControls: number;
  overlappingControls: number;
  overlappingControlIds: string[];
}

export interface CurrentMergeConfig {
  id: string;
  combineMethod: string;
  structuringDirective: string;
  custom: unknown | null;
}

export interface ResolvedControlItem {
  controlId: string;
  controlName: string;
  groupId: string | null;
  groupName: string | null;
  hasParameters: boolean;
  parameters: unknown[];
}

export interface MergeAnalysisResponse {
  profileId: string;
  totalImports: number;
  totalUniqueControls: number;
  totalOverlappingControls: number;
  totalIncluded: number;
  currentMergeConfig: CurrentMergeConfig | null;
  overlappingControls: OverlappingControl[];
  importSummaries: ImportSummary[];
  resolvedControls: ResolvedControlItem[];
  overallTotalControlCountOfProfile: number;
}

export function getProfileMergeAnalysis(
  id: string,
  combineMethod?: "use-first" | "keep",
): Promise<ApiEnvelope<MergeAnalysisResponse>> {
  const query = combineMethod
    ? `?combineMethod=${encodeURIComponent(combineMethod)}`
    : "";
  return apiFetch<ApiEnvelope<MergeAnalysisResponse>>(
    `/profiles/${id}/merge-analysis${query}`,
  );
}

export interface AvailableParameter {
  paramId: string;
  controlId: string;
  controlTitle: string;
  controlStatement?: string;
  controlGuidance?: string;
  label: string;
  usage?: string;
  clazz?: string | null;
  values?: string[];
  constraints?: ParameterConstraint[];
  guidelines?: { prose: string }[];
  selectConfig?: ParameterSelection | null;
}

export function getProfileAvailableParameters(
  id: string,
): Promise<ApiEnvelope<AvailableParameter[]>> {
  return apiFetch<ApiEnvelope<AvailableParameter[]>>(
    `/profiles/${id}/available-parameters`,
  );
}

export interface AvailableControlPart {
  partId: string;
  name: string;
  title: string | null;
  hasProse: boolean;
}

export interface AvailableControl {
  controlId: string;
  controlTitle: string;
  groupId?: string;
  groupTitle?: string;
  parts: AvailableControlPart[];
  hasAlter: boolean;
}

export function getProfileAvailableControls(
  id: string,
): Promise<ApiEnvelope<AvailableControl[]>> {
  return apiFetch<ApiEnvelope<AvailableControl[]>>(
    `/profiles/${id}/available-controls`,
  );
}

export function deleteProfile(id: string, reason?: string): Promise<void> {
  const qs = reason ? `?reason=${encodeURIComponent(reason)}` : "";
  return apiFetch<void>(`/profiles/${id}${qs}`, { method: "DELETE" });
}

export interface ResolvedControlPart {
  ns?: string;
  name: string;
  prose?: string;
  part_id?: string;
  partId?: string;
  resolved_prose?: string;
  resolvedProse?: string;
  parts?: ResolvedControlPart[];
}

export interface ResolvedControlParam {
  class?: string;
  label?: string;
  values?: string[];
  param_id?: string;
  paramId?: string;
  guidelines?: { prose: string }[];
}

export interface ResolvedControl {
  title: string;
  control_id: string;
  group_id?: string;
  group_title?: string;
  source_href: string;
  source_catalog_id: string;
  parts?: ResolvedControlPart[];
  params?: ResolvedControlParam[];
}

export interface ResolveProfileResponse {
  profileId: string;
  resolvedAt: string;
  profileVersion: number;
  method?: string;
  sourceCount?: number;
  controls: ResolvedControl[];
  sourceCatalogVersions: Record<string, unknown>;
}

export function resolveProfile(
  id: string,
): Promise<ApiEnvelope<ResolveProfileResponse>> {
  return apiFetch<ApiEnvelope<ResolveProfileResponse>>(
    `/profiles/${id}/resolve`,
    { method: "POST" },
  );
}

export function getResolvedCatalog(
  id: string,
): Promise<ApiEnvelope<ResolveProfileResponse>> {
  return apiFetch<ApiEnvelope<ResolveProfileResponse>>(
    `/profiles/${id}/resolved-catalog`,
  );
}

export function listResolvedProfiles(): Promise<
  ApiEnvelope<ProfileResponse[]>
> {
  return apiFetch<ApiEnvelope<ProfileResponse[]>>(`/profiles/resolved`);
}

export function getMapping(id: string): Promise<ControlMapping> {
  return apiFetch<ControlMapping>(`/mappings/${id}`);
}

export function createMapping(
  payload: CreateMappingPayload,
  owningOrgUnitId?: string,
): Promise<ControlMapping> {
  let url = "/mappings";
  if (owningOrgUnitId) {
    url += `?owning_org_unit_id=${owningOrgUnitId}`;
  }
  return apiFetch<ControlMapping>(url, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function updateMapping(
  id: string,
  payload: Partial<CreateMappingPayload>,
): Promise<ControlMapping> {
  return apiFetch<ControlMapping>(`/mappings/${id}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export function deleteMapping(id: string): Promise<void> {
  return apiFetch<void>(`/mappings/${id}`, { method: "DELETE" });
}

export function importMapping(
  payload: ImportMappingPayload,
  owningOrgUnitId?: string,
): Promise<ImportResult> {
  let url = "/mappings/import";
  if (owningOrgUnitId) {
    url += `?owning_org_unit_id=${owningOrgUnitId}`;
  }
  return apiFetch<ImportResult>(url, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function exportMapping(
  id: string,
  format: "json" | "xml" = "json",
): Promise<Blob> {
  return fetch(`${BASE_URL}/mappings/${id}/export?format=${format}`, {
    headers: {
      Accept: format === "json" ? "application/json" : "application/xml",
      ...getAuthHeaders(),
    },
  }).then((r) => {
    if (!r.ok) throw new Error(`Export failed: ${r.status}`);
    return r.blob();
  });
}

export function upsertMappingPairs(
  mappingId: string,
  pairs: MappingPair[],
): Promise<{ updated: number }> {
  return apiFetch<{ updated: number }>(`/mappings/${mappingId}/pairs`, {
    method: "PUT",
    body: JSON.stringify({ pairs }),
  });
}

export interface MappingCollectionSummary {
  id: string;
  documentId: string;
  title?: string;
  oscalVersion?: string;
  lifecycleStatus?: string;
  versionNumber?: number;
  mappingCount?: number;
  mappedSourceControls?: number;
  entryCount?: number;
  derived?: boolean;
  derivedFromCollectionAId?: string | null;
  derivedFromCollectionBId?: string | null;
  derivedFromCollectionATitle?: string | null;
  derivedFromCollectionBTitle?: string | null;
  sourceDocumentName?: string | null;
  targetDocumentName?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface MappingStatement {
  id: string;
  partId?: string;
  name: string;
  ns?: string;
  prose: string;
  sortOrder?: number;
  parts?: unknown[];
}

export interface MappingParam {
  id: string;
  paramId: string;
  label?: string;
  clazz?: string;
  guidelines?: { prose: string }[];
  values?: string[];
  sortOrder?: number;
}

export interface MappingEntryResponse {
  id: string;
  entryUuid: string;
  sourceControlId: string;
  sourceControlTitle: string | null;
  sourceStatements?: MappingStatement[];
  sourceParams?: MappingParam[];
  targetControlId: string;
  targetControlTitle: string | null;
  targetStatements?: MappingStatement[];
  targetParams?: MappingParam[];
  relationship: MappingRelationship;
  remarks?: string | null;
  relatedSourceMappings?: RelatedSourceMappingResponse[];
  derived?: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface RelatedSourceMappingResponse {
  mappingId: string;
  entryId: string;
  sourceCatalogId: string;
  sourceCatalogTitle?: string;
  sourceResourceHref?: string;
  sourceControlId: string;
  sourceControlTitle?: string;
  relationship: MappingRelationship;
}

export type MappingRelationship =
  | "equivalent-to"
  | "equal-to"
  | "subset-of"
  | "superset-of"
  | "intersects-with"
  | "no-relationship";

export interface MappingResponse {
  id: string;
  mappingUuid: string;
  sourceResourceHref: string;
  targetResourceHref: string;
  sourceCatalogTitle?: string | null;
  targetCatalogTitle?: string | null;
  sortOrder: number;
  entries: MappingEntryResponse[];
  autoCreated?: AutoCreatedCollection[];
  createdAt: string;
  updatedAt: string;
}

export interface MappingCollectionDetail {
  id: string;
  documentId: string;
  title?: string;
  mappings: MappingResponse[];
  mappingCount?: number;
  entryCount?: number;
  derived?: boolean;
  derivedFromCollectionAId?: string | null;
  derivedFromCollectionBId?: string | null;
  derivedFromCollectionATitle?: string | null;
  derivedFromCollectionBTitle?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AutoCreatedCollection {
  derivedCollectionId: string;
  derivedDocumentId: string;
  derivedCollectionTitle: string;
  derivedEntryCount: number;
  skippedEntryCount: number;
  skippedSourceControlIds: string[];
}

export interface AppliedAutoMapping {
  collectionId: string;
  documentId: string;
  title: string;
  derivedEntryCount: number;
  skippedEntryCount: number;
  skippedSourceControlIds: string[];
}

export interface AutoMappingSuggestion {
  collectionAId: string;
  collectionATitle: string;
  collectionBId: string;
  collectionBTitle: string;
  intermediaryCatalogHref: string;
  derivedSourceHref: string;
  derivedTargetHref: string;
  derivedSourceCatalogTitle?: string;
  derivedTargetCatalogTitle?: string;
  derivedMappingTitle?: string;
}

export interface CoverageData {
  mappingCollectionId: string;
  totalSourceControls: number;
  mappedSourceControls: number;
  totalTargetControls: number;
  coveredTargetControls: number;
  sourceCoveragePercent: number;
  targetCoveragePercent: number;
  unmappedSourceControlIds: string[];
  uncoveredTargetControlIds: string[];
  gaps: { controlId: string; framework: string; gapType: string }[];
}

export function listMappingCollections(): Promise<
  ApiEnvelope<MappingCollectionSummary[]>
> {
  return apiFetch<ApiEnvelope<MappingCollectionSummary[]>>(
    `/mapping-collections`,
  );
}

export function getMappingCollection(
  id: string,
): Promise<ApiEnvelope<MappingCollectionDetail>> {
  return apiFetch<ApiEnvelope<MappingCollectionDetail>>(
    `/mapping-collections/${id}`,
  );
}

export function createMappingCollection(payload: {
  title: string;
  owningOrgUnitId?: string;
}): Promise<ApiEnvelope<MappingCollectionSummary>> {
  return apiFetch<ApiEnvelope<MappingCollectionSummary>>(
    "/mapping-collections",
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
  );
}

export function deleteMappingCollection(
  id: string,
  reason?: string,
): Promise<void> {
  const qs = reason ? `?reason=${encodeURIComponent(reason)}` : "";
  return apiFetch<void>(`/mapping-collections/${id}${qs}`, {
    method: "DELETE",
  });
}

export function addMappingToCollection(
  collectionId: string,
  payload: {
    sourceResourceHref: string;
    targetResourceHref: string;
    sortOrder?: number;
  },
): Promise<ApiEnvelope<MappingResponse>> {
  return apiFetch<ApiEnvelope<MappingResponse>>(
    `/mapping-collections/${collectionId}/mappings`,
    { method: "POST", body: JSON.stringify(payload) },
  );
}

export function getAutoMappingSuggestions(
  collectionId: string,
): Promise<ApiEnvelope<AutoMappingSuggestion[]>> {
  return apiFetch<ApiEnvelope<AutoMappingSuggestion[]>>(
    `/mapping-collections/${collectionId}/auto-mapping-suggestions`,
  );
}

export function applyAutoMappingSuggestions(
  collectionId: string,
): Promise<ApiEnvelope<AppliedAutoMapping[]>> {
  return apiFetch<ApiEnvelope<AppliedAutoMapping[]>>(
    `/mapping-collections/${collectionId}/auto-mapping-suggestions/apply`,
    { method: "POST" },
  );
}

export function getCollectionMapping(
  collectionId: string,
  mappingId: string,
): Promise<ApiEnvelope<MappingResponse>> {
  return apiFetch<ApiEnvelope<MappingResponse>>(
    `/mapping-collections/${collectionId}/mappings/${mappingId}`,
  );
}

export function createMappingEntry(
  collectionId: string,
  mappingId: string,
  payload: {
    sourceControlId: string;
    targetControlId: string;
    relationship: MappingRelationship;
    sortOrder?: number;
  },
): Promise<ApiEnvelope<MappingEntryResponse>> {
  return apiFetch<ApiEnvelope<MappingEntryResponse>>(
    `/mapping-collections/${collectionId}/mappings/${mappingId}/entries`,
    { method: "POST", body: JSON.stringify(payload) },
  );
}

export function updateMappingEntry(
  collectionId: string,
  mappingId: string,
  entryId: string,
  payload: {
    sourceControlId: string;
    targetControlId: string;
    relationship: MappingRelationship;
    sortOrder: number;
    remarks?: string | null;
  },
): Promise<ApiEnvelope<MappingEntryResponse>> {
  return apiFetch<ApiEnvelope<MappingEntryResponse>>(
    `/mapping-collections/${collectionId}/mappings/${mappingId}/entries/${entryId}`,
    { method: "PUT", body: JSON.stringify(payload) },
  );
}

export function deleteMappingEntry(
  collectionId: string,
  mappingId: string,
  entryId: string,
): Promise<void> {
  return apiFetch<void>(
    `/mapping-collections/${collectionId}/mappings/${mappingId}/entries/${entryId}`,
    { method: "DELETE" },
  );
}

export function getMappingCoverage(
  collectionId: string,
  profileId?: string,
): Promise<ApiEnvelope<CoverageData>> {
  const qs = profileId ? `?profile_id=${encodeURIComponent(profileId)}` : "";
  return apiFetch<ApiEnvelope<CoverageData>>(
    `/mapping-collections/${collectionId}/coverage${qs}`,
  );
}

export interface ComponentDefinitionSummaryResponse {
  id: string;
  documentId: string;
  title?: string;
  lifecycleStatus?: string;
  componentCount?: number;
  controlCount?: number;
  createdAt: string;
  updatedAt: string;
}

export interface ComponentResponse {
  id: string;
  componentUuid: string;
  componentType: ComponentType;
  title: string;
  description: string;
  purpose: string;
  props?: { name: string; value: string }[];
  sortOrder: number;
  controlImplementations: ControlImplementationResponse[];
  createdAt: string;
  updatedAt: string;
}

export interface CapabilityResponse {
  id: string;
  capabilityUuid: string;
  name: string;
  description: string;
  sortOrder: number;
  remarks?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ComponentDefinitionResponse {
  id: string;
  documentId: string;
  components: ComponentResponse[];
  capabilities: CapabilityResponse[];
  createdAt: string;
  updatedAt: string;
}

export interface SetParameterResponse {
  paramId: string;
  values: string[];
  remarks?: string;
}

export interface ControlImplementationResponse {
  id: string;
  ciUuid: string;
  source: string;
  description: string;
  setParameters: SetParameterResponse[];
  sortOrder: number;
  implementedRequirements: ImplementedRequirementResponse[];
  createdAt: string;
  updatedAt: string;
}

export interface ImplementedRequirementResponse {
  id: string;
  irUuid: string;
  controlId: string;
  controlTitle?: string | null;
  description: string;
  setParameters: SetParameterResponse[];
  sortOrder: number;
  statements: StatementResponse[];
  createdAt: string;
  updatedAt: string;
}

export interface StatementResponse {
  id: string;
  statementUuid: string;
  statementId: string;
  description: string;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export type ComponentType =
  | "software"
  | "hardware"
  | "service"
  | "policy"
  | "process"
  | "procedure"
  | "plan"
  | "guidance"
  | "standard"
  | "validation"
  | "operating-system";

export interface CreateComponentDefinitionPayload {
  title: string;
  owningOrgUnitId?: string | null;
}

export interface AddComponentPayload {
  componentType: ComponentType;
  title: string;
  description?: string;
  purpose?: string;
  props?: { name: string; value: string }[];
  sortOrder: number;
}

export interface AddCapabilityPayload {
  name: string;
  description?: string;
  sortOrder: number;
}

export interface ComponentResponsibleRoleResponse {
  id: string;
  roleId: string;
  title: string;
  partyUuids: string[];
  createdAt: string;
  updatedAt: string;
}

export interface AddComponentResponsibleRolePayload {
  roleId: string;
  title: string;
  partyUuids?: string[];
}

export type CdefExportFormat = "json" | "xml" | "yaml";

export interface CdefExportPayload {
  format: CdefExportFormat;
}

export interface CdefExportResponse {
  format: CdefExportFormat;
  filename: string;
  content: string;
}

export interface ComponentSspUsageResponse {
  sspId: string;
  sspTitle: string;
  sspDocumentId: string;
  componentTitle: string;
  importedAt: string;
}

export interface ComponentComplianceStatusResponse {
  componentId: string;
  totalControls: number;
  implementedControls: number;
  partialControls: number;
  plannedControls: number;
  notApplicableControls: number;
  compliancePercentage: number;
  lastAssessedAt: string | null;
  nextAssessmentDue: string | null;
}

export interface SetParameterPayload {
  paramId: string;
  values: string[];
  remarks?: string;
}

export interface StatementPayload {
  statementId: string;
  description?: string;
  sortOrder: number;
}

export interface ImplementedRequirementPayload {
  controlId: string;
  description?: string;
  setParameters?: SetParameterPayload[];
  statements?: StatementPayload[];
}

export interface AddControlImplementationPayload {
  source: string;
  description?: string;
  setParameters?: SetParameterPayload[];
  implementedRequirements?: ImplementedRequirementPayload[];
}

export function listComponentDefinitions(): Promise<
  ApiEnvelope<ComponentDefinitionSummaryResponse[]>
> {
  return apiFetch<ApiEnvelope<ComponentDefinitionSummaryResponse[]>>(
    `/component-definitions`,
  );
}

export function getComponentDefinition(
  id: string,
): Promise<ApiEnvelope<ComponentDefinitionResponse>> {
  return apiFetch<ApiEnvelope<ComponentDefinitionResponse>>(
    `/component-definitions/${id}`,
  );
}

export function createComponentDefinition(
  payload: CreateComponentDefinitionPayload,
): Promise<ApiEnvelope<ComponentDefinitionSummaryResponse>> {
  return apiFetch<ApiEnvelope<ComponentDefinitionSummaryResponse>>(
    "/component-definitions",
    { method: "POST", body: JSON.stringify(payload) },
  );
}

export function deleteComponentDefinition(
  id: string,
  reason?: string,
): Promise<ApiEnvelope<null>> {
  const qs = reason ? `?reason=${encodeURIComponent(reason)}` : "";
  return apiFetch<ApiEnvelope<null>>(`/component-definitions/${id}${qs}`, {
    method: "DELETE",
  });
}

export function listComponents(
  definitionId: string,
  type?: ComponentType,
): Promise<ApiEnvelope<ComponentResponse[]>> {
  const qs = type ? `?type=${type}` : "";
  return apiFetch<ApiEnvelope<ComponentResponse[]>>(
    `/component-definitions/${definitionId}/components${qs}`,
  );
}

export function deleteComponent(
  definitionId: string,
  componentId: string,
): Promise<ApiEnvelope<null>> {
  return apiFetch<ApiEnvelope<null>>(
    `/component-definitions/${definitionId}/components/${componentId}`,
    { method: "DELETE" },
  );
}

export function addComponent(
  definitionId: string,
  payload: AddComponentPayload,
): Promise<ApiEnvelope<ComponentResponse>> {
  return apiFetch<ApiEnvelope<ComponentResponse>>(
    `/component-definitions/${definitionId}/components`,
    { method: "POST", body: JSON.stringify(payload) },
  );
}

export function listCapabilities(
  definitionId: string,
): Promise<ApiEnvelope<CapabilityResponse[]>> {
  return apiFetch<ApiEnvelope<CapabilityResponse[]>>(
    `/component-definitions/${definitionId}/capabilities`,
  );
}

export function addCapability(
  definitionId: string,
  payload: AddCapabilityPayload,
): Promise<ApiEnvelope<CapabilityResponse>> {
  return apiFetch<ApiEnvelope<CapabilityResponse>>(
    `/component-definitions/${definitionId}/capabilities`,
    { method: "POST", body: JSON.stringify(payload) },
  );
}

export function listControlImplementations(
  definitionId: string,
  componentId: string,
): Promise<ApiEnvelope<ControlImplementationResponse[]>> {
  return apiFetch<ApiEnvelope<ControlImplementationResponse[]>>(
    `/component-definitions/${definitionId}/components/${componentId}/control-implementations`,
  );
}

export function addControlImplementation(
  definitionId: string,
  componentId: string,
  payload: AddControlImplementationPayload,
): Promise<ApiEnvelope<ControlImplementationResponse>> {
  return apiFetch<ApiEnvelope<ControlImplementationResponse>>(
    `/component-definitions/${definitionId}/components/${componentId}/control-implementations`,
    { method: "POST", body: JSON.stringify(payload) },
  );
}

export function updateControlImplementation(
  definitionId: string,
  componentId: string,
  ciId: string,
  payload: AddControlImplementationPayload,
): Promise<ApiEnvelope<ControlImplementationResponse>> {
  return apiFetch<ApiEnvelope<ControlImplementationResponse>>(
    `/component-definitions/${definitionId}/components/${componentId}/control-implementations/${ciId}`,
    { method: "PUT", body: JSON.stringify(payload) },
  );
}

export function deleteImplementedRequirements(
  definitionId: string,
  componentId: string,
  implementedRequirementIds: string[],
  reason?: string,
): Promise<ApiEnvelope<{ requested: number; deleted: number }>> {
  return apiFetch(
    `/component-definitions/${definitionId}/components/${componentId}/implemented-requirements/delete`,
    {
      method: "POST",
      body: JSON.stringify({ implementedRequirementIds, reason }),
    },
  );
}

export function listComponentResponsibleRoles(
  definitionId: string,
  componentId: string,
): Promise<ApiEnvelope<ComponentResponsibleRoleResponse[]>> {
  return apiFetch<ApiEnvelope<ComponentResponsibleRoleResponse[]>>(
    `/component-definitions/${definitionId}/components/${componentId}/responsible-roles`,
  );
}

export function addComponentResponsibleRole(
  definitionId: string,
  componentId: string,
  payload: AddComponentResponsibleRolePayload,
): Promise<ApiEnvelope<ComponentResponsibleRoleResponse>> {
  return apiFetch<ApiEnvelope<ComponentResponsibleRoleResponse>>(
    `/component-definitions/${definitionId}/components/${componentId}/responsible-roles`,
    { method: "POST", body: JSON.stringify(payload) },
  );
}

export function deleteComponentResponsibleRole(
  definitionId: string,
  componentId: string,
  roleId: string,
): Promise<ApiEnvelope<null>> {
  return apiFetch<ApiEnvelope<null>>(
    `/component-definitions/${definitionId}/components/${componentId}/responsible-roles/${roleId}`,
    { method: "DELETE" },
  );
}

export function exportDocumentJson(documentId: string): Promise<Blob> {
  return fetch(`${BASE_URL}/export/${documentId}/json`, {
    headers: { Accept: "application/json", ...getAuthHeaders() },
  }).then((r) => {
    if (!r.ok) throw new Error(`Export failed: ${r.status}`);
    return r.blob();
  });
}

export function exportComponentDefinition(
  definitionId: string,
  format: CdefExportFormat = "json",
): Promise<ApiEnvelope<CdefExportResponse>> {
  return apiFetch<ApiEnvelope<CdefExportResponse>>(
    `/component-definitions/${definitionId}/export?format=${format}`,
  );
}

export function listComponentSspUsage(
  definitionId: string,
  componentId: string,
): Promise<ApiEnvelope<ComponentSspUsageResponse[]>> {
  return apiFetch<ApiEnvelope<ComponentSspUsageResponse[]>>(
    `/component-definitions/${definitionId}/components/${componentId}/ssp-usage`,
  );
}

export function getComponentComplianceStatus(
  definitionId: string,
  componentId: string,
): Promise<ApiEnvelope<ComponentComplianceStatusResponse>> {
  return apiFetch<ApiEnvelope<ComponentComplianceStatusResponse>>(
    `/component-definitions/${definitionId}/components/${componentId}/compliance-status`,
  );
}

export function searchComponents(params?: {
  q?: string;
  type?: ComponentType;
  control_id?: string;
}): Promise<ApiEnvelope<ComponentResponse[]>> {
  const qs = new URLSearchParams();
  if (params?.q) qs.set("q", params.q);
  if (params?.type) qs.set("type", params.type);
  if (params?.control_id) qs.set("control_id", params.control_id);
  const query = qs.toString();
  return apiFetch<ApiEnvelope<ComponentResponse[]>>(
    `/components/search${query ? `?${query}` : ""}`,
  );
}

export function getControlImplementationsForControl(
  controlId: string,
): Promise<ApiEnvelope<ImplementedRequirementResponse[]>> {
  return apiFetch<ApiEnvelope<ImplementedRequirementResponse[]>>(
    `/controls/${controlId}/implementations`,
  );
}

export interface SspSummaryResponse {
  id: string;
  documentId: string;
  title?: string;
  profileName?: string;
  lifecycleStatus?: string;
  importProfileHref: string;
  owningOrgUnitId?: string;
  owningOrgUnitName?: string;
  sourceType?: string;
  sourceId?: string;
  sourceName?: string;
  createdAt: string;
  updatedAt: string;
}

export interface SspResponse {
  id: string;
  title: string;
  documentId: string;
  importProfileHref: string;
  owningOrgUnitId?: string;
  owningOrgUnitName?: string;
  systemCharacteristics: SystemCharacteristicsResponse | null;
  systemImplementation: {
    id: string;
    description: string | null;
    components: SspComponentResponse[];
    inventoryItems: InventoryItemResponse[];
  } | null;
  controlImplementations: SspControlImplementationResponse[];
  leveragedAuthorizations: LeveragedAuthorizationResponse[];
  createdAt: string;
  updatedAt: string;
}

export interface CreateSspPayload {
  title: string;
  importProfileHref: string;
  owningOrgUnitId?: string | null;
  divisionId?: string | null;
  divisionName?: string | null;
  departmentId?: string | null;
  departmentName?: string | null;
}

export interface CreateSspFromApplicationPayload {
  title: string;
  ssp_short_code: string;
  import_profile_href: string;
  owning_org_unit_id: string;
  application_id: string;
  component_ids: string[];
  provider_ssp_ids?: string[];
  division_id?: string | null;
  division_name?: string | null;
  department_id?: string | null;
  department_name?: string | null;
}

export interface CreateSspFromServicePayload {
  title: string;
  ssp_short_code: string;
  import_profile_href: string;
  owning_org_unit_id: string;
  service_id: string;
  component_ids: string[];
  division_id?: string | null;
  division_name?: string | null;
  department_id?: string | null;
  department_name?: string | null;
}

export interface UpdateSspPayload {
  importProfileHref?: string;
}

export interface SystemCharacteristicsResponse {
  id: string;
  systemName: string;
  systemNameShort: string | null;
  description: string | null;
  securitySensitivityLevel: string | null;
  systemType?: string | null;
  authorizationType?: string | null;
  deploymentModel?: string | null;
  serviceModel?: string | null;
  systemIds: { "identifier-type": string; id: string }[];
  authorizationBoundary: { description: string } | null;
  networkArchitecture: { description: string } | null;
  dataFlow: { description: string } | null;
  securityImpactLevel: {
    "security-objective-confidentiality": string;
    "security-objective-integrity": string;
    "security-objective-availability": string;
  } | null;
  status: string | null;
  statusRemarks: string | null;
  dateAuthorized: string | null;
  informationTypes: InformationTypeResponse[];
  createdAt: string;
  updatedAt: string;
}

export interface SetSystemCharacteristicsPayload {
  systemName: string;
  systemNameShort?: string;
  description?: string;
  securitySensitivityLevel?: string;
  systemType?: string;
  authorizationType?: string;
  serviceModel?: string;
  deploymentModel?: string;
  systemIds?: { "identifier-type": string; id: string }[];
  authorizationBoundary?: { description: string };
  networkArchitecture?: { description: string };
  dataFlow?: { description: string };
  securityImpactLevel?: {
    "security-objective-confidentiality": string;
    "security-objective-integrity": string;
    "security-objective-availability": string;
  };
  status?: string;
  statusRemarks?: string;
  dateAuthorized?: string;
}

export interface InformationTypeResponse {
  id: string;
  infoTypeUuid: string;
  title: string;
  description: string | null;
  confidentialityImpact: { base: string } | null;
  integrityImpact: { base: string } | null;
  availabilityImpact: { base: string } | null;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface AddInformationTypePayload {
  title: string;
  description?: string;
  confidentialityImpact?: { base: string; selected?: string };
  integrityImpact?: { base: string; selected?: string };
  availabilityImpact?: { base: string; selected?: string };
  sortOrder: number;
}

export interface SspComponentResponse {
  id: string;
  componentUuid: string;
  componentType: string;
  title: string;
  description: string | null;
  purpose: string | null;
  status: string | null;
  statusRemarks: string | null;
  sourceComponentId: string | null;
  props?: { name: string; value: string }[];
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
  assetCount?: number | null;
  implementedControlCount?: number | null;
  fullyImplementedControlCount?: number | null;
  sspLayerSlug?: string | null;
  sspLayerName?: string | null;
  displayStatus?: string | null;
  displayStatusLabel?: string | null;
}

export interface ComponentProtocolPayload {
  name: string;
  title?: string;
  portRanges?: { start: number; end: number; transport: string }[];
  sortOrder: number;
}

export interface ComponentResponsibleRolePayload {
  role_id: string;
  party_uuids?: string[];
}

export interface AddSspComponentPayload {
  sourceComponentId?: string | null;
  componentType: string;
  title: string;
  description?: string;
  purpose?: string;
  status?: string;
  responsibleRoles?: ComponentResponsibleRolePayload[];
  protocols?: ComponentProtocolPayload[];
  sortOrder: number;
}

export interface AssetIdentifierResponse {
  id: string;
  identifierType: string;
  identifierValue: string;
}

export interface InventoryItemResponse {
  id: string;
  inventoryUuid: string;
  componentId: string | null;
  componentUuid: string | null;
  componentName: string | null;
  description: string;
  implementedComponents: Record<string, unknown>[];
  assetIdentifiers: AssetIdentifierResponse[];
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface AddInventoryItemPayload {
  description: string;
  component_id?: string;
  implementedComponents?: { "component-uuid": string }[];
  assetIdentifiers?: { identifierType: string; identifierValue: string }[];
  sortOrder: number;
}

export interface PatchInformationTypePayload {
  title?: string;
  description?: string;
  confidentialityImpact?: { base: string; selected?: string } | null;
  integrityImpact?: { base: string; selected?: string } | null;
  availabilityImpact?: { base: string; selected?: string } | null;
}

export interface PatchInventoryItemPayload {
  description?: string;
  component_id?: string | null;
  implementedComponents?: { "component-uuid": string }[];
  assetIdentifiers?: { identifierType: string; identifierValue: string }[];
}

export interface PatchSspComponentPayload {
  title?: string;
  componentType?: string;
  description?: string | null;
  purpose?: string | null;
  status?: string | null;
  statusRemarks?: string | null;
  responsibleRoles?: ComponentResponsibleRolePayload[];
}

export interface ComponentControlRow {
  implementedRequirementId: string;
  controlId: string;
  controlTitle?: string | null;
  title?: string | null;
  implementationStatus: string | null;
  description: string | null;
}

export interface PatchByComponentLinkPayload {
  description?: string | null;
  implementationStatus?: string | null;
  responsibleRoles?: { roleId: string }[];
}

export interface SspImplementedRequirementResponse {
  id: string;
  irUuid: string;
  controlId: string;
  implementationStatus: string;
  origination: string;
  description?: string | null;
  setParameters: { values: string[]; "param-id": string }[];
  byComponents: Record<string, unknown>[];
  statements: {
    id: string;
    statementUuid: string;
    statementId: string;
    sortOrder: number;
    createdAt: string;
    updatedAt: string;
  }[];
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface SspControlImplementationRawResponse {
  id: string;
  ciUuid: string;
  componentId: string | null;
  source: string;
  description: string | null;
  implementedRequirements: SspImplementedRequirementResponse[];
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface SspControlImplementationResponse {
  id: string;
  irUuid: string;
  controlId: string;
  componentId: string | null;
  source: string;
  description: string | null;
  implementationStatus: string;
  controlOrigination: string;
  setParameters: { values: string[]; "param-id": string }[];
  responsibleRoles?: { "role-id": string; "party-uuids"?: string[] }[];
  byComponents: Record<string, unknown>[];
  statements: {
    id: string;
    statementUuid: string;
    statementId: string;
    description?: string;
    sortOrder: number;
    createdAt: string;
    updatedAt: string;
  }[];
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
  ciId: string;
}

export type LeveragedAuthorizationStatus = "active" | "revoked" | "expired";

export interface LeveragedAuthorizationResponse {
  id: string;
  leveragedUuid: string;
  title: string;
  partyUuid: string | null;
  dateAuthorized: string | null;
  providerSspId: string | null;
  status: LeveragedAuthorizationStatus | null;
  controlScope: string[] | null;
  sourceSspVersion: string | null;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface AddLeveragedAuthorizationPayload {
  leveragedUuid?: string | null;
  title: string;
  partyUuid?: string;
  dateAuthorized?: string;
  sortOrder: number;
}

export interface UpdateLeveragedAuthorizationPayload {
  title?: string;
  partyUuid?: string;
  dateAuthorized?: string;
  status?: LeveragedAuthorizationStatus;
  controlScope?: string[] | null;
  remarks?: string;
}

export interface AssetResolutionResponse {
  inventoryItem: InventoryItemResponse;
  components: SspComponentResponse[];
  controlImplementations: SspControlImplementationResponse[];
}

export interface SspRoleResponse {
  id: string;
  documentId: string;
  roleId: string;
  title: string;
  shortName: string | null;
  description: string | null;
  sortOrder: number;
  createdAt: string;
}

export interface SspRolePayload {
  roleId: string;
  title: string;
  shortName?: string | null;
  description?: string | null;
  sortOrder: number;
}

export interface SspPartyResponse {
  id: string;
  documentId: string;
  partyUuid: string;
  partyType: "person" | "organization";
  name: string;
  shortName: string | null;
  emailAddresses: string[];
  telephoneNumbers: string[];
  memberOfOrganizations: string[];
  externalIds: string[] | null;
  sortOrder: number;
  createdAt: string;
}

export interface SspPartyPayload {
  partyUuid?: string | null;
  partyType: "person" | "organization";
  name: string;
  shortName?: string | null;
  emailAddresses?: string[];
  telephoneNumbers?: string[];
  memberOfOrganizations?: string[];
  externalIds?: string[] | null;
  sortOrder: number;
}

export interface SspLocationResponse {
  id: string;
  documentId: string;
  locationUuid: string;
  title: string;
  addressLines: string[];
  city: string | null;
  state: string | null;
  postalCode: string | null;
  country: string | null;
  latitude: number | null;
  longitude: number | null;
  sortOrder: number;
  createdAt: string;
}

export interface SspLocationPayload {
  locationUuid?: string | null;
  title: string;
  addressLines?: string[];
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
  country?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  sortOrder: number;
}

export interface SspResponsiblePartyResponse {
  id: string;
  documentId: string;
  roleId: string;
  partyUuids: string[];
  ownerType: string | null;
  ownerUuid: string | null;
  createdAt: string;
}

export interface SspResponsiblePartyPayload {
  roleId: string;
  partyUuids: string[];
}

export type ImplementationStatus =
  | "implemented"
  | "partial"
  | "planned"
  | "alternative"
  | "not-implemented"
  | "not applicable";

export type ControlOrigination =
  | "system-specific"
  | "inherited"
  | "customer-configured"
  | "customer"
  | "customer-responsibility"
  | "hybrid"
  | "shared"
  | "provider-specific"
  | "provider";

export interface AddSspControlImplementationPayload {
  source: string;
  controlId?: string;
  component_id?: string;
  description?: string;
  implementationStatus: ImplementationStatus;
  controlOrigination?: ControlOrigination;
  responsibleRoleIds?: string[];
  setParameters?: { "param-id": string; values: string[]; remarks?: string }[];
  statements?: {
    statementId: string;
    description?: string;
    sortOrder?: number;
  }[];
}

export interface UpdateSspControlImplementationPayload {
  description?: string;
  implementationStatus?: ImplementationStatus;
  controlOrigination?: ControlOrigination;
  responsibleRoleIds?: string[];
  setParameters?: Record<string, unknown>[];
  statements?: { statementId: string; description: string }[];
}

export interface UpdateImplementedRequirementPayload {
  controlId: string;
  implementationStatus: ImplementationStatus;
  origination: ControlOrigination;
  description?: string;
  setParameters?: { "param-id": string; values: string[] }[] | null;
  byComponents?:
  | {
    componentUuid: string;
    description?: string;
    implementationStatus?: ImplementationStatus;
    responsibleRoles?: { "role-id": string }[];
    setParameters?: null;
    inherited?: null;
    satisfied?: null;
    sortOrder?: number;
  }[]
  | null;
  statements?:
  | {
    statementId: string;
    description?: string;
    sortOrder?: number;
  }[]
  | null;
  sortOrder?: number;
}

export interface SspLifecycleResponse {
  sspId: string;
  currentStatus: string;
  availableTransitions: string[];
  lastTransitionAt: string | null;
  lastTransitionBy: string | null;
}

export interface TransitionSspPayload {
  targetStatus: string;
  comment?: string;
}

export interface SspFullResponse {
  ssp: SspResponse & {
    systemCharacteristics: SystemCharacteristicsResponse | null;
    systemImplementation: {
      users: SspSystemUserResponse[];
      components: SspComponentResponse[];
      inventoryItems: InventoryItemResponse[];
      interconnections: SspInterconnectionResponse[];
    };
    controlImplementations: SspControlImplementationRawResponse[];
    leveragedAuthorizations: LeveragedAuthorizationResponse[];
  };
  informationTypes: InformationTypeResponse[];
  roles: SspRoleResponse[];
  parties: SspPartyResponse[];
  locations: SspLocationResponse[];
  responsibleParties: SspResponsiblePartyResponse[];
  baselineControls: BaselineControlResponse[] | { controls: BaselineControlResponse[] };
}

export function getSspFull(id: string): Promise<ApiEnvelope<SspFullResponse>> {
  return apiFetch(`/ssps/${id}/full`);
}

export interface SspHistoryEntry {
  id: string;
  action: string;
  entityType: string;
  entityId: string;
  changedBy: string | null;
  changedAt: string;
  summary: string;
  details: Record<string, unknown> | null;
}

export interface SspExportPayload {
  format: "json" | "xml" | "yaml";
  profile: string;
  backMatterMode: string;
}

export interface SspExportResponse {
  downloadUrl: string;
  format: string;
  oscalVersion: string;
  generatedAt: string;
}

export function listSSPs(): Promise<ApiEnvelope<SspSummaryResponse[]>> {
  return apiFetch(`/ssps`);
}

export function getSSP(id: string): Promise<ApiEnvelope<SspResponse>> {
  return apiFetch(`/ssps/${id}`);
}

export function getSspProfile(
  sspId: string,
): Promise<ApiEnvelope<ProfileDetailResponse>> {
  return apiFetch<ApiEnvelope<ProfileDetailResponse>>(`/ssps/${sspId}/profile`);
}

export function getSspProfileMatchedComponents(
  sspId: string,
): Promise<
  ApiEnvelope<
    (ComponentResponse & { cdefTitle?: string; sourceComponentId?: string })[]
  >
> {
  return apiFetch<
    ApiEnvelope<
      (ComponentResponse & { cdefTitle?: string; sourceComponentId?: string })[]
    >
  >(`/ssps/${sspId}/profile-matched-components`);
}

export function createSSP(
  payload: CreateSspPayload,
): Promise<ApiEnvelope<SspSummaryResponse>> {
  return apiFetch(`/ssps`, { method: "POST", body: JSON.stringify(payload) });
}

export function createSSPFromApplication(
  payload: CreateSspFromApplicationPayload,
): Promise<ApiEnvelope<SspSummaryResponse>> {
  return apiFetch(`/ssps/from-application`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function createSSPFromService(
  payload: CreateSspFromServicePayload,
): Promise<ApiEnvelope<SspSummaryResponse>> {
  return apiFetch(`/ssps/from-service`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function updateSSP(
  id: string,
  payload: UpdateSspPayload,
): Promise<ApiEnvelope<SspResponse>> {
  return apiFetch(`/ssps/${id}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export function deleteSSP(
  id: string,
  reason?: string,
): Promise<ApiEnvelope<null>> {
  const qs = reason ? `?reason=${encodeURIComponent(reason)}` : "";
  return apiFetch(`/ssps/${id}${qs}`, { method: "DELETE" });
}

export function getSystemCharacteristics(
  sspId: string,
): Promise<ApiEnvelope<SystemCharacteristicsResponse>> {
  return apiFetch(`/ssps/${sspId}/system-characteristics`);
}

export function setSystemCharacteristics(
  sspId: string,
  payload: SetSystemCharacteristicsPayload,
): Promise<ApiEnvelope<SystemCharacteristicsResponse>> {
  return apiFetch(`/ssps/${sspId}/system-characteristics`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export function listInformationTypes(
  sspId: string,
): Promise<ApiEnvelope<InformationTypeResponse[]>> {
  return apiFetch(`/ssps/${sspId}/information-types`);
}

export function addInformationType(
  sspId: string,
  payload: AddInformationTypePayload,
): Promise<ApiEnvelope<InformationTypeResponse>> {
  return apiFetch(`/ssps/${sspId}/information-types`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function patchInformationType(
  sspId: string,
  infoTypeId: string,
  payload: PatchInformationTypePayload,
): Promise<ApiEnvelope<InformationTypeResponse>> {
  return apiFetch(`/ssps/${sspId}/information-types/${infoTypeId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export function deleteInformationType(
  sspId: string,
  infoTypeId: string,
): Promise<ApiEnvelope<null>> {
  return apiFetch(`/ssps/${sspId}/information-types/${infoTypeId}`, {
    method: "DELETE",
  });
}

export function listSspComponents(
  sspId: string,
): Promise<ApiEnvelope<SspComponentResponse[]>> {
  return apiFetch(`/ssps/${sspId}/components`);
}

export function addSspComponent(
  sspId: string,
  payload: AddSspComponentPayload,
): Promise<ApiEnvelope<SspComponentResponse>> {
  return apiFetch(`/ssps/${sspId}/components`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function getSspComponent(
  sspId: string,
  componentUuid: string,
): Promise<ApiEnvelope<SspComponentResponse>> {
  return apiFetch(`/ssps/${sspId}/components/${componentUuid}`);
}

export function deleteSspComponent(
  sspId: string,
  componentUuid: string,
): Promise<ApiEnvelope<null>> {
  return apiFetch(`/ssps/${sspId}/components/${componentUuid}`, {
    method: "DELETE",
  });
}

export function patchSspComponent(
  sspId: string,
  componentId: string,
  payload: PatchSspComponentPayload,
): Promise<ApiEnvelope<SspComponentResponse>> {
  return apiFetch(`/ssps/${sspId}/components/${componentId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export function listComponentInventoryItems(
  sspId: string,
  componentUuid: string,
): Promise<ApiEnvelope<InventoryItemResponse[]>> {
  return apiFetch(
    `/ssps/${sspId}/components/${componentUuid}/inventory-items`,
  );
}

export function listComponentControlImplementations(
  sspId: string,
  componentUuid: string,
): Promise<ApiEnvelope<SspControlImplementationRawResponse[]>> {
  return apiFetch(
    `/ssps/${sspId}/components/${componentUuid}/control-implementations`,
  );
}

export function listComponentControls(
  sspId: string,
  componentId: string,
): Promise<ApiEnvelope<ComponentControlRow[]>> {
  return apiFetch(`/ssps/${sspId}/components/${componentId}/controls`);
}

export function listInventoryItems(
  sspId: string,
): Promise<ApiEnvelope<InventoryItemResponse[]>> {
  return apiFetch(`/ssps/${sspId}/inventory-items`);
}

export function addInventoryItem(
  sspId: string,
  payload: AddInventoryItemPayload,
): Promise<ApiEnvelope<InventoryItemResponse>> {
  return apiFetch(`/ssps/${sspId}/inventory-items`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function patchInventoryItem(
  sspId: string,
  inventoryItemId: string,
  payload: PatchInventoryItemPayload,
): Promise<ApiEnvelope<InventoryItemResponse>> {
  return apiFetch(`/ssps/${sspId}/inventory-items/${inventoryItemId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export function deleteInventoryItem(
  sspId: string,
  inventoryItemId: string,
): Promise<ApiEnvelope<null>> {
  return apiFetch(`/ssps/${sspId}/inventory-items/${inventoryItemId}`, {
    method: "DELETE",
  });
}

export function listSspControlImplementationsRaw(
  sspId: string,
): Promise<ApiEnvelope<SspControlImplementationRawResponse[]>> {
  return apiFetch(`/ssps/${sspId}/control-implementations`);
}

export async function listSspControlImplementations(
  sspId: string,
  controlId?: string,
): Promise<ApiEnvelope<SspControlImplementationResponse[]>> {
  const qs = controlId ? `?control_id=${encodeURIComponent(controlId)}` : "";
  const raw: ApiEnvelope<SspControlImplementationRawResponse[]> =
    await apiFetch(`/ssps/${sspId}/control-implementations${qs}`);
  const flattened: SspControlImplementationResponse[] = [];
  for (const ci of raw.data) {
    for (const ir of ci.implementedRequirements ?? []) {
      flattened.push({
        id: ir.id,
        irUuid: ir.irUuid,
        controlId: ir.controlId,
        componentId: ci.componentId ?? null,
        source: ci.source,
        description: ir.description ?? ci.description,
        implementationStatus: ir.implementationStatus,
        controlOrigination: ir.origination,
        setParameters: ir.setParameters ?? [],
        byComponents: ir.byComponents ?? [],
        statements: ir.statements ?? [],
        sortOrder: ir.sortOrder,
        createdAt: ir.createdAt,
        updatedAt: ir.updatedAt,
        ciId: ci.id,
      });
    }
  }
  return { ...raw, data: flattened };
}

export function listSspImplementedRequirements(
  sspId: string,
  ciId: string,
): Promise<ApiEnvelope<ImplementedRequirementResponse[]>> {
  return apiFetch(
    `/ssps/${sspId}/control-implementations/${ciId}/implemented-requirements`,
  );
}

export function resolveAsset(
  sspId: string,
  identifier: string,
): Promise<ApiEnvelope<AssetResolutionResponse>> {
  return apiFetch(
    `/ssps/${sspId}/resolve-asset?identifier=${encodeURIComponent(identifier)}`,
  );
}

export function listLeveragedAuthorizations(
  sspId: string,
): Promise<ApiEnvelope<LeveragedAuthorizationResponse[]>> {
  return apiFetch(`/ssps/${sspId}/leveraged-authorizations`);
}

export function addLeveragedAuthorization(
  sspId: string,
  payload: AddLeveragedAuthorizationPayload,
): Promise<ApiEnvelope<LeveragedAuthorizationResponse>> {
  return apiFetch(`/ssps/${sspId}/leveraged-authorizations`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function updateLeveragedAuthorization(
  sspId: string,
  authId: string,
  payload: UpdateLeveragedAuthorizationPayload,
): Promise<ApiEnvelope<LeveragedAuthorizationResponse>> {
  return apiFetch(`/ssps/${sspId}/leveraged-authorizations/${authId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export function deleteLeveragedAuthorization(
  sspId: string,
  authId: string,
): Promise<ApiEnvelope<null>> {
  return apiFetch(`/ssps/${sspId}/leveraged-authorizations/${authId}`, {
    method: "DELETE",
  });
}

export interface LeveragedAuthorizationItem {
  id: string;
  sspId: string;
  providerSspId: string | null;
  leveragedUuid: string;
  title: string;
  status: string;
  sourceSspVersion: string | null;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface CmdbLaResolveResponse {
  consumerSspId: string;
  created: number;
  alreadyExisting: number;
  leveragedAuthorizations: LeveragedAuthorizationItem[];
}

export async function resolveLeveragedAuthorizationsFromCmdb(
  sspId: string,
): Promise<CmdbLaResolveResponse> {
  const response = await apiFetch<ApiEnvelope<CmdbLaResolveResponse>>(
    `/ssps/${sspId}/leveraged-authorizations/resolve-from-cmdb`,
    { method: "POST" },
  );
  return response.data;
}

export function listSspRoles(
  sspId: string,
): Promise<ApiEnvelope<SspRoleResponse[]>> {
  return apiFetch(`/ssps/${sspId}/roles`);
}

export function replaceSspRoles(
  sspId: string,
  payload: SspRolePayload[],
): Promise<ApiEnvelope<SspRoleResponse[]>> {
  return apiFetch(`/ssps/${sspId}/roles`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export function listSspParties(
  sspId: string,
): Promise<ApiEnvelope<SspPartyResponse[]>> {
  return apiFetch(`/ssps/${sspId}/parties`);
}

export function replaceSspParties(
  sspId: string,
  payload: SspPartyPayload[],
): Promise<ApiEnvelope<SspPartyResponse[]>> {
  return apiFetch(`/ssps/${sspId}/parties`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export function listSspLocations(
  sspId: string,
): Promise<ApiEnvelope<SspLocationResponse[]>> {
  return apiFetch(`/ssps/${sspId}/locations`);
}

export function replaceSspLocations(
  sspId: string,
  payload: SspLocationPayload[],
): Promise<ApiEnvelope<SspLocationResponse[]>> {
  return apiFetch(`/ssps/${sspId}/locations`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export function listSspResponsibleParties(
  sspId: string,
): Promise<ApiEnvelope<SspResponsiblePartyResponse[]>> {
  return apiFetch(`/ssps/${sspId}/responsible-parties`);
}

export function replaceSspResponsibleParties(
  sspId: string,
  payload: SspResponsiblePartyPayload[],
): Promise<ApiEnvelope<SspResponsiblePartyResponse[]>> {
  return apiFetch(`/ssps/${sspId}/responsible-parties`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export function addSspControlImplementation(
  sspId: string,
  payload: AddSspControlImplementationPayload,
): Promise<ApiEnvelope<SspControlImplementationResponse>> {
  return apiFetch(`/ssps/${sspId}/control-implementations`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function updateSspControlImplementation(
  sspId: string,
  ciId: string,
  payload: UpdateSspControlImplementationPayload,
): Promise<ApiEnvelope<SspControlImplementationResponse>> {
  return apiFetch(`/ssps/${sspId}/control-implementations/${ciId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export function updateImplementedRequirement(
  sspId: string,
  ciId: string,
  payload: UpdateImplementedRequirementPayload,
): Promise<ApiEnvelope<SspControlImplementationResponse>> {
  return apiFetch(
    `/ssps/${sspId}/control-implementations/${ciId}/implemented-requirements`,
    { method: "PUT", body: JSON.stringify(payload) },
  );
}

export interface BulkImplementedRequirementPayload {
  controlId: string;
  implementationStatus: ImplementationStatus;
  origination?: ControlOrigination;
  description?: string;
  setParameters?: { "param-id": string; values: string[] }[];
  responsibleRoles?: { "role-id": string; "party-uuids"?: string[] }[];
  byComponents?: {
    componentUuid: string;
    description?: string;
    implementationStatus?: string;
    exportMethod?: "fully_provided" | "shared" | "not_applicable";
    provided?: {
      description?: string;
      responsibleRoles?: { "role-id": string; "party-uuids"?: string[] }[];
    };
    sortOrder: number;
  }[];
  statements?: {
    statementId: string;
    description?: string;
    sortOrder: number;
  }[];
  sortOrder?: number;
}

export function bulkUpdateImplementedRequirements(
  sspId: string,
  ciId: string,
  payload: BulkImplementedRequirementPayload[],
): Promise<ApiEnvelope<SspControlImplementationResponse[]>> {
  return apiFetch(
    `/ssps/${sspId}/control-implementations/${ciId}/implemented-requirements/bulk`,
    {
      method: "PUT",
      body: JSON.stringify(payload),
    },
  );
}

export interface SaveValidationDocumentPayload {
  implementedRequirementId: string;
  validationDocumentId: string;
  validationDocumentName: string;
}

export interface SspCiValidationDocumentResponse {
  id: string;
  controlImplementationId: string;
  implementedRequirementId: string;
  componentId: string;
  validationDocumentId: string;
  validationDocumentName: string;
  createdAt: string;
  updatedAt: string;
}

export function saveValidationDocument(
  sspId: string,
  ciId: string,
  payload: SaveValidationDocumentPayload,
): Promise<ApiEnvelope<SspCiValidationDocumentResponse>> {
  return apiFetch(
    `/ssps/${sspId}/control-implementations/${ciId}/validation-document/save`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
  );
}

export function deleteSspControlImplementation(
  sspId: string,
  ciId: string,
): Promise<ApiEnvelope<null>> {
  return apiFetch(`/ssps/${sspId}/control-implementations/${ciId}`, {
    method: "DELETE",
  });
}

export interface EvidenceValidationRequest {
  implementedRequirementId: string;
  componentId: string;
  evidenceId: string;
  orgId: string;
  controlId: string;
  controlStatement: string;
}

export interface EvidenceValidationChunk {
  chunkId: string;
  sectionTitle: string;
  pageRange: string;
  score: number;
}

export interface EvidenceValidationResult {
  id: string;
  implementedRequirementId: string;
  controlImplementationId: string;
  componentId: string;
  evidenceId: string;
  controlId: string;
  status: string;
  reasoning: string;
  missingRequirements: string[];
  supportingEvidenceChunks: EvidenceValidationChunk[];
  confidenceScore: number;
  modelUsed: string;
  validatedAt: string;
}

export function validateEvidenceForCi(
  sspId: string,
  ciId: string,
  payload: EvidenceValidationRequest,
): Promise<ApiEnvelope<EvidenceValidationResult>> {
  return apiFetch(
    `/ssps/${sspId}/control-implementations/${ciId}/evidence-validation`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
  );
}

export function putSspIrSetParameters(
  sspId: string,
  irId: string,
  setParameters: { "param-id": string; values: string[] }[],
): Promise<ApiEnvelope<unknown>> {
  return apiFetch(`/ssps/${sspId}/implemented-requirements/${irId}/set-parameters`, {
    method: "PUT",
    body: JSON.stringify({ setParameters }),
  });
}

export type AssetControlImplementationStatus =
  | "implemented"
  | "partial"
  | "planned"
  | "alternative"
  | "not-applicable";

export interface AssetControlMatrixEntry {
  byComponentId: string;
  controlId: string;
  inventoryItemId: string;
  inventoryItemDisplayName: string;
  implementationStatus: AssetControlImplementationStatus;
}

export interface AssetControlImplementationRequest {
  controlId: string;
  inventoryItemId: string;
  implementationStatus: AssetControlImplementationStatus;
}

export function getAssetControlMatrix(
  sspId: string,
  componentId: string,
): Promise<ApiEnvelope<AssetControlMatrixEntry[]>> {
  return apiFetch(`/ssps/${sspId}/components/${componentId}/asset-control-matrix`);
}

export function saveAssetControlMatrix(
  sspId: string,
  componentId: string,
  items: AssetControlImplementationRequest[],
): Promise<ApiEnvelope<AssetControlMatrixEntry[]>> {
  return apiFetch(`/ssps/${sspId}/components/${componentId}/asset-control-matrix`, {
    method: "PUT",
    body: JSON.stringify(items),
  });
}

export function recomputeControlRollups(sspId: string): Promise<ApiEnvelope<number>> {
  return apiFetch(`/ssps/${sspId}/recompute-control-rollups`, { method: "POST" });
}

export type ProviderResponsibility = "fully_implemented" | "shared" | "not_applicable";
export type InheritanceType = "fully_inherited" | "shared_inherited" | "not_applicable";

export interface ExportProvidedBlock {
  uuid?: string;
  description: string;
  responsibleRoles?: { roleId: string }[];
}

export interface ExportResponsibilityBlock {
  uuid?: string;
  description: string;
  providedUuid?: string;
  responsibleRoles?: { roleId: string }[];
}

export interface ComponentExportRow {
  byComponentId: string;
  controlId: string;
  providerResponsibility: ProviderResponsibility;
  inheritanceType: InheritanceType;
  provided: ExportProvidedBlock | null;
  responsibilities: ExportResponsibilityBlock[];
}

export interface ProviderExportSavePayload {
  providerResponsibility: ProviderResponsibility;
  provided: ExportProvidedBlock;
  responsibilities: ExportResponsibilityBlock[];
}

export function getComponentExport(
  sspId: string,
  componentId: string,
): Promise<ApiEnvelope<ComponentExportRow[]>> {
  return apiFetch(`/ssps/${sspId}/components/${componentId}/export`);
}

export type ComponentExportStatus = "fully_implemented" | "shared" | "not_applicable" | "not_authored";

export interface ComponentExportStatusRow {
  controlId: string;
  controlTitle: string;
  exportStatus: ComponentExportStatus;
  byComponentId: string | null;
  providerResponsibility: ProviderResponsibility | null;
  hasProvided: boolean;
  hasResponsibilities: boolean;
}

export function getComponentExportStatus(
  sspId: string,
  componentId: string,
): Promise<ApiEnvelope<ComponentExportStatusRow[]>> {
  return apiFetch(`/ssps/${sspId}/components/${componentId}/export/status`);
}

export function saveControlExport(
  sspId: string,
  componentId: string,
  controls: { controlId: string; export: ProviderExportSavePayload }[],
): Promise<ApiEnvelope<ComponentExportRow[]>> {
  return apiFetch(
    `/ssps/${sspId}/components/${componentId}/export`,
    { method: "PUT", body: JSON.stringify({ controls }) },
  );
}

export interface MatchedAsset {
  inventoryItemId: string;
  displayName: string;
  identifierType: string;
  identifierValue: string;
}

export interface MatchedProviderSsp {
  providerSspId: string;
  title: string;
  lifecycleStatus: "draft" | "in_review" | "published" | "archived";
  isAuthorized: boolean;
  matchedAssetCount: number;
  matchedAssets: MatchedAsset[];
}

export interface EligibleControl {
  controlId: string;
  byComponentId: string;
  componentUuid: string;
  componentTitle: string;
  providerResponsibility: ProviderResponsibility;
  inheritanceType: InheritanceType;
  provided: ExportProvidedBlock | null;
}

export interface InheritableComponent {
  id: string;
  componentUuid: string;
  componentType: string;
  title: string;
  description: string | null;
  selected: boolean;
}

export interface InheritFromSspPayload {
  providerSspId: string;
  componentIds?: string[] | null;
  controlIds?: string[] | null;
}

export interface InheritFromSspResult {
  consumerSspId: string;
  providerSspId: string;
  providerSspTitle: string;
  leveragedAuthorizationId: string;
  componentsCreated: number;
  componentsSkipped: number;
  inventoryItemsCreated: number;
  components: { id: string; title: string; status: string }[];
}

export function listMatchedProviderSsps(
  consumerSspId: string,
): Promise<ApiEnvelope<MatchedProviderSsp[]>> {
  return apiFetch(`/ssps/${consumerSspId}/matched-provider-ssps`);
}

export function listEligibleControls(
  providerSspId: string,
): Promise<ApiEnvelope<EligibleControl[]>> {
  return apiFetch(`/ssps/${providerSspId}/eligible-controls`);
}

export function listInheritableComponents(
  providerSspId: string,
): Promise<ApiEnvelope<InheritableComponent[]>> {
  return apiFetch(`/ssps/${providerSspId}/inheritable-components`);
}

export function inheritFromSsp(
  consumerSspId: string,
  payload: InheritFromSspPayload,
): Promise<ApiEnvelope<InheritFromSspResult>> {
  return apiFetch(`/ssps/${consumerSspId}/inherit-from-ssp`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function inheritFromApplication(
  sspId: string,
  applicationId: string,
): Promise<ApiEnvelope<{ componentsCreated: number }>> {
  return apiFetch(`/ssps/${sspId}/inherit-from-application`, {
    method: "POST",
    body: JSON.stringify({ applicationId }),
  });
}

export interface SspProviderSummary {
  exportedControlCount: number;
  consumerCount: number;
  hasUnauthoredExports: boolean;
}

export interface SspConsumerSummary {
  leveragedAuthorizationCount: number;
  pendingSatisfiedCount: number;
}

export interface SspRoleSummaryResponse {
  sspId: string;
  isProvider: boolean;
  isConsumer: boolean;
  isStandalone: boolean;
  providerSummary: SspProviderSummary | null;
  consumerSummary: SspConsumerSummary | null;
}

export function getSspRoleSummary(
  sspId: string,
): Promise<ApiEnvelope<SspRoleSummaryResponse>> {
  return apiFetch(`/ssps/${sspId}/role-summary`);
}

export interface PatchSatisfiedPayload {
  description: string;
  implementationStatus: string;
  responsibleRoles?: { roleId: string }[];
}

export function patchSatisfiedStatement(
  sspId: string,
  byComponentId: string,
  payload: PatchSatisfiedPayload,
): Promise<ApiEnvelope<ByComponentResponse>> {
  return apiFetch(`/ssps/${sspId}/by-components/${byComponentId}/satisfied`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export function patchByComponent(
  sspId: string,
  byComponentId: string,
  payload: PatchByComponentLinkPayload,
): Promise<ApiEnvelope<ByComponentResponse>> {
  return apiFetch(`/ssps/${sspId}/by-components/${byComponentId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export function deleteByComponent(
  sspId: string,
  byComponentId: string,
): Promise<ApiEnvelope<null>> {
  return apiFetch(`/ssps/${sspId}/by-components/${byComponentId}`, {
    method: "DELETE",
  });
}

export function getSspLifecycle(
  sspId: string,
): Promise<ApiEnvelope<SspLifecycleResponse>> {
  return apiFetch(`/ssps/${sspId}/lifecycle`);
}

export function transitionSspLifecycle(
  sspId: string,
  payload: TransitionSspPayload,
): Promise<ApiEnvelope<SspLifecycleResponse>> {
  return apiFetch(`/ssps/${sspId}/lifecycle/transition`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function getSspHistory(
  sspId: string,
): Promise<ApiEnvelope<SspHistoryEntry[]>> {
  return apiFetch(`/ssps/${sspId}/history`);
}

export function exportSsp(
  sspId: string,
  payload: SspExportPayload,
): Promise<ApiEnvelope<SspExportResponse>> {
  return apiFetch(`/ssps/${sspId}/export`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export interface SourceControlImplementationResponse {
  id: string;
  source: string;
  implementedRequirements: {
    id: string;
    controlId: string;
    description: string;
  }[];
}

export interface ImportedControlImplementationResponse {
  id: string;
  ciUuid: string;
  componentId: string;
  source: string;
  implementedRequirements: ImplementedRequirementDetailResponse[];
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface ImportComponentResponse extends SspComponentResponse {
  sourceControlImplementations: SourceControlImplementationResponse[];
  controlImplementations: ImportedControlImplementationResponse[];
}

export type ImportComponentsResponse = ImportComponentResponse[];

export function importComponentToSsp(
  sspId: string,
  componentIds: string[],
): Promise<ApiEnvelope<ImportComponentsResponse>> {
  return apiFetch(`/ssps/${sspId}/import-components`, {
    method: "POST",
    body: JSON.stringify({ component_ids: componentIds }),
  });
}

export interface SspSystemUserResponse {
  id: string;
  userUuid: string;
  title: string;
  shortName: string | null;
  description: string | null;
  roleIds: string[];
  authorizedPrivileges: {
    title: string;
    description: string;
    functions_performed: string[];
  }[];
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface AddSspSystemUserPayload {
  title: string;
  shortName?: string;
  description?: string;
  roleIds?: string[];
  authorizedPrivileges?: {
    title: string;
    description?: string;
    functions_performed?: string[];
  }[];
  sortOrder: number;
}

export interface UpdateSspSystemUserPayload {
  title?: string;
  shortName?: string;
  description?: string;
  roleIds?: string[];
  authorizedPrivileges?: {
    title: string;
    description?: string;
    functions_performed?: string[];
  }[];
}

export function listSspSystemUsers(
  sspId: string,
): Promise<ApiEnvelope<SspSystemUserResponse[]>> {
  return apiFetch(`/ssps/${sspId}/system-users`);
}

export function addSspSystemUser(
  sspId: string,
  payload: AddSspSystemUserPayload,
): Promise<ApiEnvelope<SspSystemUserResponse>> {
  return apiFetch(`/ssps/${sspId}/system-users`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function updateSspSystemUser(
  sspId: string,
  userId: string,
  payload: UpdateSspSystemUserPayload,
): Promise<ApiEnvelope<SspSystemUserResponse>> {
  return apiFetch(`/ssps/${sspId}/system-users/${userId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export function deleteSspSystemUser(
  sspId: string,
  userId: string,
): Promise<ApiEnvelope<null>> {
  return apiFetch(`/ssps/${sspId}/system-users/${userId}`, {
    method: "DELETE",
  });
}

export interface SspInterconnectionResponse {
  id: string;
  interconnectionUuid: string;
  title: string;
  description: string | null;
  direction: "incoming" | "outgoing" | "incoming-outgoing";
  remoteSystemName: string | null;
  responsibleRoles: { role_id: string; party_uuids: string[] }[];
  protocols: {
    name: string;
    title?: string;
    port_ranges: { start: number; end: number; transport: string }[];
  }[];
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface AddSspInterconnectionPayload {
  title: string;
  description?: string;
  direction: "incoming" | "outgoing" | "incoming-outgoing";
  remoteSystemName?: string;
  responsibleRoles?: { role_id: string; party_uuids?: string[] }[];
  protocols?: {
    name: string;
    title?: string;
    port_ranges?: { start: number; end: number; transport: string }[];
  }[];
  sortOrder: number;
}

export interface UpdateSspInterconnectionPayload {
  title?: string;
  description?: string;
  direction?: "incoming" | "outgoing" | "incoming-outgoing";
  remoteSystemName?: string;
  responsibleRoles?: { role_id: string; party_uuids?: string[] }[];
  protocols?: {
    name: string;
    title?: string;
    port_ranges?: { start: number; end: number; transport: string }[];
  }[];
}

export function listSspInterconnections(
  sspId: string,
): Promise<ApiEnvelope<SspInterconnectionResponse[]>> {
  return apiFetch(`/ssps/${sspId}/interconnections`);
}

export function addSspInterconnection(
  sspId: string,
  payload: AddSspInterconnectionPayload,
): Promise<ApiEnvelope<SspInterconnectionResponse>> {
  return apiFetch(`/ssps/${sspId}/interconnections`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function updateSspInterconnection(
  sspId: string,
  connId: string,
  payload: UpdateSspInterconnectionPayload,
): Promise<ApiEnvelope<SspInterconnectionResponse>> {
  return apiFetch(`/ssps/${sspId}/interconnections/${connId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export function deleteSspInterconnection(
  sspId: string,
  connId: string,
): Promise<ApiEnvelope<null>> {
  return apiFetch(`/ssps/${sspId}/interconnections/${connId}`, {
    method: "DELETE",
  });
}

export interface BaselineControlParameter {
  paramId: string;
  label: string;
  catalogValues: string[];
  profileValues: string[];
  constraints: string[];
}

export interface BaselineControlParameterSource {
  label?: string;
  usage?: string;
  class?: string;
  clazz?: string;
  constraints?: unknown[];
  guidelines?: { prose?: string }[];
  values?: string[];
}

export interface BaselineControlResolvedParameter {
  param_id?: string;
  paramId?: string;
  is_overridden?: boolean;
  isOverridden?: boolean;
  catalog?: BaselineControlParameterSource | null;
  profile?: BaselineControlParameterSource | null;
  component_definition?: BaselineControlParameterSource | null;
  componentDefinition?: BaselineControlParameterSource | null;
}

export interface BaselineControlPart {
  id?: string;
  partId: string;
  name: string;
  ns?: string;
  prose?: string;
  resolved_prose?: string;
  sortOrder?: number;
  parts?: BaselineControlPart[];
}

export interface BaselineControlDetails {
  title?: string;
  control_id?: string;
  source_href?: string;
  source_catalog_id?: string;
  parts?: BaselineControlPart[];
  params?: {
    id?: string;
    paramId: string;
    label?: string;
    clazz?: string;
    guidelines?: { prose: string }[];
    values?: string[];
    sortOrder?: number;
  }[];
}

export interface BaselineControlResponse {
  controlId: string;
  control_id?: string;
  controlTitle?: string;
  title?: string;
  family?: string;
  parameters?: BaselineControlParameter[];
  params?: BaselineControlResolvedParameter[];
  controlDetails?: BaselineControlDetails | null;
  implementationStatus?: string;
  implementedRequirements?: ImplementedRequirementDetailResponse[];
  enhancements?: unknown[];
}

export interface BaselineControlsResponse {
  totalControls: number;
  byImplementationStatus: Record<string, number>;
  controls: BaselineControlResponse[];
}

export function listBaselineControls(
  sspId: string,
  params?: { startDate?: string; endDate?: string },
): Promise<ApiEnvelope<BaselineControlResponse[]>> {
  const qs = params
    ? "?" + new URLSearchParams(
      Object.fromEntries(
        Object.entries(params).filter(([, v]) => !!v) as [string, string][]
      )
    ).toString()
    : "";
  return apiFetch(`/ssps/${sspId}/baseline-controls${qs}`);
}

export function searchBaselineControls(
  sspId: string,
  page: number,
  size: number,
  payload: ListSearchPayload,
): Promise<PagedSearchResult<BaselineControlResponse>> {
  return postPagedSearch<BaselineControlResponse>(
    `/ssps/${sspId}/baseline-controls`,
    page,
    size,
    payload,
  );
}

export interface ByComponentPayload {
  componentUuid: string;
  description?: string;
  implementationStatus?: ImplementationStatus;
  responsibleRoles?: { role_id: string; party_uuids?: string[] }[];
  inherited?: {
    provided_uuid: string;
    description?: string;
  } | null;
  satisfied?: {
    responsibility_uuid: string;
    description?: string;
  } | null;
  sortOrder: number;
}

export interface ByComponentResponse {
  id: string | null;
  componentUuid: string;
  componentName?: string;
  description: string | null;
  implementationStatus: string | null;
  responsibleRoles: { role_id: string; party_uuids: string[] }[];
  inherited: { uuid?: string; provided_uuid?: string; "provided-uuid"?: string; "leveraged-authorization-uuid"?: string; description: string | null } | null;
  satisfied: { uuid?: string; responsibility_uuid?: string; "responsibility-uuid"?: string; description: string | null; "responsible-roles"?: { roleId: string }[]; implementationStatus?: string } | null;
  provided: { uuid?: string; description: string | null; "responsible-roles"?: { roleId: string }[] } | null;
  providerResponsibility: ProviderResponsibility | null;
  responsibilities: ExportResponsibilityBlock[] | null;
  inheritanceStatus: string | null;
  pendingSatisfied?: boolean;
  validationDocumentId?: string | null;
  validationDocumentName?: string | null;
  linkedDocument?: string | null;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface AddImplementedRequirementPayload {
  controlId: string;
  implementationStatus: ImplementationStatus;
  origination?: string;
  description?: string;
  setParameters?: { "param-id": string; values: string[] }[];
  byComponents?: ByComponentPayload[];
  statements?: {
    statementId: string;
    description: string;
    sortOrder: number;
  }[];
  sortOrder: number;
}

export interface PatchImplementedRequirementPayload {
  implementationStatus?: ImplementationStatus;
  origination?: string;
  description?: string;
  setParameters?: { "param-id": string; values: string[] }[];
  byComponents?: ByComponentPayload[];
  statements?: {
    statementId: string;
    description: string;
    sortOrder: number;
  }[];
}

export interface ImplementedRequirementDetailResponse {
  id: string;
  irUuid: string;
  controlId: string;
  implementationStatus: string;
  origination: string | null;
  description: string | null;
  setParameters: { "param-id": string; values: string[] }[];
  byComponents: ByComponentResponse[];
  statements: {
    id: string;
    statementId: string;
    description: string;
    sortOrder: number;
  }[];
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export function listImplementedRequirements(
  sspId: string,
  ciId: string,
): Promise<ApiEnvelope<ImplementedRequirementDetailResponse[]>> {
  return apiFetch(
    `/ssps/${sspId}/control-implementations/${ciId}/implemented-requirements`,
  );
}

export function addImplementedRequirement(
  sspId: string,
  ciId: string,
  payload: AddImplementedRequirementPayload,
): Promise<ApiEnvelope<ImplementedRequirementDetailResponse>> {
  return apiFetch(
    `/ssps/${sspId}/control-implementations/${ciId}/implemented-requirements`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
  );
}

export function updateImplementedRequirement_ssp(
  sspId: string,
  ciId: string,
  irId: string,
  payload: PatchImplementedRequirementPayload,
): Promise<ApiEnvelope<ImplementedRequirementDetailResponse>> {
  return apiFetch(
    `/ssps/${sspId}/control-implementations/${ciId}/implemented-requirements/${irId}`,
    {
      method: "PATCH",
      body: JSON.stringify(payload),
    },
  );
}

export function deleteImplementedRequirement(
  sspId: string,
  ciId: string,
  irId: string,
): Promise<ApiEnvelope<null>> {
  return apiFetch(
    `/ssps/${sspId}/control-implementations/${ciId}/implemented-requirements/${irId}`,
    {
      method: "DELETE",
    },
  );
}

export interface SspSupportingEvidence {
  id: string;
  evidenceId: string;
  title: string;
  description?: string;
  tags?: string[];
  hasFile?: boolean;
  fileName?: string;
  fileSize?: number;
  contentType?: string;
  createdAt: string;
  updatedAt: string;
}

export function listSspSupportingEvidence(
  sspId: string,
): Promise<ApiEnvelope<SspSupportingEvidence[]>> {
  return apiFetch(`/ssps/${sspId}/supporting-evidence`);
}

export function createSspSupportingEvidence(
  sspId: string,
  payload: { title: string; description?: string; tags?: string[] },
): Promise<ApiEnvelope<SspSupportingEvidence>> {
  return apiFetch(`/ssps/${sspId}/supporting-evidence`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function uploadSspSupportingEvidenceFile(
  sspId: string,
  evidenceId: string,
  file: File,
): Promise<ApiEnvelope<SspSupportingEvidence>> {
  const formData = new FormData();
  formData.append("file", file);
  return apiFetch(`/ssps/${sspId}/supporting-evidence/${evidenceId}/upload`, {
    method: "POST",
    headers: {},
    body: formData,
  });
}

export function deleteSspSupportingEvidence(
  sspId: string,
  evidenceId: string,
): Promise<ApiEnvelope<null>> {
  return apiFetch(`/ssps/${sspId}/supporting-evidence/${evidenceId}`, {
    method: "DELETE",
  });
}

export interface ApiPoam {
  id: string;
  title?: string;
  documentId: string;
  importSspHref: string;
  sspName?: string;
  itemCount?: number;
  remediatedCount?: number;
  items?: ApiPoamItem[];
  createdAt: string;
  updatedAt: string;
}

export type PoamSeverity = "critical" | "high" | "medium" | "low";

export interface PoamFrameworkControl {
  control_id: string;
  statement?: string | null;
}

export interface PoamAffectedAsset {
  asset_uuid: string;
  hostname?: string | null;
  component_name?: string | null;
  status?: string | null;
  command?: string | null;
  evidence_data?: string | null;
  scanned_at?: string | null;
}

export interface ApiPoamItem {
  id: string;
  poamItemUuid: string;
  itemKey?: string | null;
  itemNumber?: number;
  assessmentPlanId?: string;
  title: string;
  description: string;
  controlId?: string;
  controlTitle?: string;
  statementId?: string | null;
  statementDescription?: string | null;
  status?: string;
  severity?: PoamSeverity | null;
  relatedObservations: string[] | null;
  relatedRisks: string[] | null;
  relatedFindings: string[] | null;
  techControlStatement?: string | null;
  affectedFrameworkControls?: string[] | null;
  frameworkControls?: PoamFrameworkControl[] | null;
  affectedAssetUuids?: string[] | null;
  affectedAssets?: PoamAffectedAsset[] | null;
  assigneeNames?: string[] | null;
  holdReason?: string | null;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface PoamPlan {
  planId: string;
  planTitle: string;
  itemCount: number;
  createdAt: string;
}

export interface ApiRemediation {
  id: string;
  poamItemId: string;
  planTitle: string;
  planDescription: string;
  milestones: string | null;
  scheduledCompletionDate: string | null;
  actualCompletionDate: string | null;
  status: RemediationStatusApi;
  createdAt: string;
  updatedAt: string;
}

export type RemediationStatusApi =
  | "open"
  | "in_progress"
  | "completed"
  | "delayed"
  | "cancelled";

export interface ApiMilestone {
  id?: string;
  name: string;
  due?: string;
  completed?: boolean;
}

export interface ApiRiskAdjustment {
  id: string;
  poamItemId: string;
  adjustmentType:
  | "false_positive"
  | "risk_acceptance"
  | "operational_requirement";
  justification: string;
  adjustedRiskLevel: string | null;
  approvalWorkflowId: string | null;
  approvedBy: string;
  approvedAt: string;
  effectiveFrom: string;
  effectiveUntil: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ApiMonthlySubmissionResult {
  poamId: string;
  snapshotId: string;
  totalItems: number;
  openItems: number;
  completedItems: number;
  submittedAt: string;
}

export function parseMilestones(raw: string | null): ApiMilestone[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function serializeMilestones(milestones: ApiMilestone[]): string {
  return JSON.stringify(milestones);
}

export function listPoams(): Promise<{ status: string; data: ApiPoam[] }> {
  return apiFetch<{ status: string; data: ApiPoam[] }>(`/poams`);
}

export function createPoam(
  title: string,
  importSspHref?: string,
): Promise<{ status: string; data: ApiPoam }> {
  return apiFetch<{ status: string; data: ApiPoam }>("/poams", {
    method: "POST",
    body: JSON.stringify({
      title,
      ...(importSspHref ? { importSspHref } : {}),
    }),
  });
}

export interface GeneratePoamResponse {
  poam_id: string;
  created: number;
  updated: number;
  closed: number;
  items: ApiPoamItem[];
}

export function generatePoam(
  planId: string,
): Promise<{ status: string; data: GeneratePoamResponse }> {
  return apiFetch<{ status: string; data: GeneratePoamResponse }>(
    `/poams/generate/${planId}`,
    { method: "POST" },
  );
}

export function getPoam(
  id: string,
): Promise<{ status: string; data: ApiPoam }> {
  return apiFetch<{ status: string; data: ApiPoam }>(`/poams/${id}`);
}

export function listPoamItems(
  poamId: string,
  opts: {
    planId?: string;
    status?: string;
    overdueOnly?: boolean;
  } = {},
): Promise<{ status: string; data: ApiPoamItem[] }> {
  const params = new URLSearchParams({
    ...(opts.planId ? { plan_id: opts.planId } : {}),
    ...(opts.status ? { status: opts.status } : {}),
    ...(opts.overdueOnly
      ? { overdue_only: "true" }
      : { overdue_only: "false" }),
  });
  return apiFetch<{ status: string; data: ApiPoamItem[] }>(
    `/poams/${poamId}/items?${params}`,
  );
}

export function searchPoamItems(
  poamId: string,
  page: number,
  size: number,
  payload: ListSearchPayload,
): Promise<PagedSearchResult<ApiPoamItem>> {
  return postPagedSearch<ApiPoamItem>(
    `/poams/${poamId}/items`,
    page,
    size,
    payload,
  );
}

export function getPoamItemsByPlan(
  poamId: string,
  planId: string,
): Promise<{ status: string; data: ApiPoamItem[] }> {
  return apiFetch<{ status: string; data: ApiPoamItem[] }>(
    `/poams/${poamId}/items/by-plan/${planId}`,
  );
}

export function listPoamPlans(
  poamId: string,
): Promise<{ status: string; data: PoamPlan[] }> {
  return apiFetch<{ status: string; data: PoamPlan[] }>(
    `/poams/${poamId}/plans`,
  );
}

export function createPoamItem(
  poamId: string,
  payload: {
    title: string;
    description?: string;
    relatedObservations?: string[];
    relatedRisks?: string[];
    relatedFindings?: string[];
  },
): Promise<{ status: string; data: ApiPoamItem }> {
  return apiFetch<{ status: string; data: ApiPoamItem }>(
    `/poams/${poamId}/items`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
  );
}

function remediationCacheKey(poamId: string, itemId: string): string {
  return `poam_remediation_${poamId}_${itemId}`;
}

export async function getRemediation(
  poamId: string,
  itemId: string,
): Promise<{ status: string; data: ApiRemediation } | null> {
  try {
    const result = await apiFetch<{ status: string; data: ApiRemediation }>(
      `/poams/${poamId}/items/${itemId}/remediation`,
    );
    localStorage.setItem(
      remediationCacheKey(poamId, itemId),
      JSON.stringify(result),
    );
    return result;
  } catch {
    try {
      const cached = localStorage.getItem(remediationCacheKey(poamId, itemId));
      if (cached) return JSON.parse(cached);
    } catch {
    }
    return null;
  }
}

export async function updateRemediation(
  poamId: string,
  itemId: string,
  payload: {
    planTitle: string;
    planDescription: string;
    milestones: string;
    scheduledCompletionDate: string | null;
    actualCompletionDate?: string | null;
    status: RemediationStatusApi;
    assigneeUserIds?: string[];
  },
): Promise<{ status: string; data: ApiRemediation }> {
  const result = await apiFetch<{ status: string; data: ApiRemediation }>(
    `/poams/${poamId}/items/${itemId}/remediation`,
    { method: "PUT", body: JSON.stringify(payload) },
  );
  localStorage.setItem(
    remediationCacheKey(poamId, itemId),
    JSON.stringify(result),
  );
  return result;
}

export function transitionPoamItemStatus(
  poamId: string,
  itemId: string,
  payload: { targetStatus: string; reason?: string },
): Promise<{ status: string; data: ApiPoamItem }> {
  return apiFetch<{ status: string; data: ApiPoamItem }>(
    `/poams/${poamId}/items/${itemId}/status-transitions`,
    { method: "POST", body: JSON.stringify(payload) },
  );
}

export function updatePoamItemSeverity(
  poamId: string,
  itemId: string,
  severity: PoamSeverity,
): Promise<{ status: string; data: ApiPoamItem }> {
  return apiFetch<{ status: string; data: ApiPoamItem }>(
    `/poams/${poamId}/items/${itemId}/severity`,
    { method: "PUT", body: JSON.stringify({ severity }) },
  );
}

export function submitRiskAdjustment(
  poamId: string,
  itemId: string,
  payload: {
    adjustmentType:
    | "false_positive"
    | "risk_acceptance"
    | "operational_requirement";
    justification: string;
    adjustedRiskLevel: string | null;
    effectiveFrom: string;
    effectiveUntil?: string | null;
  },
): Promise<{ status: string; data: ApiRiskAdjustment }> {
  return apiFetch<{ status: string; data: ApiRiskAdjustment }>(
    `/poams/${poamId}/items/${itemId}/risk-adjustments`,
    { method: "POST", body: JSON.stringify(payload) },
  );
}

export function submitMonthly(
  poamId: string,
): Promise<{ status: string; data: ApiMonthlySubmissionResult }> {
  return apiFetch<{ status: string; data: ApiMonthlySubmissionResult }>(
    `/poams/${poamId}/submit-monthly`,
    { method: "POST", body: JSON.stringify({}) },
  );
}

export type AssessmentPlanStatus =
  | "draft"
  | "in_review"
  | "approved"
  | "rejected";

export interface ApiReviewedControlStatement {
  id: string;
  statementId: string;
  statementDescription: string;
  assessmentMethods: string[];
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface ApiReviewedControl {
  id: string;
  controlId: string;
  controlDescription: string;
  implementationStatus?: string;
  componentName?: string;
  byComponents?: { componentUuid: string; componentName?: string }[];
  statements: ApiReviewedControlStatement[];
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface ApiAssessmentPlan {
  id: string;
  documentId: string;
  title?: string;
  importSspHref: string;
  sspId?: string;
  sspTitle?: string;
  sspLifecycleStatus?: string;
  externalSource?: string;
  status?: AssessmentPlanStatus;
  lifecycleStatus?: string;
  stageComments?: StageComment[];
  assessmentMethods?: string[];
  assessors?: string[];
  reviewedControls?: ApiReviewedControl[];
  subjects?: ApiAssessmentSubject[];
  activities?: ApiAssessmentActivity[];
  assets?: ApiAssessmentAsset[];
  tasks?: ApiAssessmentTask[];
  task?: {
    id: string;
    startDate: string;
    endDate: string;
    assignees?: {
      id: string;
      userId: string;
      email: string;
      displayName: string;
      assignedAt: string;
    }[];
    createdAt: string;
    updatedAt: string;
  };
  controlCount?: number;
  assessedControlCount?: number;
  startDate?: string;
  endDate?: string;
  owningOrgUnitId?: string | null;
  reviewCompletedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ApiAssessmentSubject {
  id: string;
  subjectType: string;
  description: string;
  includeSubjects: string;
  excludeSubjects: string;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface ApiAssessmentActivity {
  id: string;
  activityUuid: string;
  title: string;
  description: string;
  method: "interview" | "examine" | "test";
  relatedControls: string;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface ApiAssessmentAsset {
  id: string;
  assetUuid: string;
  assetType: string;
  title: string;
  description: string;
  componentDetails: string;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface ApiTaskAssignee {
  id: string;
  userId: string;
  email: string;
  displayName: string;
  assignedAt: string;
}

export interface ApiAssessmentTask {
  id: string;
  taskUuid?: string;
  taskType?: "action" | "milestone";
  title?: string;
  description?: string;
  startDate: string;
  endDate: string;
  timing?: string;
  assignees?: ApiTaskAssignee[];
  dependencies?: string[] | null;
  associatedActivities?: string[] | null;
  sortOrder?: number;
  createdAt: string;
  updatedAt: string;
}

export function listAssessmentPlans(): Promise<{
  status: string;
  data: ApiAssessmentPlan[];
}> {
  return apiFetch<{ status: string; data: ApiAssessmentPlan[] }>(
    `/assessment-plans`,
  );
}

export function createAssessmentPlan(
  title: string,
  importSspHref?: string,
  owningOrgUnitId?: string,
): Promise<{ status: string; data: ApiAssessmentPlan }> {
  return apiFetch<{ status: string; data: ApiAssessmentPlan }>(
    "/assessment-plans",
    {
      method: "POST",
      body: JSON.stringify({
        title,
        ...(importSspHref ? { importSspHref } : {}),
        ...(owningOrgUnitId ? { owningOrgUnitId } : {}),
      }),
    },
  );
}

export function getAssessmentPlan(
  id: string,
): Promise<{ status: string; data: ApiAssessmentPlan }> {
  return apiFetch<{ status: string; data: ApiAssessmentPlan }>(
    `/assessment-plans/${id}/detail`,
  );
}

export function addAssessmentSubject(
  planId: string,
  payload: {
    subjectType: string;
    description: string;
    includeSubjects: string;
    excludeSubjects: string;
  },
): Promise<{ status: string; data: ApiAssessmentSubject }> {
  return apiFetch<{ status: string; data: ApiAssessmentSubject }>(
    `/assessment-plans/${planId}/subjects`,
    { method: "POST", body: JSON.stringify(payload) },
  );
}

export function addAssessmentActivity(
  planId: string,
  payload: {
    title: string;
    description: string;
    method: "interview" | "examine" | "test";
    relatedControls: string;
  },
): Promise<{ status: string; data: ApiAssessmentActivity }> {
  return apiFetch<{ status: string; data: ApiAssessmentActivity }>(
    `/assessment-plans/${planId}/activities`,
    { method: "POST", body: JSON.stringify(payload) },
  );
}

export function addAssessmentAsset(
  planId: string,
  payload: {
    assetType: string;
    title: string;
    description: string;
    componentDetails?: string;
  },
): Promise<{ status: string; data: ApiAssessmentAsset }> {
  return apiFetch<{ status: string; data: ApiAssessmentAsset }>(
    `/assessment-plans/${planId}/assets`,
    { method: "POST", body: JSON.stringify(payload) },
  );
}

export function addAssessmentTask(
  planId: string,
  payload: {
    userIds: string[];
    startDate: string;
    endDate: string;
  },
): Promise<{ status: string; data: ApiAssessmentTask }> {
  return apiFetch<{ status: string; data: ApiAssessmentTask }>(
    `/assessment-plans/${planId}/tasks`,
    { method: "PUT", body: JSON.stringify(payload) },
  );
}

export function saveAssessmentSchedule(
  planId: string,
  payload: {
    assigneeUserIds: string[];
    startDate: string;
    endDate: string;
  },
): Promise<{ status: string; data: ApiAssessmentPlan }> {
  return apiFetch<{ status: string; data: ApiAssessmentPlan }>(
    `/assessment-plans/${planId}/schedule`,
    { method: "PUT", body: JSON.stringify(payload) },
  );
}

export interface ApiUser {
  id: string;
  email: string;
  givenName: string;
  familyName: string;
  displayName: string;
  oidcSubject: string;
  isActive: boolean;
  platformRoles: string[];
  createdAt: string;
}

export function listUsers(
  owningOrgUnitId?: string,
  role?: string,
  applicationId?: string,
): Promise<ApiEnvelope<ApiUser[]>> {
  const qs = new URLSearchParams();
  if (owningOrgUnitId) qs.set("org_id", owningOrgUnitId);
  if (role) qs.set("role", role);
  if (applicationId) qs.set("applicationId", applicationId);
  const q = qs.toString();
  return apiFetch<ApiEnvelope<ApiUser[]>>(`/users${q ? `?${q}` : ""}`);
}

export function setAssessmentMethod(
  planId: string,
  assessmentMethod: string,
): Promise<ApiEnvelope<unknown>> {
  return apiFetch<ApiEnvelope<unknown>>(`/assessment-plans/${planId}/method`, {
    method: "PUT",
    body: JSON.stringify({ assessmentMethod }),
  });
}

export function getAssessmentPlanReviewedControls(
  planId: string,
): Promise<{ status: string; data: ApiReviewedControl[] }> {
  return apiFetch<{ status: string; data: ApiReviewedControl[] }>(
    `/assessment-plans/${planId}/reviewed-controls`,
  );
}

export const EXECUTION_ROW_SORT_COLUMNS: Record<string, string> = {
  control: "controlId",
  title: "controlDescription",
  statement: "statementDescription",
  assessmentMethod: "assessmentMethod",
  testMethod: "executionMethod",
  status: "status",
};

export function searchAssessmentExecutionRows(
  executionId: string,
  page: number,
  size: number,
  payload: ListSearchPayload,
): Promise<PagedSearchResult<ControlExecutionStatusResponse>> {
  return postPagedSearch<ControlExecutionStatusResponse>(
    `/assessment-execution/${executionId}/control-statuses`,
    page,
    size,
    payload,
  );
}

export function searchAssessmentReviewItems(
  executionId: string,
  page: number,
  size: number,
  payload: ListSearchPayload,
): Promise<PagedSearchResult<ExecutionReviewItem>> {
  return postPagedSearch<ExecutionReviewItem>(
    `/assessment-execution/${executionId}/review-items`,
    page,
    size,
    payload,
  );
}

export const ASSESSMENT_RESULT_SORT_COLUMNS: Record<string, string> = {
  controlId: "controlId",
  description: "controlDescription",
  method: "assessmentMethod",
  status: "satisfied",
};

export interface AssessmentResultControlRow {
  id?: string;
  controlId: string;
  statementId?: string | null;
  controlDescription?: string | null;
  testMethod?: string | null;
  finalDetermination?: string | null;
  compiledAt?: string | null;
  kind?: "declarative_group" | "automation";
  satisfied?: boolean;
  method?: string | null;
  assessment_status?: string | null;
  state?: string | null;
  /** Execution-view shaped statement list, present on the raw rows. */
  statements?: AssessmentExecutionViewStatement[];
  mode?: string;
}

export function searchAssessmentResultControls(
  resultId: string,
  page: number,
  size: number,
  payload: ListSearchPayload & {
    method?: string | string[] | null;
    result?: string | string[] | null;
  },
): Promise<PagedSearchResult<AssessmentResultControlRow>> {
  return postPagedSearch<AssessmentResultControlRow>(
    `/assessment-results/${resultId}/controls`,
    page,
    size,
    {
      ...payload,
      filters: {
        ...payload.filters,
        method: payload.method,
        result: payload.result,
      },
    },
  );
}

export const PLAN_CONTROL_SORT_COLUMNS: Record<string, string> = {
  controlId: "controlId",
  title: "controlDescription",
  statement: "statementDescription",
  method: "assessmentMethod",
  status: "implementationStatus",
};

export function searchAssessmentPlanReviewedControls(
  planId: string,
  page: number,
  size: number,
  payload: ListSearchPayload,
): Promise<PagedSearchResult<ApiReviewedControl>> {
  return postPagedSearch<ApiReviewedControl>(
    `/assessment-plans/${planId}/reviewed-controls`,
    page,
    size,
    payload,
  );
}

export function saveReviewedControls(
  planId: string,
  controls: {
    controlId: string;
    controlDescription: string;
    implementationStatus?: string;
    componentName?: string;
    statements: {
      statementId: string;
      statementDescription: string;
      assessmentMethods: string[];
    }[];
  }[],
): Promise<{ status: string; data: ApiReviewedControl[] }> {
  return apiFetch<{ status: string; data: ApiReviewedControl[] }>(
    `/assessment-plans/${planId}/reviewed-controls`,
    { method: "PUT", body: JSON.stringify({ controls }) },
  );
}

// Demo-only trigger: posts the raw JSON payload as-is to /assessment-plans/full.
// Used by the internal demo page (AssessmentPlanFullDemo) to showcase the API to customers
// without constraining the payload to createAssessmentPlanFull's typed shape.
export function triggerAssessmentPlanFullDemo(
  payload: Record<string, unknown>,
): Promise<unknown> {
  return apiFetch<unknown>("/assessment-plans/full", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function createAssessmentPlanFull(payload: {
  title: string;
  importSspHref?: string;
  controls: {
    controlId: string;
    controlDescription: string;
    statements: {
      statementId: string;
      statementDescription: string;
      assessmentMethods: string[];
    }[];
  }[];
  assigneeUserIds: string[];
  startDate: string;
  endDate: string;
}): Promise<{ status: string; data: ApiAssessmentPlan }> {
  return apiFetch<{ status: string; data: ApiAssessmentPlan }>(
    "/assessment-plans/full",
    { method: "POST", body: JSON.stringify(payload) },
  );
}

export function submitAssessmentPlan(
  planId: string,
  orgId?: string,
): Promise<{ status: string; data: ApiAssessmentPlan }> {
  const url = orgId
    ? `/assessment-plans/${planId}/submit?orgId=${orgId}`
    : `/assessment-plans/${planId}/submit`;
  return apiFetch<{ status: string; data: ApiAssessmentPlan }>(
    url,
    { method: "POST" },
  );
}

export function updateAssessmentPlanStatus(
  planId: string,
  targetStatus: AssessmentPlanStatus,
  comments?: string,
): Promise<{ status: string; data: ApiAssessmentPlan }> {
  return apiFetch<{ status: string; data: ApiAssessmentPlan }>(
    `/assessment-plans/${planId}/status`,
    {
      method: "PUT",
      body: JSON.stringify({ targetStatus, comments: comments ?? "" }),
    },
  );
}

export interface TriggerEvalControl {
  control_id: string;
  control_status: string;
  evidence?: unknown[];
}

export interface TriggerEvalAssetResult {
  metadata: {
    catalog_name?: string;
    data_collected_date?: string;
    uuid?: string;
    props?: Array<{ name: string; value: string }>;
  };
  controls: TriggerEvalControl[];
}

export type TriggerEvalResponse =
  | TriggerEvalAssetResult
  | TriggerEvalAssetResult[]
  | null;

export function triggerAssessmentPlan(
  planId: string,
  opts?: { esIndex?: string; signal?: AbortSignal },
): Promise<{ status: string; data: TriggerEvalResponse }> {
  const idx = opts?.esIndex?.trim();
  const qs = idx ? `?es_index=${encodeURIComponent(idx)}` : "";
  return apiFetch<{ status: string; data: TriggerEvalResponse }>(
    `/assessment-plans/${planId}/trigger${qs}`,
    { method: "POST", signal: opts?.signal },
  );
}

export interface TriggerPayloadAsset {
  asset_id: string;
  hostname?: string;
  ip_address?: string;
  es_index?: string | null;
  controls: Array<{ control_id: string; params: unknown }>;
}

export interface TriggerPayloadComponent {
  component_type: string;
  component_name: string;
  assets: TriggerPayloadAsset[];
}

export interface TriggerPayload {
  assessment_id: string;
  assessment_name: string;
  assessment_date: string;
  components: TriggerPayloadComponent[];
}

export function getAssessmentPlanTriggerPayload(
  planId: string,
  opts?: { esIndex?: string },
): Promise<{ status: string; data: TriggerPayload }> {
  const idx = opts?.esIndex?.trim();
  const qs = idx ? `?es_index=${encodeURIComponent(idx)}` : "";
  return apiFetch<{ status: string; data: TriggerPayload }>(
    `/assessment-plans/${planId}/trigger-payload${qs}`,
    { method: "GET" },
  );
}

export interface PendingControlStatement {
  id: string;
  statementId: string;
  statementLabel?: string;
  statementDescription: string;
  assessmentMethods: string[];
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
  executionMethod?: "opa" | "declarative" | "declarative_evidence" | null;
  state?: string;
  verdict?: string;
  justification?: string | null;
  note?: string | null;
  needsEvidence?: boolean;
  reviewStatus?: string | null;
  iterations?: AssessmentExecutionViewIteration[];
  evidenceFiles?: AssessmentExecutionViewEvidenceFile[];
}

export interface PendingControl {
  id: string;
  controlId: string;
  controlDescription: string;
  statements: PendingControlStatement[];
}

export function getPendingControls(
  planId: string,
  source = "openstack",
): Promise<ApiEnvelope<PendingControl[]>> {
  return apiFetch<ApiEnvelope<PendingControl[]>>(
    `/assessment-execution/${planId}/pending-controls?source=${encodeURIComponent(source)}`,
  );
}

export interface MappedOpaControlResult {
  opaControlId: string;
  catalogName: string | null;
  controlStatus: string;
  evidence: Record<string, unknown>[];
  assetUuid: string | null;
  assetName: string | null;
  assetIdentifierType: string | null;
}

export interface AssessmentExecutionViewTechControl {
  tech_control_id: string;
  tech_control_description?: string;
  status: string;
  command?: string;
  evidence_data?: string;
  evidence_timestamp?: string;
}

export interface AssessmentExecutionViewAsset {
  asset_uuid: string;
  asset_hostname: string;
  ip_address?: string;
  component_name: string;
  scanned_at?: string;
  controls: AssessmentExecutionViewTechControl[];
}

export interface AssessmentExecutionViewEvidenceFile {
  supporting_evidence_id: string;
  title: string;
  file_name: string;
}

export interface AssessmentExecutionViewIteration {
  iteration_number: number;
  statement_execution_id?: string;
  execution_method?: "opa" | "declarative" | "declarative_evidence" | string | null;
  state?: string;
  justification?: string | null;
  saved_at?: string | null;
  review_status?: string | null;
  reviewer_note?: string | null;
  needs_evidence?: boolean;
  evidence_files?: AssessmentExecutionViewEvidenceFile[];
  reviewed_at?: string | null;
}

export interface AssessmentExecutionViewStatement {
  statement_id: string;
  statement_label: string;
  statement_description: string;
  assessment_methods: string[];
  needs_evidence: boolean;
  verdict: string;
  assessment_status?: string | null;
  execution_method?: "opa" | "declarative" | "declarative_evidence" | null;
  state?: "NEW" | "AWAITING_REVIEW" | "FOLLOWUP" | "FOLLOWUP_RESOLVED" | "REVIEWED" | string;
  iteration?: number;
  statement_execution_id?: string;
  justification?: string | null;
  evidence_files?: AssessmentExecutionViewEvidenceFile[];
  assets?: AssessmentExecutionViewAsset[];
  review_status?: string | null;
  note?: string | null;
  reviewer_note?: string | null;
  iterations?: AssessmentExecutionViewIteration[];
  reviewed_by?: string | null;
  reviewed_at?: string | null;
}

export interface AssessmentExecutionViewControl {
  plan_control_id: string;
  control_id: string;
  control_description: string;
  component_names: string[];
  mode: "AUTOMATED" | "MANUAL" | string;
  statements: AssessmentExecutionViewStatement[];
}

export interface AssessmentExecutionViewResponse {
  plan_id: string;
  ssp_id: string;
  review_complete?: boolean;
  controls: AssessmentExecutionViewControl[];
}

export interface ControlExecutionStatusResponse {
  planControlId: string;
  controlId: string;
  controlDescription: string | null;
  statements: PendingControlStatement[];
  sortOrder: number;
  implementationStatus: string | null;
  controlType: "automated" | "manual";
  executable: boolean;
  executed: boolean;
  followUpPending: boolean;
  followUpQuestion: string | null;
  opaOverallStatus: "passed" | "failed" | "pending" | null;
  opaResults: MappedOpaControlResult[];
  determination: boolean | null;
  justification: string | null;
  executedAt: string | null;
  executionMode?: string;
  executionViewAssets?: AssessmentExecutionViewAsset[];
  executionViewStatements?: AssessmentExecutionViewStatement[];
  assessment_status?: string | null;
  state?: string | null;
}

export function getAssessmentExecutionView(
  planId: string,
): Promise<ApiEnvelope<AssessmentExecutionViewResponse>> {
  return apiFetch<ApiEnvelope<AssessmentExecutionViewResponse>>(
    `/assessment-plans/${planId}/execution-view`,
  );
}

export function getControlExecutionStatuses(
  planId: string,
  moduleName?: string,
): Promise<ApiEnvelope<ControlExecutionStatusResponse[]>> {
  const url = moduleName
    ? `/assessment-execution/${planId}/control-statuses?moduleName=${moduleName}`
    : `/assessment-execution/${planId}/control-statuses`;
  return apiFetch<ApiEnvelope<ControlExecutionStatusResponse[]>>(url);
}

export interface ResultViolation {
  summary: string;
  role?: string;
  project?: string;
  user_name?: string;
  [key: string]: string | undefined;
}

export interface OpaResult {
  opaControlId: string;
  title: string;
  component: string;
  status: string;
  passed: boolean;
  hasData: boolean;
  controlStatement: string;
  evidenceCounts: Record<string, number>;
  violations: ResultViolation[];
  passingEvidence: Record<string, unknown>[];
}

export interface ResultControlCheck {
  controlId: string;
  controlTitle: string;
  statementId?: string;
  statementDescription?: string;
  status: string;
  passed: boolean;
  opaResults: OpaResult[];
}

export interface AssessmentResultResponse {
  source: string;
  collectedAt: string;
  requested: string[];
  foundCount: number;
  controls: ResultControlCheck[];
  notFound: string[];
}

export function executeAssessmentControls(
  controlIds: string[],
  source = "openstack",
): Promise<AssessmentResultResponse> {
  return apiFetch<AssessmentResultResponse>(
    `/assessment-results/controls?source=${encodeURIComponent(source)}`,
    { method: "POST", body: JSON.stringify(controlIds) },
  );
}

export function getAssessmentResultControls(
  source = "openstack",
): Promise<AssessmentResultResponse> {
  return apiFetch<AssessmentResultResponse>(
    `/assessment-results/controls?source=${encodeURIComponent(source)}`,
  );
}

export interface DeclarativeStatementPayload {
  planControlId: string;
  statementId: string;
  determination: boolean;
  justification: string;
}

export interface DeclarativePayload {
  statements: DeclarativeStatementPayload[];
}

export function submitDeclarativeAssessment(
  planId: string,
  payload: DeclarativePayload,
): Promise<ApiEnvelope<unknown>> {
  return apiFetch<ApiEnvelope<unknown>>(
    `/assessment-execution/${planId}/declarative`,
    { method: "POST", body: JSON.stringify(payload) },
  );
}

export type ReviewStatus =
  | "pending"
  | "approved"
  | "accepted"
  | "rejected"
  | "completed"
  | "follow_up_raised"
  | "follow_up_resolved"
  | null;

export interface ExecutionResponse {
  id: string;
  planControlId: string;
  controlId: string;
  controlDescription: string;
  statementId: string;
  statementLabel?: string;
  statementDescription: string;
  executionMethod: string;
  determination: boolean;
  justification: string;
  executedBy: string;
  executedAt: string;
  reviewStatus: ReviewStatus;
  editable: boolean;
  followUpQuestion: string | null;
  createdAt: string;
  updatedAt: string;
  assessment_status?: string | null;
  state?: string | null;
}

export function getExecutions(
  planId: string,
): Promise<ApiEnvelope<ExecutionResponse[]>> {
  return apiFetch<ApiEnvelope<ExecutionResponse[]>>(
    `/assessment-execution/${planId}`,
  );
}

export interface ReviewHistoryEntry {
  id: string;
  reviewStatus: string;
  followUpQuestion: string | null;
  followUpResponse: string | null;
  followUpRespondedBy: string | null;
  followUpRespondedAt: string | null;
  followUpEvidenceIds?: string[];
  reviewedBy: string;
  reviewedAt: string;
}

export interface ExecutionReviewItem {
  execution: ExecutionResponse;
  currentReviewStatus: string;
  reviewHistory: ReviewHistoryEntry[];
  assessment_status?: string | null;
}

export function getExecutionReviews(
  planId: string,
  moduleName?: string,
): Promise<ApiEnvelope<ExecutionReviewItem[]>> {
  const url = moduleName
    ? `/assessment-execution/${planId}/review?moduleName=${moduleName}`
    : `/assessment-execution/${planId}/review`;
  return apiFetch<ApiEnvelope<ExecutionReviewItem[]>>(url);
}

export interface ReviewSubmitItem {
  statementExecutionId: string;
  status: "accepted" | "approved" | "follow_up_raised" | "rejected";
  reviewStatus?: "pending" | "approved" | "follow_up_raised";
  reviewComments?: string;
  followUpQuestion?: string;
  evidenceIds?: string[];
  needsEvidence?: boolean;
}

export interface ReviewSubmitPayload {
  reviews: ReviewSubmitItem[];
}

export function submitExecutionReviews(
  planId: string,
  payload: ReviewSubmitPayload,
): Promise<ApiEnvelope<unknown>> {
  return apiFetch<ApiEnvelope<unknown>>(
    `/assessment-execution/${planId}/review`,
    { method: "POST", body: JSON.stringify(payload) },
  );
}

export interface StatementReviewPayload {
  action: "accept" | "reject" | "followup";
  needs_evidence?: boolean;
  note?: string;
}

export interface StatementReviewResponse {
  review_id: string;
  statement_execution_id: string;
  plan_id: string;
  statement_id: string;
  iteration: number;
  state: "REVIEWED" | "FOLLOWUP" | string;
  review_status: "accepted" | "rejected" | "follow_up_raised" | string;
  needs_evidence: boolean;
  note: string | null;
  reviewed_by: string;
  reviewed_at: string;
}

export function reviewAssessmentPlanStatement(
  planId: string,
  statementId: string,
  payload: StatementReviewPayload,
): Promise<ApiEnvelope<StatementReviewResponse>> {
  return apiFetch<ApiEnvelope<StatementReviewResponse>>(
    `/assessment-plans/${planId}/statements/${statementId}/review`,
    { method: "POST", body: JSON.stringify(payload) },
  );
}

export interface FinalizeReviewsResponse {
  assessmentResultsId: string | null;
  planId: string;
  reviewsFinalized: number;
}

export function finalizeReviews(
  planId: string,
): Promise<ApiEnvelope<FinalizeReviewsResponse>> {
  return apiFetch<ApiEnvelope<FinalizeReviewsResponse>>(
    `/assessment-execution/${planId}/finalize-reviews`,
    { method: "POST" },
  );
}

export interface LlmControlValidationResult {
  controlId: string;
  status: "pass" | "fail" | "partial";
  confidence: number;
  reasoning: string;
  recommendations: string[];
}

export interface LlmValidationResponse {
  results: LlmControlValidationResult[];
}

export function validateControlsWithLlm(
  planId: string,
  controlIds: string[],
  executionId?: string,
): Promise<ApiEnvelope<LlmValidationResponse>> {
  return apiFetch<ApiEnvelope<LlmValidationResponse>>(
    `/assessment-execution/${planId}/controls/validate-llm`,
    {
      method: "POST",
      body: JSON.stringify({ controlIds, ...(executionId ? { executionId } : {}) }),
    },
  );
}

export interface FollowUpItem {
  id: string;
  statementExecutionId: string;
  reviewStatus: string;
  needsEvidence: boolean;
  followUpQuestion: string;
  followUpResponse: string | null;
  reviewedBy: string;
  reviewedAt: string;
  createdAt?: string;
}

export function getFollowUps(
  planId: string,
): Promise<ApiEnvelope<FollowUpItem[]>> {
  return apiFetch<ApiEnvelope<FollowUpItem[]>>(
    `/assessment-execution/${planId}/follow-ups`,
  );
}

export function respondToFollowUp(
  planId: string,
  executionId: string,
  followUpResponse: string,
  evidenceIds?: string[],
  determination?: boolean,
  justification?: string,
): Promise<ApiEnvelope<unknown>> {
  return apiFetch<ApiEnvelope<unknown>>(
    `/assessment-execution/${planId}/follow-ups/${executionId}`,
    {
      method: "PUT",
      body: JSON.stringify({
        followUpResponse: followUpResponse,
        ...(determination !== undefined ? { determination } : {}),
        ...(justification ? { justification } : {}),
        ...(evidenceIds?.length ? { evidenceIds } : {}),
      }),
    },
  );
}

export function getDeclarativeResults(
  planId: string,
  moduleName?: string,
): Promise<ApiEnvelope<ExecutionReviewItem[]>> {
  const url = moduleName
    ? `/assessment-results/plans/${planId}/declarative-results?moduleName=${moduleName}`
    : `/assessment-results/plans/${planId}/declarative-results`;
  return apiFetch<ApiEnvelope<ExecutionReviewItem[]>>(url);
}

export function getReviewedControls(
  planId: string,
): Promise<ApiEnvelope<PendingControl[]>> {
  return apiFetch<ApiEnvelope<PendingControl[]>>(
    `/assessment-plans/${planId}/reviewed-controls`,
  );
}


export interface DeclarativeEvidenceStatement {
  planControlId: string;
  statementId: string;
  determination: boolean;
  evidenceIds: string[];
}

export interface DeclarativeEvidenceExecution {
  id: string;
  planControlId: string;
  statementId: string;
  executionMethod: string;
  determination: boolean;
  justification: string;
  executedBy: string;
  executedAt: string;
  reviewStatus: string | null;
  followUpQuestion: string | null;
  createdAt: string;
  updatedAt: string;
}

export function submitDeclarativeEvidence(
  planId: string,
  payload: { statements: DeclarativeEvidenceStatement[] },
): Promise<ApiEnvelope<DeclarativeEvidenceExecution[]>> {
  return apiFetch(`/assessment-execution/${planId}/declarative-evidence`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export interface StatementExecutionPayload {
  execution_method?: "opa" | "declarative" | "declarative_evidence";
  justification?: string;
}

export interface StatementExecutionResponse {
  statement_execution_id: string;
  iteration: number;
  state: string;
  verdict: string;
  assets?: Array<{
    component_name: string;
    asset_uuid: string;
    controls?: Array<{
      tech_control_id: string;
      status: string;
      evidence_data?: unknown;
    }>;
  }>;
}

export function executeStatement(
  planId: string,
  statementId: string,
  payload?: StatementExecutionPayload,
): Promise<ApiEnvelope<StatementExecutionResponse>> {
  return apiFetch<ApiEnvelope<StatementExecutionResponse>>(
    `/assessment-plans/${planId}/statements/${statementId}/execution`,
    { method: "POST", body: JSON.stringify(payload || {}) },
  );
}

export function finalizeExecution(
  planId: string,
  source = "openstack",
): Promise<ApiEnvelope<unknown>> {
  return apiFetch<ApiEnvelope<unknown>>(
    `/assessment-execution/${planId}/finalize-execution?source=${encodeURIComponent(source)}`,
    { method: "POST" },
  );
}

export function submitForReview(
  planId: string,
  source = "openstack",
): Promise<ApiEnvelope<unknown>> {
  return apiFetch<ApiEnvelope<unknown>>(
    `/assessment-execution/${planId}/submit-for-review?source=${encodeURIComponent(source)}`,
    { method: "POST" },
  );
}

export interface ReviewReadinessResponse {
  allReviewed: boolean;
  totalExecutions: number;
  reviewedExecutions: number;
  pendingExecutions: number;
  followUpPending: number;
  blockers: string[];
}

export function getReviewReadiness(
  planId: string,
): Promise<ApiEnvelope<ReviewReadinessResponse>> {
  return apiFetch<ApiEnvelope<ReviewReadinessResponse>>(
    `/assessment-execution/${planId}/review-readiness`,
  );
}

export interface SSEMessage {
  event?: string;
  data: string;
  id?: string;
}

export interface SSESubscribeOptions {
  onMessage: (message: SSEMessage) => void;
  method?: "GET" | "POST";
  body?: BodyInit | Record<string, unknown> | null;
  signal?: AbortSignal;
}

export async function subscribeSSE(
  path: string,
  { onMessage, method = "GET", body, signal }: SSESubscribeOptions,
): Promise<void> {
  const isJsonBody =
    body != null &&
    typeof body === "object" &&
    !(body instanceof FormData) &&
    !(body instanceof Blob) &&
    !(body instanceof ArrayBuffer) &&
    !(body instanceof URLSearchParams);
  const response = await fetch(`${BASE_URL}${path}`, {
    method,
    headers: {
      ...getAuthHeaders(),
      Accept: "text/event-stream",
      ...(isJsonBody ? { "Content-Type": "application/json" } : {}),
    },
    body:
      body == null
        ? undefined
        : isJsonBody
          ? JSON.stringify(body)
          : (body as BodyInit),
    signal,
  });
  if (!response.ok) {
    const detail = await response.text().catch(() => "");
    throw new Error(briefHttpError(response.status, detail, response.statusText));
  }
  if (!response.body) return;

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  const emit = (frame: string) => {
    const fields = frame.split(/\r?\n/);
    let event: string | undefined;
    let id: string | undefined;
    const data: string[] = [];
    for (const line of fields) {
      if (line.startsWith("event:")) event = line.slice(6).trim();
      else if (line.startsWith("id:")) id = line.slice(3).trim();
      else if (line.startsWith("data:")) data.push(line.slice(5).trimStart());
    }
    if (data.length > 0) onMessage({ event, id, data: data.join("\n") });
  };

  while (true) {
    const { done, value } = await reader.read();
    buffer += decoder.decode(value, { stream: !done });
    const frames = buffer.split(/\r?\n\r?\n/);
    buffer = frames.pop() ?? "";
    frames.forEach(emit);
    if (done) {
      if (buffer.trim()) emit(buffer);
      return;
    }
  }
}


export interface EvidenceByStatementItem {
  linkId: string;
  assessmentPlanId: string;
  statementId: string;
  supportingEvidenceId: string;
  linkedBy: string;
  linkedAt: string;
  title: string;
  tags: string[];
  hasFile: boolean;
  fileName: string | null;
  fileSize: number | null;
  contentType: string | null;
}

export interface AssessmentResultByPlan {
  id: string;
  documentId: string;
  assessmentPlanId: string;
  title: string;
  lifecycleStatus: string;
  importApHref: string;
  createdAt: string;
  updatedAt: string;
}

export interface CompiledResult {
  id: string;
  controlId: string;
  statementId: string;
  finalDetermination:
  | "satisfied"
  | "not-satisfied"
  | "partially-satisfied"
  | "not-applicable";
  contributingAttestationIds: string[];
  contributingDirectFindingIds: string[];
  contributingDerivedFindingIds: string[];
  reconciliationMethod: string;
  reconciliationNotes: string | null;
  compiledBy: string;
  compiledAt: string;
}

export interface ExecutionSummary {
  totalStatements: number;
  passed: number;
  failed: number;
  declarativePassed: number;
  declarativeFailed: number;
  declarativeEvidencePassed: number;
  declarativeEvidenceFailed: number;
}

export function getAssessmentResultByPlan(
  planId: string,
): Promise<ApiEnvelope<AssessmentResultByPlan>> {
  return apiFetch<ApiEnvelope<AssessmentResultByPlan>>(
    `/assessment-results/by-plan/${planId}`,
  );
}

export function compileResults(
  arId: string,
  source?: string,
  owningOrgUnitId?: string,
): Promise<ApiEnvelope<CompiledResult[]>> {
  const params = new URLSearchParams();
  if (source) params.append("source", source);
  if (owningOrgUnitId) params.append("orgId", owningOrgUnitId);
  const q = params.toString() ? `?${params.toString()}` : "";
  return apiFetch<ApiEnvelope<CompiledResult[]>>(
    `/assessment-results/${arId}/compile${q}`,
    { method: "POST" },
  );
}

export function getCompiledResults(
  arId: string,
  controlId?: string,
): Promise<ApiEnvelope<CompiledResult[]>> {
  const q = controlId ? `?control_id=${encodeURIComponent(controlId)}` : "";
  return apiFetch<ApiEnvelope<CompiledResult[]>>(
    `/assessment-results/${arId}/compiled-results${q}`,
  );
}

export function getExecutionSummary(
  planId: string,
): Promise<ApiEnvelope<ExecutionSummary>> {
  return apiFetch<ApiEnvelope<ExecutionSummary>>(
    `/assessment-results/plans/${planId}/execution-summary`,
  );
}

export function getEvidenceByStatement(
  planId: string,
  statementId: string,
): Promise<ApiEnvelope<EvidenceByStatementItem[]>> {
  return apiFetch<ApiEnvelope<EvidenceByStatementItem[]>>(
    `/assessment-execution/${planId}/evidence/by-statement?statementId=${encodeURIComponent(statementId)}`,
  );
}

export async function getLicense(): Promise<ApiEnvelope<LicenseDetails>> {
  const res = await apiFetch<ApiEnvelope<LicenseDetails>>("/license");
  const key = res.data?.licenseKey;
  if (key) {
    try {
      res.data.licenseKey = await decryptCipher(key);
    } catch {
      console.warn("Unable to decrypt license key; hiding it.");
      res.data.licenseKey = "";
    }
  }
  return res;
}

export function validateLicense(
  licenseKey: string,
): Promise<LicenseValidationResponse> {
  return apiFetch<LicenseValidationResponse>("/license/validate", {
    method: "POST",
    body: JSON.stringify({ licenseKey }),
  });
}

export interface AssetApplicationMapping {
  id: string;
  assetId: string;
  applicationId: string;
  applicationName: string;
  applicationShortName: string | null;
  createdAt: string;
}

export interface OrgUnit {
  id: string;
  name: string;
  shortName?: string;
  description?: string;
  parentId?: string;
  path?: string;
  orgType: string;
  isActive: boolean;
  children?: OrgUnit[];
  createdAt?: string;
  updatedAt?: string;
}

export function getOrgUnitTree(
  orgTypes?: string[],
): Promise<ApiEnvelope<OrgUnit[]>> {
  const types = (orgTypes ?? [])
    .map((type) => `&org_type=${encodeURIComponent(type)}`)
    .join("");
  return apiFetch<ApiEnvelope<OrgUnit[]>>(
    `/org-units?root_only=true&flat=false${types}`,
  );
}

export interface CMDBAsset {
  id: string;
  name: string;
  assetType: "ON_PREMISES" | "CLOUD_ACCOUNT" | "CLOUD_VM";
  ipAddress?: string;
  accountId?: string;
  cloudProvider?: string;
  region?: string;
  description?: string;
  status: "ACTIVE" | "INACTIVE" | "DECOMMISSIONED";
  applications?: AssetApplicationMapping[];
  mappings?: CMDBComponentMapping[];
  createdAt?: string;
  updatedAt?: string;
}

export interface CMDBComponentMapping {
  id: string;
  assetId: string;
  componentId: string;
  component: {
    id: string;
    title: string;
    componentType: string | null;
  };
  port: number | null;
  protocol: string;
  status: "RUNNING" | "STOPPED" | "UNKNOWN";
  createdAt: string;
  updatedAt: string;
}

export function listOrgUnits(
  orgType?: string,
): Promise<ApiEnvelope<OrgUnit[]>> {
  const qs = orgType ? `?orgType=${orgType}&flat=true` : `?flat=true`;
  return apiFetch<ApiEnvelope<OrgUnit[]>>(`/org-units${qs}`);
}

export function getOrgUnit(id: string): Promise<ApiEnvelope<OrgUnit>> {
  return apiFetch<ApiEnvelope<OrgUnit>>(`/org-units/${id}`);
}

export function createOrgUnit(payload: {
  name: string;
  shortName?: string;
  description?: string;
  parentId?: string;
  orgType: string;
}): Promise<ApiEnvelope<OrgUnit>> {
  return apiFetch<ApiEnvelope<OrgUnit>>(`/org-units`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function updateOrgUnit(
  id: string,
  payload: {
    name?: string;
    shortName?: string;
    description?: string;
    isActive?: boolean;
  },
): Promise<ApiEnvelope<OrgUnit>> {
  return apiFetch<ApiEnvelope<OrgUnit>>(`/org-units/${id}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export function deleteOrgUnit(id: string): Promise<void> {
  return apiFetch<void>(`/org-units/${id}`, { method: "DELETE" });
}

export function listAssets(
  applicationId: string,
): Promise<ApiEnvelope<CMDBAsset[]>> {
  return apiFetch<ApiEnvelope<CMDBAsset[]>>(
    `/cmdb/applications/${applicationId}/assets`,
  );
}

export function getAsset(assetId: string): Promise<ApiEnvelope<CMDBAsset>> {
  return apiFetch<ApiEnvelope<CMDBAsset>>(`/cmdb/assets/${assetId}`);
}

export function createAsset(
  applicationId: string,
  payload: {
    name: string;
    assetType: "ON_PREMISES" | "CLOUD_ACCOUNT" | "CLOUD_VM";
    ipAddress?: string;
    accountId?: string;
    cloudProvider?: string;
    region?: string;
    description?: string;
  },
): Promise<ApiEnvelope<CMDBAsset>> {
  return apiFetch<ApiEnvelope<CMDBAsset>>(
    `/cmdb/applications/${applicationId}/assets`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
  );
}

export function updateAsset(
  assetId: string,
  payload: {
    name?: string;
    description?: string;
    status?: "ACTIVE" | "INACTIVE" | "DECOMMISSIONED";
    ipAddress?: string;
    accountId?: string;
    region?: string;
  },
): Promise<ApiEnvelope<CMDBAsset>> {
  return apiFetch<ApiEnvelope<CMDBAsset>>(`/cmdb/assets/${assetId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export function deleteAsset(assetId: string): Promise<void> {
  return apiFetch<void>(`/cmdb/assets/${assetId}`, { method: "DELETE" });
}

export function listComponentMappings(
  assetId: string,
): Promise<ApiEnvelope<CMDBComponentMapping[]>> {
  return apiFetch<ApiEnvelope<CMDBComponentMapping[]>>(
    `/cmdb/assets/${assetId}/components`,
  );
}

export function createComponentMapping(
  assetId: string,
  payload: {
    componentId: string;
    port?: number;
    protocol?: string;
    status: "RUNNING" | "STOPPED" | "UNKNOWN";
  },
): Promise<ApiEnvelope<CMDBComponentMapping>> {
  return apiFetch<ApiEnvelope<CMDBComponentMapping>>(
    `/cmdb/assets/${assetId}/components`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
  );
}

export function updateComponentMapping(
  mappingId: string,
  payload: {
    port?: number;
    protocol?: string;
    status?: "RUNNING" | "STOPPED" | "UNKNOWN";
  },
): Promise<ApiEnvelope<CMDBComponentMapping>> {
  return apiFetch<ApiEnvelope<CMDBComponentMapping>>(
    `/cmdb/asset-components/${mappingId}`,
    {
      method: "PATCH",
      body: JSON.stringify(payload),
    },
  );
}

export function deleteComponentMapping(mappingId: string): Promise<void> {
  return apiFetch<void>(`/cmdb/asset-components/${mappingId}`, {
    method: "DELETE",
  });
}

export function listComponentsForSearch(
  query?: string,
): Promise<ApiEnvelope<Array<{ id: string; title: string; componentType?: string }>>> {
  return searchComponents({ q: query }).then((res) => ({
    ...res,
    data: (res.data || []).map((comp: any) => ({
      id: comp.id || comp.uuid,
      title: comp.title,
      componentType: comp.componentType,
    })),
  }));
}

export type ApplicationStatus = "OPERATIONAL" | "UNDER_DEVELOPMENT" | "DISPOSITION" | "OTHER";
export type ApplicationCriticality = "CAT-1" | "CAT-2" | "CAT-3" | "CAT-4";

export interface CMDBApplication {
  id: string;
  name: string;
  appCode: string | null;
  description: string | null;
  parentId: string | null;
  path: string | null;
  isActive: boolean;
  organizationId?: string | null;
  organizationName?: string | null;
  divisionId: string | null;
  divisionName: string | null;
  departmentId: string | null;
  departmentName: string | null;
  status: ApplicationStatus;
  criticality: ApplicationCriticality;
  componentCount: number;
  primaryManagerId?: string | null;
  primaryManagerName?: string | null;
  secondaryManagerId?: string | null;
  secondaryManagerName?: string | null;
  ssps?: { sspId: string; title: string }[];
  createdAt: string;
  updatedAt: string;
}

export function listCMDBApplications(): Promise<ApiEnvelope<CMDBApplication[]>> {
  return apiFetch<ApiEnvelope<CMDBApplication[]>>(`/cmdb/applications`);
}

export function getCMDBApplication(id: string): Promise<ApiEnvelope<CMDBApplication>> {
  return apiFetch<ApiEnvelope<CMDBApplication>>(`/cmdb/applications/${id}`);
}

export function createCMDBApplication(payload: {
  name: string;
  appCode?: string | null;
  description?: string | null;
  parentId: string;
  status?: ApplicationStatus;
  criticality?: ApplicationCriticality;
  primaryManagerId?: string | null;
  secondaryManagerId?: string | null;
}): Promise<ApiEnvelope<CMDBApplication>> {
  return apiFetch<ApiEnvelope<CMDBApplication>>(`/cmdb/applications`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function updateCMDBApplication(
  id: string,
  payload: {
    name?: string;
    appCode?: string | null;
    description?: string | null;
    status?: ApplicationStatus;
    criticality?: ApplicationCriticality;
    active?: boolean;
    primaryManagerId?: string | null;
    secondaryManagerId?: string | null;
  }
): Promise<ApiEnvelope<CMDBApplication>> {
  return apiFetch<ApiEnvelope<CMDBApplication>>(`/cmdb/applications/${id}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export function deleteCMDBApplication(id: string): Promise<void> {
  return apiFetch<void>(`/cmdb/applications/${id}`, {
    method: "DELETE",
  });
}

export interface CMDBComponent {
  id: string;
  title: string;
  componentType?: string;
  layerId?: string;
  layer?: { id: string; name?: string; code?: string } | null;
}

export function listCMDBComponents(params?: {
  q?: string;
  type?: "SOFTWARE" | "SERVICE" | "HARDWARE" | "POLICY" | "PROCESS";
}): Promise<ApiEnvelope<CMDBComponent[]>> {
  const qs = new URLSearchParams();
  if (params?.q) qs.set("q", params.q);
  if (params?.type) qs.set("type", params.type);
  const query = qs.toString();
  return apiFetch<ApiEnvelope<CMDBComponent[]>>(
    `/cmdb/components${query ? `?${query}` : ""}`,
  );
}

export interface SSPLayer {
  id: string;
  slug: string;
  displayName: string;
  description?: string;
  displayOrder?: number;
  iconKey?: string;
  active?: boolean;
}

export function listSSPLayers(): Promise<ApiEnvelope<SSPLayer[]>> {
  return apiFetch<ApiEnvelope<SSPLayer[]>>(`/cmdb/layers`);
}

export function listAllAssets(): Promise<ApiEnvelope<CMDBAsset[]>> {
  return apiFetch<ApiEnvelope<CMDBAsset[]>>(`/cmdb/assets`);
}

export function createAssetGlobal(payload: {
  name: string;
  assetType: "ON_PREMISES" | "CLOUD_ACCOUNT" | "CLOUD_VM";
  ipAddress?: string;
  accountId?: string;
  cloudProvider?: string;
  region?: string;
  description?: string;
  status?: "ACTIVE" | "INACTIVE" | "DECOMMISSIONED";
}): Promise<ApiEnvelope<CMDBAsset>> {
  return apiFetch<ApiEnvelope<CMDBAsset>>(`/cmdb/assets`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function listAssetApplicationMappings(
  assetId: string,
): Promise<ApiEnvelope<AssetApplicationMapping[]>> {
  return apiFetch<ApiEnvelope<AssetApplicationMapping[]>>(
    `/cmdb/assets/${assetId}/applications`,
  );
}

export function linkAssetToApplication(
  assetId: string,
  applicationId: string,
): Promise<ApiEnvelope<AssetApplicationMapping>> {
  return apiFetch<ApiEnvelope<AssetApplicationMapping>>(
    `/cmdb/assets/${assetId}/applications`,
    {
      method: "POST",
      body: JSON.stringify({ applicationId }),
    },
  );
}

export function unlinkAssetFromApplication(mappingId: string): Promise<void> {
  return apiFetch<void>(`/cmdb/asset-applications/${mappingId}`, {
    method: "DELETE",
  });
}

export type DeviceType = "SERVER" | "VM" | "FIREWALL" | "SWITCH" | "ROUTER" | "LB" | "CONTAINER" | "STORAGE";
export type DeviceStatus = "ACTIVE" | "INACTIVE" | "MAINTENANCE" | "DECOMMISSIONED";

export interface DeviceResponse {
  id: string;
  orgId: string;
  orgName?: string | null;
  name: string;
  type: DeviceType;
  status: DeviceStatus;
  description: string | null;
  ipAddress: string | null;
  managementIp: string | null;
  virtualIp: string | null;
  hostname: string | null;
  osName: string | null;
  osVersion: string | null;
  cpuCores: number | null;
  ramGb: number | null;
  diskGb: number | null;
  hypervisor: string | null;
  parentCiId: string | null;
  imageName: string | null;
  namespace: string | null;
  registryUrl: string | null;
  totalCapacityTb: number | null;
  usedCapacityTb: number | null;
  environment?: string | null;
  application?: string | null;
  applicationNames?: string[] | null;
  applications?: string[] | DeviceApplicationDetail[] | null;
  vmLastSeenDate?: string | null;
  vmLastFetchDate?: string | null;
  cmLastSeenDate?: string | null;
  cmLastFetchDate?: string | null;
  createdAt: string;
  updatedAt: string;
}

export type CiOnpremResponse = DeviceResponse;

export function listCiDevices(params?: { type?: DeviceType }): Promise<ApiEnvelope<DeviceResponse[]>> {
  const qs = params?.type ? `?type=${params.type}` : "";
  return apiFetch<ApiEnvelope<DeviceResponse[]>>(`/cmdb/devices${qs}`);
}
export const listCiOnprem = listCiDevices;

export interface DeviceFiltersResponse {
  applicationNames: string[];
  deviceTypes: (DeviceType | string)[];
  environmentTypes: string[];
  osTypes: string[];
}

export function getDeviceFilters(params?: {
  orgId?: string | string[];
}): Promise<ApiEnvelope<DeviceFiltersResponse>> {
  let qs = "";
  if (params?.orgId) {
    const orgVal = Array.isArray(params.orgId) ? params.orgId.join(",") : params.orgId;
    if (orgVal) qs = `?orgId=${encodeURIComponent(orgVal)}`;
  }
  return apiFetch<ApiEnvelope<DeviceFiltersResponse>>(`/cmdb/devices/filters${qs}`);
}

export interface DeviceApplicationComponentMapping {
  componentName: string;
  layerSlug?: string;
  port?: number;
  protocol?: string;
  role?: string;
  status?: string;
}

export interface DeviceApplicationMapping {
  applicationName: string;
  components: DeviceApplicationComponentMapping[];
}

export interface DeviceComponentDetail {
  assetId?: string;
  componentId?: string;
  title?: string;
  name?: string;
  componentType?: string;
  sspLayerSlug?: string;
  sspLayerName?: string;
  layerSlug?: string;
  port?: number | null;
  protocol?: string | null;
  role?: string | null;
  status?: string | null;
}

export interface DeviceApplicationDetail {
  id?: string;
  name: string;
  components?: DeviceComponentDetail[];
}

export interface DeviceMutationResponseData {
  device: DeviceResponse;
  applications?: DeviceApplicationDetail[];
}

export function createCiDevice(payload: {
  orgId: string;
  name: string;
  type: DeviceType;
  status?: DeviceStatus;
  description?: string;
  ipAddress?: string;
  managementIp?: string;
  virtualIp?: string;
  hostname?: string;
  osName?: string;
  osVersion?: string;
  cpuCores?: number;
  ramGb?: number;
  diskGb?: number;
  hypervisor?: string;
  parentCiId?: string;
  imageName?: string;
  namespace?: string;
  registryUrl?: string;
  totalCapacityTb?: number;
  usedCapacityTb?: number;
  environment?: string;
  application?: string;
  applicationMappings?: DeviceApplicationMapping[];
}): Promise<ApiEnvelope<DeviceResponse | DeviceMutationResponseData>> {
  return apiFetch<ApiEnvelope<DeviceResponse | DeviceMutationResponseData>>(`/cmdb/devices`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}
export const createCiOnprem = createCiDevice;

export function updateCiDevice(
  id: string,
  payload: Partial<Omit<DeviceResponse, "id" | "createdAt" | "updatedAt">> & {
    applicationMappings?: DeviceApplicationMapping[];
  },
): Promise<ApiEnvelope<DeviceResponse | DeviceMutationResponseData>> {
  return apiFetch<ApiEnvelope<DeviceResponse | DeviceMutationResponseData>>(`/cmdb/devices/${id}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}
export const updateCiOnprem = updateCiDevice;

export function deleteCiDevice(id: string): Promise<void> {
  return apiFetch<void>(`/cmdb/devices/${id}`, { method: "DELETE" });
}
export const deleteCiOnprem = deleteCiDevice;

export interface DeviceImportError {
  row: number;
  field?: string;
  message: string;
}

export interface DeviceImportResult {
  total: number;
  success: number;
  failed: number;
  errors: DeviceImportError[];
}

export function importDevices(file: File, orgId: string): Promise<ApiEnvelope<DeviceImportResult>> {
  const fd = new FormData();
  fd.append("file", file);
  return apiFetch<ApiEnvelope<DeviceImportResult>>(`/cmdb/devices/import?orgId=${encodeURIComponent(orgId)}`, {
    method: "POST",
    headers: {},
    body: fd,
  });
}

export async function downloadDeviceImportTemplate(): Promise<void> {
  const res = await fetch(
    `${BASE_URL}/cmdb/devices/import/template`,
    {
      credentials: "include",
      headers: { ...getAuthHeaders() },
    },
  );
  if (!res.ok) throw new Error(`Failed to download template (${res.status})`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "device-import-template.csv";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export type RelationshipType = "HOSTED_ON" | "CONNECTED_TO" | "BEHIND" | "ATTACHED_TO" | "PROTECTED_BY";

export interface CiRelationshipResponse {
  id: string;
  relationshipType: RelationshipType;
  direction: "OUTGOING" | "INCOMING";
  peerCiId: string;
  peerName: string;
  peerType: DeviceType;
  peerStatus: string;
  peerIpAddress: string | null;
  notes: string | null;
  createdAt: string;
}

export interface DeviceRelationshipGraphResponse {
  deviceId: string;
  deviceName: string;
  deviceType: DeviceType;
  deviceStatus: string;
  hostedOn: CiRelationshipResponse[];
  connectedTo: CiRelationshipResponse[];
  behind: CiRelationshipResponse[];
  attachedTo: CiRelationshipResponse[];
  protectedBy: CiRelationshipResponse[];
  hosts: CiRelationshipResponse[];
  connectedFrom: CiRelationshipResponse[];
  backends: CiRelationshipResponse[];
  attachedFrom: CiRelationshipResponse[];
  protects: CiRelationshipResponse[];
}

export function getDeviceRelationships(ciId: string): Promise<ApiEnvelope<DeviceRelationshipGraphResponse>> {
  return apiFetch<ApiEnvelope<DeviceRelationshipGraphResponse>>(`/cmdb/devices/${ciId}/relationships`);
}

export function addDeviceRelationship(
  ciId: string,
  payload: { targetCiId: string; relationshipType: string; notes?: string },
): Promise<ApiEnvelope<CiRelationshipResponse>> {
  return apiFetch<ApiEnvelope<CiRelationshipResponse>>(`/cmdb/devices/${ciId}/relationships`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function deleteDeviceRelationship(relationshipId: string): Promise<void> {
  return apiFetch<void>(`/cmdb/device-relationships/${relationshipId}`, { method: "DELETE" });
}

export type ServiceStatus = "ACTIVE" | "INACTIVE" | "DEGRADED" | "MAINTENANCE";
export type ServiceDeploymentRole = "PRIMARY" | "BACKUP" | "STANDBY" | "READ_REPLICA" | "DR" | "ACTIVE" | "PASSIVE";
export type AppServiceDependencyType = "REQUIRED" | "OPTIONAL";

export interface ServiceComponentDeployment {
  assetId: string;
  ciId: string;
  ciType: "ON_PREMISES" | "CLOUD" | null;
  ciName: string;
  ciIpAddress: string | null;
  ciIdentifier: string | null;
  ciCloudProviderName: string | null;
  awsResourceName: string | null;
  awsResourceIdentifier: string | null;
  cloudResourceName?: string | null;
  cloudResourceIdentifier?: string | null;
  awsServiceName: string | null;
  port: number | null;
  protocol: string;
  role: string;
  status: string;
  endpointUrl: string | null;
}

export interface ServiceComponentMapping {
  id: string;
  serviceId: string;
  componentId: string;
  component: { id: string; title: string; componentType: string | null };
  notes: string | null;
  createdAt: string;
  deployments: ServiceComponentDeployment[];
  sspLayerSlug?: string | null;
  sspLayerName?: string | null;
}

export interface ServiceResponse {
  id: string;
  orgId: string;
  name: string;
  status: ServiceStatus;
  description: string | null;
  createdAt: string;
  updatedAt: string;
  components: ServiceComponentMapping[];
}

export interface AppServiceDependency {
  id: string;
  applicationId: string;
  serviceId: string;
  service: ServiceResponse;
  dependencyType: AppServiceDependencyType;
  notes: string | null;
  createdAt: string;
}

export function listServices(): Promise<ApiEnvelope<ServiceResponse[]>> {
  return apiFetch<ApiEnvelope<ServiceResponse[]>>(`/cmdb/services`);
}

export function createService(payload: {
  orgId: string;
  name: string;
  status?: ServiceStatus;
  description?: string;
}): Promise<ApiEnvelope<ServiceResponse>> {
  return apiFetch<ApiEnvelope<ServiceResponse>>(`/cmdb/services`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function updateService(
  id: string,
  payload: Partial<Omit<ServiceResponse, "id" | "createdAt" | "updatedAt" | "components">>,
): Promise<ApiEnvelope<ServiceResponse>> {
  return apiFetch<ApiEnvelope<ServiceResponse>>(`/cmdb/services/${id}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export function deleteService(id: string): Promise<void> {
  return apiFetch<void>(`/cmdb/services/${id}`, { method: "DELETE" });
}

export function getServiceById(id: string): Promise<ApiEnvelope<ServiceResponse>> {
  return apiFetch<ApiEnvelope<ServiceResponse>>(`/cmdb/services/${id}`);
}

export function addServiceComponent(
  serviceId: string,
  payload: {
    componentId: string;
    notes?: string;
    layerSlug?: string;
    layerOverrideReason?: string;
  },
): Promise<ApiEnvelope<ServiceComponentMapping>> {
  return apiFetch<ApiEnvelope<ServiceComponentMapping>>(`/cmdb/services/${serviceId}/components`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function removeServiceComponent(mappingId: string): Promise<void> {
  return apiFetch<void>(`/cmdb/service-components/${mappingId}`, { method: "DELETE" });
}

export function linkAssetToService(serviceId: string, assetId: string): Promise<void> {
  return apiFetch<void>(`/cmdb/services/${serviceId}/assets`, {
    method: "POST",
    body: JSON.stringify({ assetId }),
  });
}

export function removeServiceAsset(assetId: string): Promise<void> {
  return apiFetch<void>(`/cmdb/service-assets/${assetId}`, { method: "DELETE" });
}

export function listAppServices(applicationId: string): Promise<ApiEnvelope<AppServiceDependency[]>> {
  return apiFetch<ApiEnvelope<AppServiceDependency[]>>(`/cmdb/applications/${applicationId}/services`);
}

export function linkServiceToApp(
  applicationId: string,
  payload: { serviceId: string; dependencyType: AppServiceDependencyType; notes?: string },
): Promise<ApiEnvelope<AppServiceDependency>> {
  return apiFetch<ApiEnvelope<AppServiceDependency>>(`/cmdb/applications/${applicationId}/services`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function unlinkServiceFromApp(dependencyId: string): Promise<void> {
  return apiFetch<void>(`/cmdb/app-services/${dependencyId}`, { method: "DELETE" });
}

export interface ServiceAsset {
  id: string;
  componentId: string;
  componentTitle: string;
  ciType: "ON_PREMISES" | "CLOUD" | null;
  ciId: string;
  ciName: string;
  ciIdentifier: string | null;
  port: number | null;
  role: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

export interface ServiceAssetWithSelection {
  asset: ServiceAsset;
  isSelected: boolean;
}

export function listServiceAssetsWithSelection(
  applicationId: string,
  serviceId: string,
): Promise<ApiEnvelope<ServiceAssetWithSelection[]>> {
  return apiFetch<ApiEnvelope<ServiceAssetWithSelection[]>>(
    `/cmdb/applications/${applicationId}/services/${serviceId}/assets`,
  );
}

export function saveServiceAssetSelections(
  applicationId: string,
  serviceId: string,
  assetIds: string[],
): Promise<ApiEnvelope<ServiceAssetWithSelection[]>> {
  return apiFetch<ApiEnvelope<ServiceAssetWithSelection[]>>(
    `/cmdb/applications/${applicationId}/services/${serviceId}/asset-selections`,
    { method: "PUT", body: JSON.stringify({ assetIds }) },
  );
}

export interface CloudProviderResponse {
  id: string;
  orgId: string;
  provider: "AWS" | "AZURE" | "GCP" | "OCI";
  accountName: string;
  accountId: string;
  region: string;
  regions?: string[];
  role: "PRODUCTION" | "DEVELOPMENT" | "STAGING" | "DR" | "UAT" | "SANDBOX";
  description: string | null;
  status: "ACTIVE" | "INACTIVE" | "SUSPENDED";
  createdAt: string;
  updatedAt: string;
}

export interface TestConnectionPayload {
  orgId: string;
  provider: string;
  credentialType: "ACCESS_KEY" | "ASSUME_ROLE";
  accessKey?: string;
  secretKey?: string;
  roleArn?: string;
  externalId?: string;
}

export interface TestConnectionResult {
  success: boolean;
  accountId: string;
  message: string;
}

export interface CreateCloudAccountPayload {
  orgId: string;
  provider: string;
  credentialType: "ACCESS_KEY" | "ASSUME_ROLE";
  accessKey?: string;
  secretKey?: string;
  roleArn?: string;
  externalId?: string;
  accountName: string;
  role?: string;
  description?: string;
  status?: string;
}

export interface DiscoveryRunStatus {
  id: string;
  cloudProviderId: string;
  status: "RUNNING" | "SUCCESS" | "FAILED";
  regionsScanned: string[];
  progressPercent: number;
  regionsCompleted: number;
  regionsTotal: number;
  totalDiscovered: number;
  createdCount: number;
  updatedCount: number;
  staleCount: number;
  errorMessage: string | null;
  startedAt: string;
  finishedAt: string | null;
}

export function testCloudConnection(
  payload: TestConnectionPayload
): Promise<ApiEnvelope<TestConnectionResult>> {
  return apiFetch(`/cmdb/cloud/accounts/test-connection`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function createCloudAccount(
  payload: CreateCloudAccountPayload
): Promise<ApiEnvelope<CloudProviderResponse>> {
  return apiFetch(`/cmdb/cloud/accounts`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function discoverCloudAccount(
  cloudProviderId: string
): Promise<ApiEnvelope<{ runId: string; status: string; message: string }>> {
  return apiFetch(`/cmdb/cloud/accounts/${cloudProviderId}/discover`, {
    method: "POST",
  });
}

export function getDiscoveryRun(
  runId: string
): Promise<ApiEnvelope<DiscoveryRunStatus>> {
  return apiFetch(`/cmdb/cloud/discovery-runs/${runId}`);
}

export function listCloudProviders(params?: { applicationId?: string }): Promise<ApiEnvelope<CloudProviderResponse[]>> {
  const qs = params?.applicationId ? `?applicationId=${params.applicationId}` : "";
  return apiFetch<ApiEnvelope<CloudProviderResponse[]>>(`/cmdb/ci/cloud${qs}`);
}

export function getCloudProvider(id: string): Promise<ApiEnvelope<CloudProviderResponse>> {
  return apiFetch<ApiEnvelope<CloudProviderResponse>>(`/cmdb/ci/cloud/${id}`);
}

export function createCloudProvider(payload: {
  orgId: string;
  provider: "AWS" | "AZURE" | "GCP" | "OCI";
  accountName: string;
  accountId: string;
  region: string;
  role?: "PRODUCTION" | "DEVELOPMENT" | "STAGING" | "DR" | "UAT" | "SANDBOX";
  description?: string;
  status?: "ACTIVE" | "INACTIVE" | "SUSPENDED";
}): Promise<ApiEnvelope<CloudProviderResponse>> {
  return apiFetch<ApiEnvelope<CloudProviderResponse>>(`/cmdb/ci/cloud`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function updateCloudProvider(
  id: string,
  payload: Partial<Omit<CloudProviderResponse, "id" | "createdAt" | "updatedAt">>
): Promise<ApiEnvelope<CloudProviderResponse>> {
  return apiFetch<ApiEnvelope<CloudProviderResponse>>(`/cmdb/ci/cloud/${id}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export function deleteCloudProvider(id: string): Promise<void> {
  return apiFetch<void>(`/cmdb/ci/cloud/${id}`, { method: "DELETE" });
}

export type AssetRole = "PRIMARY" | "BACKUP" | "STANDBY" | "READ_REPLICA" | "DR" | "ACTIVE" | "PASSIVE";
export type AssetStatus = "RUNNING" | "STOPPED" | "UNKNOWN" | "STARTING" | "DEGRADED" | "MAINTENANCE";
export type AssetProtocol = "TCP" | "UDP" | "HTTP" | "HTTPS" | "GRPC";

export interface AssetSummaryResponse {
  id: string;
  componentId: string;
  componentTitle: string;
  ciType: "ON_PREMISES" | "CLOUD";
  ciId: string | null;
  ciName: string | null;
  ciIdentifier: string | null;
  port: number | null;
  protocol: AssetProtocol;
  role: AssetRole;
  status: AssetStatus;
  assetName?: string | null;
  platform?: string | null;
  assetType?: string | null;
  hostname?: string | null;
  ip?: string | null;
  os?: string | null;
  environment?: string | null;
  application?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CiOnpremDetail {
  id: string;
  orgId: string;
  name: string;
  type: string;
  hostname: string | null;
  ipAddress: string;
  osName: string | null;
  osVersion: string | null;
  description: string | null;
  status: string;
}

export interface CloudProviderDetail {
  id: string;
  orgId: string;
  provider: string;
  accountName: string;
  accountId: string;
  region: string;
  role: string;
  description: string | null;
  status: string;
}

export interface AwsResourceListResponse {
  id: string;
  cloudProviderId: string;
  resourceId: string;
  resourceName: string | null;
  serviceName: string;
  category: string;
  cloudIdentifier: string;
  awsIdentifier?: string;
  iconKey: string;
  region: string;
  availabilityZone: string | null;
  status: string | null;
  isGlobal: boolean;
  vpcId: string | null;
}

export interface AssetResponse {
  id: string;
  componentId: string;
  component: { id: string; title: string; componentType?: string };
  ciType: "ON_PREMISES" | "CLOUD";
  ciOnpremId?: string;
  ciOnprem?: CiOnpremDetail;
  ciCloudProviderId?: string;
  ciCloud?: CloudProviderDetail;
  cloudResourceId?: string | null;
  awsResourceId?: string | null;
  cloudResource?: {
    id: string;
    resourceId: string;
    resourceName: string | null;
    serviceName: string;
    cloudIdentifier: string;
    awsIdentifier?: string;
    region: string;
    availabilityZone: string | null;
    status: string | null;
  } | null;
  awsResource?: {
    id: string;
    resourceId: string;
    resourceName: string | null;
    serviceName: string;
    awsIdentifier: string;
    region: string;
    availabilityZone: string | null;
    status: string | null;
  } | null;
  port: number | null;
  arn: string | null;
  protocol: AssetProtocol;
  role: AssetRole;
  status: AssetStatus;
  assetName?: string | null;
  platform?: string | null;
  assetType?: string | null;
  hostname?: string | null;
  ip?: string | null;
  os?: string | null;
  environment?: string | null;
  application?: string | null;
  endpointUrl: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export function listAssetsByComponent(componentId?: string): Promise<ApiEnvelope<AssetSummaryResponse[]>> {
  const query = componentId ? `?componentId=${componentId}` : "";
  return apiFetch<ApiEnvelope<AssetSummaryResponse[]>>(`/cmdb/assets${query}`);
}

export function getAssetDetail(assetId: string): Promise<ApiEnvelope<AssetResponse>> {
  return apiFetch<ApiEnvelope<AssetResponse>>(`/cmdb/assets/${assetId}`);
}

export function createAssetDeployment(payload: {
  componentId: string;
  ciType: "ON_PREMISES" | "CLOUD";
  ciOnpremId?: string;
  ciCloudProviderId?: string;
  cloudResourceId?: string;
  awsResourceId?: string;
  port?: number;
  arn?: string;
  protocol?: AssetProtocol;
  role?: AssetRole;
  status?: AssetStatus;
  assetName?: string;
  platform?: string;
  assetType?: string;
  hostname?: string;
  ip?: string;
  os?: string;
  environment?: string;
  application?: string;
  endpointUrl?: string;
  notes?: string;
  applicationId?: string;
}): Promise<ApiEnvelope<AssetResponse>> {
  return apiFetch<ApiEnvelope<AssetResponse>>(`/cmdb/assets`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function updateAssetDeployment(
  assetId: string,
  payload: Partial<Omit<AssetResponse, "id" | "createdAt" | "updatedAt" | "component">>
): Promise<ApiEnvelope<AssetResponse>> {
  return apiFetch<ApiEnvelope<AssetResponse>>(`/cmdb/assets/${assetId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export function deleteAssetDeployment(assetId: string): Promise<void> {
  return apiFetch<void>(`/cmdb/assets/${assetId}`, { method: "DELETE" });
}

export type ComponentSource = "ON_PREM" | "CLOUD" | "SERVICE";

export interface AppComponentDeploymentItem {
  assetId: string;
  ciId: string;
  ciType?: "ON_PREMISES" | "CLOUD" | null;
  ciName: string;
  ciIpAddress: string | null;
  ciIdentifier?: string | null;
  cloudProviderId?: string | null;
  cloudAccountName?: string | null;
  cloudAccountId?: string | null;
  cloudResourceId?: string | null;
  cloudIdentifier?: string | null;
  region?: string | null;
  ciCloudProviderName?: string | null;
  cloudResourceName?: string | null;
  awsResourceName?: string | null;
  cloudResourceIdentifier?: string | null;
  awsResourceIdentifier?: string | null;
  cloudServiceName?: string | null;
  awsServiceName?: string | null;
  port: number | null;
  protocol: string;
  role: string;
  status: string;
  endpointUrl: string | null;
}

export interface AppComponentMappingResponse {
  id: string;
  applicationId: string;
  componentId: string;
  component: { id: string; title: string; componentType: string | null };
  source: ComponentSource;
  ciName: string | null;
  ciAccountId: string | null;
  notes: string | null;
  sspLayerName?: string | null;
  sspLayerSlug?: string | null;
  createdAt: string;
  deployments?: AppComponentDeploymentItem[];
}

export interface UpdateComponentMappingPayload {
  notes?: string | null;
  layerSlug?: string | null;
  layerOverrideReason?: string;
}

export function listAppComponentMappings(
  applicationId: string
): Promise<ApiEnvelope<AppComponentMappingResponse[]>> {
  return apiFetch<ApiEnvelope<AppComponentMappingResponse[]>>(
    `/cmdb/applications/${applicationId}/components`
  );
}

export function createAppComponentMapping(
  applicationId: string,
  payload: {
    componentId: string;
    notes?: string;
    layerSlug?: string;
    layerOverrideReason?: string;
    layerId?: string;
  }
): Promise<ApiEnvelope<AppComponentMappingResponse>> {
  return apiFetch<ApiEnvelope<AppComponentMappingResponse>>(
    `/cmdb/applications/${applicationId}/components`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    }
  );
}

export function deleteAppComponentMapping(mappingId: string): Promise<void> {
  return apiFetch<void>(`/cmdb/app-components/${mappingId}`, { method: "DELETE" });
}

export function updateAppComponentMapping(
  mappingId: string,
  payload: UpdateComponentMappingPayload,
): Promise<ApiEnvelope<AppComponentMappingResponse>> {
  return apiFetch<ApiEnvelope<AppComponentMappingResponse>>(
    `/cmdb/app-components/${mappingId}`,
    { method: "PATCH", body: JSON.stringify(payload) },
  );
}

export function updateServiceComponentMapping(
  mappingId: string,
  payload: UpdateComponentMappingPayload,
): Promise<ApiEnvelope<AppComponentMappingResponse>> {
  return apiFetch<ApiEnvelope<AppComponentMappingResponse>>(
    `/cmdb/service-components/${mappingId}`,
    { method: "PATCH", body: JSON.stringify(payload) },
  );
}

export function addCloudResourceToAppComponent(
  mappingId: string,
  payload: { cloudResourceId: string },
): Promise<ApiEnvelope<AppComponentDeploymentItem>> {
  return apiFetch<ApiEnvelope<AppComponentDeploymentItem>>(
    `/cmdb/app-components/${mappingId}/cloud-resources`,
    { method: "POST", body: JSON.stringify(payload) },
  );
}

export function deleteCloudResourceDeployment(resourceMappingId: string): Promise<void> {
  return apiFetch<void>(`/cmdb/cloud-resource-deployments/${resourceMappingId}`, {
    method: "DELETE",
  });
}

export function linkCloudAccountToApp(
  applicationId: string,
  providerId: string,
): Promise<void> {
  return apiFetch<void>(
    `/cmdb/applications/${applicationId}/cloud-accounts/${providerId}`,
    { method: "PUT" },
  );
}

export function unlinkCloudAccountFromApp(
  applicationId: string,
  providerId: string,
): Promise<void> {
  return apiFetch<void>(
    `/cmdb/applications/${applicationId}/cloud-accounts/${providerId}`,
    { method: "DELETE" },
  );
}

export function unlinkCiOnpremFromApp(
  applicationId: string,
  ciId: string,
): Promise<void> {
  return apiFetch<void>(
    `/cmdb/applications/${applicationId}/devices/${ciId}`,
    { method: "DELETE" },
  );
}

export function listAppDevices(
  applicationId: string,
): Promise<ApiEnvelope<DeviceResponse[]>> {
  return apiFetch<ApiEnvelope<DeviceResponse[]>>(
    `/cmdb/applications/${applicationId}/devices`,
  );
}

export function listCmdbEnvironments(): Promise<ApiEnvelope<string[]>> {
  return apiFetch<ApiEnvelope<string[]>>(`/cmdb/environments`);
}

export type AssetConfigExposure = "Internet" | "Local-network" | "Air-gapped";
export type AssetConfigLevel = "High" | "Basic" | "None";
export type AssetConfigEnvironment = "PRODUCTION" | "STAGING" | "DEVELOPMENT" | "DR";

export interface AssetConfigResponse {
  id: string;
  ciOnpremId?: string;
  deviceName: string;
  type: string | null;
  exposure: AssetConfigExposure | null;
  protectionMeasure: AssetConfigLevel | null;
  detectionMeasure: AssetConfigLevel | null;
  distribution: AssetConfigLevel | null;
  confidentialityRequirement: AssetConfigLevel | null;
  integrityRequirement: AssetConfigLevel | null;
  availabilityRequirement: AssetConfigLevel | null;
  trsScore: number | null;
  assetScore: number | null;
  environment: AssetConfigEnvironment | null;
  createdAt: string;
  updatedAt: string | null;
}

interface AssetConfigEnvelope<T> {
  success: boolean;
  data: T;
}

export function createAssetConfig(payload: {
  ciOnpremId: string;
  applicationId: string;
  type?: string;
  exposure?: AssetConfigExposure;
  protectionMeasure?: AssetConfigLevel;
  detectionMeasure?: AssetConfigLevel;
  distribution?: AssetConfigLevel;
  confidentialityRequirement?: AssetConfigLevel;
  integrityRequirement?: AssetConfigLevel;
  availabilityRequirement?: AssetConfigLevel;
  environment?: AssetConfigEnvironment;
}): Promise<AssetConfigEnvelope<AssetConfigResponse>> {
  return apiFetch<AssetConfigEnvelope<AssetConfigResponse>>(`/cmdb/asset-configs`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function listAssetConfigs(
  ciOnpremId?: string,
): Promise<AssetConfigEnvelope<AssetConfigResponse[]>> {
  const query = ciOnpremId ? `?ciOnpremId=${ciOnpremId}` : "";
  return apiFetch<AssetConfigEnvelope<AssetConfigResponse[]> | AssetConfigResponse[]>(
    `/cmdb/asset-configs${query}`,
  ).then((res) => (Array.isArray(res) ? { success: true, data: res } : res));
}

export function getAssetConfig(
  assetConfigId: string,
): Promise<AssetConfigEnvelope<AssetConfigResponse>> {
  return apiFetch<AssetConfigEnvelope<AssetConfigResponse>>(`/cmdb/asset-configs/${assetConfigId}`);
}

export function updateAssetConfig(
  assetConfigId: string,
  payload: {
    applicationId: string;
    type?: string;
    exposure?: AssetConfigExposure;
    protectionMeasure?: AssetConfigLevel;
    detectionMeasure?: AssetConfigLevel;
    distribution?: AssetConfigLevel;
    confidentialityRequirement?: AssetConfigLevel;
    integrityRequirement?: AssetConfigLevel;
    availabilityRequirement?: AssetConfigLevel;
    environment?: AssetConfigEnvironment;
  },
): Promise<AssetConfigEnvelope<AssetConfigResponse>> {
  return apiFetch<AssetConfigEnvelope<AssetConfigResponse>>(`/cmdb/asset-configs/${assetConfigId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export function deleteAssetConfig(assetConfigId: string): Promise<AssetConfigEnvelope<null>> {
  return apiFetch<AssetConfigEnvelope<null>>(`/cmdb/asset-configs/${assetConfigId}`, {
    method: "DELETE",
  });
}

export interface AssetApplicationMappingNewResponse {
  id: string;
  applicationId: string;
  assetId: string;
  asset: AssetSummaryResponse;
  ciName?: string | null;
  notes: string | null;
  createdAt: string;
}

export function listAppAssetMappings(
  applicationId: string
): Promise<ApiEnvelope<AssetApplicationMappingNewResponse[]>> {
  return apiFetch<ApiEnvelope<AssetApplicationMappingNewResponse[]>>(
    `/cmdb/applications/${applicationId}/assets`
  );
}

export function createAppAssetMapping(
  applicationId: string,
  payload: { assetId: string; notes?: string }
): Promise<ApiEnvelope<AssetApplicationMappingNewResponse>> {
  return apiFetch<ApiEnvelope<AssetApplicationMappingNewResponse>>(
    `/cmdb/applications/${applicationId}/assets`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    }
  );
}

export function deleteAppAssetMapping(mappingId: string): Promise<void> {
  return apiFetch<void>(`/cmdb/app-assets/${mappingId}`, { method: "DELETE" });
}

export interface AwsServiceCatalog {
  id: number;
  serviceName: string;
  category: string;
  description: string | null;
  cloudIdentifier: string | null;
  awsIdentifier?: string | null;
  iconKey: string;
  isGlobal: boolean;
}

export interface AwsResourceSummary {
  id: string;
  cloudProviderId: string;
  cloudServiceId: string;
  serviceName: string | null;
  category: string | null;
  iconKey: string | null;
  cloudIdentifier: string | null;
  vpcId: string | null;
  resourceId: string;
  resourceName: string | null;
  region: string;
  availabilityZone: string | null;
  status: string | null;
  isGlobal: boolean;
  tags: Record<string, string> | null;
  lastSyncedAt: string | null;
  createdAt: string;
}

export interface AwsResource extends AwsResourceSummary {
  accountId: string | null;
  accountName: string | null;
  provider: string | null;
  tags: Record<string, string> | null;
  rawConfig: Record<string, unknown> | null;
  updatedAt: string;
}

export interface AwsServiceGroup {
  cloudServiceId: string;
  serviceName: string | null;
  category: string | null;
  iconKey: string | null;
  cloudIdentifier: string | null;
  resourceCount: number;
  resources: AwsResourceSummary[];
}

export interface AwsVpcGroup {
  vpcId: string | null;
  vpcName: string | null;
  region: string;
  totalResources: number;
  serviceGroups: AwsServiceGroup[];
}

export interface AwsAccountSummary {
  cloudProviderId: string;
  accountId: string;
  accountName: string;
  provider: string;
  totalResources: number;
  vpcs: AwsVpcGroup[];
  globalServices: AwsVpcGroup;
}

export function listAwsServices(): Promise<ApiEnvelope<AwsServiceCatalog[]>> {
  return apiFetch<ApiEnvelope<AwsServiceCatalog[]>>(`/cmdb/cloud/services`);
}

export function getAwsAccountSummary(
  cloudProviderId: string
): Promise<ApiEnvelope<AwsAccountSummary>> {
  return apiFetch<ApiEnvelope<AwsAccountSummary>>(
    `/cmdb/cloud/accounts/${cloudProviderId}/summary`
  );
}

export function listAwsResources(params: {
  cloudProviderId: string;
  vpcId?: string;
  serviceId?: string;
}): Promise<ApiEnvelope<AwsResourceSummary[]>> {
  const qs = new URLSearchParams({ cloudProviderId: params.cloudProviderId });
  if (params.vpcId) qs.set("vpcId", params.vpcId);
  if (params.serviceId) qs.set("serviceId", params.serviceId);
  return apiFetch<ApiEnvelope<AwsResourceSummary[]>>(`/cmdb/cloud/resources?${qs}`);
}

export function getAwsResource(resourceId: string): Promise<ApiEnvelope<AwsResource>> {
  return apiFetch<ApiEnvelope<AwsResource>>(`/cmdb/cloud/resources/${resourceId}`);
}

export interface TopologyResource {
  id: string;
  resourceId: string;
  resourceName: string;
  serviceName: string;
  iconKey: string;
  vpcId: string | null;
  region: string;
  availabilityZone: string | null;
  status: string;
  isGlobal: boolean;
  lastSyncedAt: string;
}

export interface ServiceTopologyGroup {
  cloudServiceId: string;
  serviceName: string;
  category: string;
  iconKey: string;
  resourceCount: number;
  resources: TopologyResource[];
}

export interface VpcTopologyGroup {
  vpcId: string | null;
  vpcName: string;
  region: string;
  totalResources: number;
  serviceGroups: ServiceTopologyGroup[];
}

export interface CloudTopologyResponse {
  cloudProviderId: string;
  accountId: string;
  accountName: string;
  provider: string;
  region: string;
  totalResources: number;
  vpcs: VpcTopologyGroup[];
  globalServices: VpcTopologyGroup;
}

export function getCloudTopology(providerId: string): Promise<ApiEnvelope<CloudTopologyResponse>> {
  return apiFetch<ApiEnvelope<CloudTopologyResponse>>(`/cmdb/ci/cloud/${providerId}/topology`);
}

export interface AwsServiceTypeResponse {
  id: string;
  serviceName: string;
  category: string;
  cloudIdentifier: string;
  awsIdentifier?: string;
  iconKey: string;
}

export function listCloudProviderResourceTypes(
  providerId: string
): Promise<ApiEnvelope<AwsServiceTypeResponse[]>> {
  return apiFetch<ApiEnvelope<AwsServiceTypeResponse[]>>(
    `/cmdb/ci/cloud/${providerId}/resource-types`
  );
}

export function listCloudProviderResources(
  providerId: string,
  params?: { cloudIdentifier?: string; q?: string }
): Promise<ApiEnvelope<AwsResourceListResponse[]>> {
  const qs = new URLSearchParams();
  if (params?.cloudIdentifier) qs.set("cloudIdentifier", params.cloudIdentifier);
  if (params?.q) qs.set("q", params.q);
  const query = qs.toString() ? `?${qs}` : "";
  return apiFetch<ApiEnvelope<AwsResourceListResponse[]>>(`/cmdb/ci/cloud/${providerId}/resources${query}`);
}

export function searchCloudProviderResources(
  providerId: string,
  page: number,
  size: number,
  payload: ListSearchPayload,
): Promise<PagedSearchResult<AwsResourceListResponse>> {
  return postPagedSearch<AwsResourceListResponse>(
    `/cmdb/ci/cloud/${providerId}/resources`,
    page,
    size,
    payload,
  );
}

export function deleteAwsResource(resourceId: string): Promise<void> {
  return apiFetch<void>(`/cmdb/cloud/resources/${resourceId}`, { method: "DELETE" });
}

export type MappingStatus = "UNMAPPED" | "PENDING" | "CONFIRMED" | "IGNORED";
export type MappingType = "AUTO" | "MANUAL";

export interface AwsServiceMappingOverviewItem {
  cloudServiceId: string;
  awsServiceId?: string;
  serviceName: string;
  category: string;
  iconKey: string | null;
  resourceCount: number;
  mappingId: string | null;
  componentId: string | null;
  componentTitle: string | null;
  mappingType: MappingType | null;
  status: MappingStatus;
}

export interface AwsAppServiceMappingResponse {
  id: string;
  applicationId: string;
  applicationName: string | null;
  cloudProviderId: string;
  accountName: string | null;
  accountId: string | null;
  cloudServiceId: string;
  awsServiceId?: string;
  serviceName: string | null;
  category: string | null;
  iconKey: string | null;
  componentId: string | null;
  componentTitle: string | null;
  mappingType: MappingType;
  status: MappingStatus;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AwsAppServiceMappingScanResult {
  scanned: number;
  autoMatched: number;
  alreadyMapped: number;
  unmatched: number;
}

export interface AwsResourceMappingResponse {
  mappingId: string | null;
  resourceId: string;
  resourceName: string | null;
  resourceIdentifier: string | null;
  region: string | null;
  availabilityZone: string | null;
  status: string | null;
  isGlobal: boolean;
  componentId: string | null;
  componentTitle: string | null;
  isExcluded: boolean;
  source: "AUTO" | "MANUAL" | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface AwsResourceMappingUpdateRequest {
  componentId: string | null;
  isExcluded: boolean | null;
  notes: string | null;
  source?: "AUTO" | "MANUAL" | null;
}

export interface AwsServiceHierarchyItem {
  cloudServiceId: string;
  awsServiceId?: string;
  serviceName: string;
  category: string;
  iconKey: string | null;
  isGlobal: boolean;
  mappingId: string | null;
  componentId: string | null;
  componentTitle: string | null;
  mappingType: MappingType | null;
  status: "CONFIRMED" | null;
  notes: string | null;
  resources: AwsResourceMappingResponse[];
}

export function getAwsMappingHierarchy(
  applicationId: string,
  cloudProviderId: string,
): Promise<ApiEnvelope<AwsServiceHierarchyItem[]>> {
  return apiFetch<ApiEnvelope<AwsServiceHierarchyItem[]>>(
    `/cmdb/cloud/app-mappings/hierarchy?applicationId=${applicationId}&cloudProviderId=${cloudProviderId}`,
  );
}

export function getAwsMappingOverview(
  applicationId: string,
  cloudProviderId: string,
): Promise<ApiEnvelope<AwsServiceMappingOverviewItem[]>> {
  return apiFetch<ApiEnvelope<AwsServiceMappingOverviewItem[]>>(
    `/cmdb/cloud/app-mappings/overview?applicationId=${applicationId}&cloudProviderId=${cloudProviderId}`,
  );
}

export function scanAwsMappings(
  applicationId: string,
  cloudProviderId: string,
): Promise<ApiEnvelope<AwsAppServiceMappingScanResult>> {
  return apiFetch<ApiEnvelope<AwsAppServiceMappingScanResult>>(`/cmdb/cloud/app-mappings/scan`, {
    method: "POST",
    body: JSON.stringify({ applicationId, cloudProviderId }),
  });
}

export interface BuildAndMapResult {
  scanned: number;
  autoMatched: number;
  alreadyMapped: number;
  unmatched: number;
}

export function buildAndMapComponents(
  applicationId: string,
  cloudProviderId: string,
): Promise<ApiEnvelope<BuildAndMapResult>> {
  return apiFetch<ApiEnvelope<BuildAndMapResult>>(`/cmdb/cloud/app-mappings/build-and-map`, {
    method: "POST",
    body: JSON.stringify({ applicationId, cloudProviderId }),
  });
}

export function createAwsMapping(payload: {
  applicationId: string;
  cloudProviderId: string;
  cloudServiceId: string;
  componentId: string | null;
  notes: string | null;
  source?: "AUTO" | "MANUAL" | null;
}): Promise<ApiEnvelope<AwsAppServiceMappingResponse>> {
  return apiFetch<ApiEnvelope<AwsAppServiceMappingResponse>>(`/cmdb/cloud/app-mappings`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function updateAwsMapping(
  mappingId: string,
  payload: { componentId: string | null; notes: string | null; source?: "AUTO" | "MANUAL" | null },
): Promise<ApiEnvelope<AwsAppServiceMappingResponse>> {
  return apiFetch<ApiEnvelope<AwsAppServiceMappingResponse>>(`/cmdb/cloud/app-mappings/${mappingId}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export function confirmAwsMapping(mappingId: string): Promise<ApiEnvelope<AwsAppServiceMappingResponse>> {
  return apiFetch<ApiEnvelope<AwsAppServiceMappingResponse>>(`/cmdb/cloud/app-mappings/${mappingId}/confirm`, {
    method: "POST",
  });
}

export function ignoreAwsMapping(mappingId: string): Promise<ApiEnvelope<AwsAppServiceMappingResponse>> {
  return apiFetch<ApiEnvelope<AwsAppServiceMappingResponse>>(`/cmdb/cloud/app-mappings/${mappingId}/ignore`, {
    method: "POST",
  });
}

export function deleteAwsMapping(mappingId: string): Promise<void> {
  return apiFetch<void>(`/cmdb/cloud/app-mappings/${mappingId}`, { method: "DELETE" });
}

export function updateAwsResourceMapping(
  serviceMappingId: string,
  resourceMappingId: string,
  payload: AwsResourceMappingUpdateRequest,
): Promise<ApiEnvelope<AwsResourceMappingResponse>> {
  return apiFetch<ApiEnvelope<AwsResourceMappingResponse>>(
    `/cmdb/cloud/app-mappings/${serviceMappingId}/resources/${resourceMappingId}`,
    { method: "PUT", body: JSON.stringify(payload) },
  );
}

export interface AwsResourceMappingBulkUpdateRequest {
  componentId: string;
  selectedResourceIds: string[];
  unselectedResourceIds?: string[];
  replace?: boolean;
}

export function bulkUpdateAwsResourceMappings(
  serviceMappingId: string,
  payload: AwsResourceMappingBulkUpdateRequest,
): Promise<ApiEnvelope<AwsResourceMappingResponse[]>> {
  return apiFetch<ApiEnvelope<AwsResourceMappingResponse[]>>(
    `/cmdb/cloud/app-mappings/${serviceMappingId}/resources/bulk`,
    { method: "PUT", body: JSON.stringify(payload) },
  );
}

export interface AppTopologyDeployment {
  assetId: string;
  ciType: "ON_PREMISES" | "CLOUD";
  ciName?: string;
  ciIpAddress?: string;
  port?: number;
  role?: string;
  status?: string;
  cloudResourceName?: string;
  cloudIdentifier?: string;
}

export interface AppTopologyComponent {
  mappingId: string;
  componentId: string;
  componentTitle: string;
  componentType: string;
  deployments: AppTopologyDeployment[];
}

export interface AppTopologyServiceDep {
  dependencyId: string;
  serviceId: string;
  serviceName: string;
  serviceStatus: string;
  dependencyType: string;
  components: AppTopologyComponent[];
}

export interface AppTopologyDirectResource {
  mappingId: string;
  cloudResourceId: string;
  resourceId: string;
  resourceName: string;
  cloudIdentifier: string;
  serviceName: string;
  iconKey: string;
  category: string;
  region: string;
  status: string;
  provider: string;
  accountName: string;
  notes: string | null;
}

export interface AppTopologyCloudAccount {
  cloudProviderId: string;
  accountId: string;
  accountName: string;
  provider: string;
  region: string;
  resourceCount: number;
  directResources: AppTopologyDirectResource[];
}

export interface AppTopologyResponse {
  applicationId: string;
  name: string;
  description?: string;
  appStatus: string;
  criticality: string;
  components: AppTopologyComponent[];
  serviceDependencies: AppTopologyServiceDep[];
  cloudAccounts: AppTopologyCloudAccount[];
  totalComponents: number;
  totalDeployments: number;
  totalCloudAccounts: number;
  totalDirectResources?: number;
}

export function getApplicationTopology(appId: string): Promise<ApiEnvelope<AppTopologyResponse>> {
  return apiFetch<ApiEnvelope<AppTopologyResponse>>(`/cmdb/applications/${appId}/topology`);
}

export function listAppResourceTypes(appId: string): Promise<ApiEnvelope<AwsServiceTypeResponse[]>> {
  return apiFetch<ApiEnvelope<AwsServiceTypeResponse[]>>(`/cmdb/applications/${appId}/resource-types`);
}

export function listAppResourcePicker(
  appId: string,
  params?: { cloudIdentifier?: string; q?: string },
): Promise<ApiEnvelope<AwsResourceListResponse[]>> {
  const qs = new URLSearchParams();
  if (params?.cloudIdentifier) qs.set("cloudIdentifier", params.cloudIdentifier);
  if (params?.q) qs.set("q", params.q);
  const query = qs.toString() ? `?${qs}` : "";
  return apiFetch<ApiEnvelope<AwsResourceListResponse[]>>(`/cmdb/applications/${appId}/resource-picker${query}`);
}

export interface DriftOpaDetail {
  opaControlId: string;
  status: string;
  assetUuid: string;
  assetName: string;
  scanUuid?: string;
  componentName?: string;
  evidence?: string;
  controlTitle?: string;
  controlStatement?: string;
}

export interface DriftHistoryPoint {
  timestamp: string;
  status: 'passed' | 'failed' | 'unknown' | 'not_applicable' | (string & {});
  scanUuid?: string;
  opaDetails?: DriftOpaDetail[];
}

export interface DriftApiControl {
  controlId: string;
  controlTitle?: string;
  controlStatement?: string;
  parts?: any[];
  currentstatus: 'passed' | 'failed' | 'unknown' | 'not_applicable';
  currentStatus?: 'passed' | 'failed' | 'unknown' | 'not_applicable';
  driftCount: number;
  longestFailedStreak: number;
  worstStreak: number;
  totalScans: number;
  passedScans: number;
  failedScans: number;
  unknownScans: number;
  history: DriftHistoryPoint[];
}

export interface DriftSummary {
  totalControls: number;
  passedControls: number;
  failedControls: number;
  totalDrifts: number;
}

export interface DriftResponse {
  planId: string;
  summary: DriftSummary;
  totalControls?: number;
  totalElements?: number;
  totalPages?: number;
  page?: number;
  size?: number;
  controls: DriftApiControl[];
}

interface DriftWireEvidence {
  command?: string;
  data?: string;
}

interface DriftWireOpaDetail {
  opaControlId: string;
  status: string;
  assetUuid: string;
  asset_uuid?: string;
  assetId?: string;
  asset_id?: string;
  assetName: string;
  scanUuid?: string;
  scan_uuid?: string;
  scanId?: string;
  scan_id?: string;
  componentName?: string | null;
  component_name?: string | null;
  componentTitle?: string | null;
  component_title?: string | null;
  evidence?: DriftWireEvidence[] | string | null;
}

interface DriftWireHistoryPoint {
  timestamp: string;
  status: 'passed' | 'failed' | 'unknown' | 'not_applicable';
  scanUuid?: string;
  scan_uuid?: string;
  scanId?: string;
  scan_id?: string;
  opaDetails?: DriftWireOpaDetail[];
}

interface DriftWireControl {
  controlId: string;
  controlTitle?: string;
  controlStatement?: string;
  parts?: any[];
  currentstatus?: 'passed' | 'failed' | 'unknown' | 'not_applicable';
  currentStatus?: 'passed' | 'failed' | 'unknown' | 'not_applicable';
  driftCount: number;
  longestFailedStreak: number;
  worstStreak: number;
  totalScans?: number;
  passedScans?: number;
  failedScans?: number;
  unknownScans?: number;
  history?: DriftWireHistoryPoint[];
}

interface DriftWireResponse {
  totalControls?: number;
  totalElements?: number;
  totalPages?: number;
  page?: number;
  size?: number;
  planId: string;
  summary: DriftSummary;
  controls?: DriftWireControl[];
}

function normalizeDriftEvidence(
  ev: DriftWireEvidence[] | string | null | undefined,
): string | undefined {
  if (ev == null) return undefined;
  if (typeof ev === 'string') return ev.trim() ? ev : undefined;
  if (Array.isArray(ev)) return ev.length ? JSON.stringify(ev, null, 2) : undefined;
  return undefined;
}

function inferDriftComponentName(controlId?: string): string | undefined {
  const prefix = controlId?.split("-")[0]?.toUpperCase();
  if (prefix === "LIN") return "Linux";
  if (prefix === "OCI") return "OCI";
  return prefix || undefined;
}

function deriveScanCounts(c: DriftWireControl): {
  totalScans: number;
  passedScans: number;
  failedScans: number;
  unknownScans: number;
} {
  if (typeof c.totalScans === 'number') {
    return {
      totalScans: c.totalScans,
      passedScans: c.passedScans ?? 0,
      failedScans: c.failedScans ?? 0,
      unknownScans: c.unknownScans ?? 0,
    };
  }
  const history = c.history ?? [];
  let passedScans = 0;
  let failedScans = 0;
  let unknownScans = 0;
  for (const h of history) {
    const s = (h.status ?? '').toLowerCase().trim().replace(/[\s-]+/g, '_');
    if (s === 'passed' || s === 'satisfied' || s === 'pass' || s === 'not_applicable' || s === 'notapplicable' || s === 'na' || s === 'not_appliable') passedScans++;
    else if (
      s === 'failed' ||
      s === 'fail' ||
      s === 'not_satisfied' ||
      s === 'notsatisfied' ||
      s === 'not_passed' ||
      s === 'unsatisfied' ||
      s === 'non_compliant'
    ) failedScans++;
    else unknownScans++;
  }
  return { totalScans: history.length, passedScans, failedScans, unknownScans };
}

function normalizeDriftResponse(wire: DriftWireResponse): DriftResponse {
  const anyWire = wire as any;
  const pagination = anyWire?.pagination;
  const totalElements =
    anyWire?.totalElements ??
    anyWire?.totalItems ??
    anyWire?.totalRecords ??
    anyWire?.totalCount ??
    anyWire?.total ??
    pagination?.totalCount ??
    pagination?.totalElements ??
    pagination?.total ??
    wire.summary?.totalControls;
  const totalPages = anyWire?.totalPages ?? pagination?.totalPages;
  const page = anyWire?.page ?? anyWire?.pageNumber ?? pagination?.pageNumber ?? pagination?.page;
  const size = anyWire?.size ?? anyWire?.pageSize ?? pagination?.pageSize;
  return {
    planId: wire.planId,
    summary: wire.summary,
    totalControls: typeof totalElements === 'number' ? totalElements : wire.summary?.totalControls,
    totalElements: typeof totalElements === 'number' ? totalElements : wire.summary?.totalControls,
    totalPages,
    page,
    size,
    controls: (wire.controls ?? []).map((c) => {
      const normalizedHistory: DriftHistoryPoint[] = (c.history ?? []).flatMap((h) => {
        const pointScanUuid =
          h.scanUuid ??
          h.scan_uuid ??
          (h as any).scanId ??
          (h as any).scan_id;

        const details: DriftOpaDetail[] = (h.opaDetails ?? []).map((od) => {
          const detailScanUuid =
            od.scanUuid ??
            od.scan_uuid ??
            (od as any).scanId ??
            (od as any).scan_id ??
            pointScanUuid;
          return {
            opaControlId: od.opaControlId,
            status: od.status,
            assetUuid:
              od.assetUuid ??
              (od as any).asset_uuid ??
              (od as any).assetId ??
              (od as any).asset_id,
            assetName: od.assetName,
            scanUuid: detailScanUuid,
            componentName:
              od.componentName ??
              od.component_name ??
              od.componentTitle ??
              od.component_title ??
              inferDriftComponentName(od.opaControlId),
            evidence: normalizeDriftEvidence(od.evidence),
          };
        });

        if (details.length > 0) {
          const scanUuidGroups = new Map<string, DriftOpaDetail[]>();
          for (const d of details) {
            const groupKey = d.scanUuid || pointScanUuid || normalizeDriftDetailStatus(d.status);
            if (!scanUuidGroups.has(groupKey)) {
              scanUuidGroups.set(groupKey, []);
            }
            scanUuidGroups.get(groupKey)!.push(d);
          }

          if (scanUuidGroups.size > 1) {
            return Array.from(scanUuidGroups.entries()).map(([groupKey, groupDetails]) => {
              const hasFail = groupDetails.some((d) => normalizeDriftDetailStatus(d.status) === 'failed');
              const hasPass = groupDetails.some((d) => normalizeDriftDetailStatus(d.status) === 'passed');
              const groupVerdict: 'passed' | 'failed' | 'not_applicable' = hasFail
                ? 'failed'
                : hasPass
                  ? 'passed'
                  : 'not_applicable';

              const actualScanUuid = groupDetails[0]?.scanUuid || (groupKey.includes('-') ? groupKey : pointScanUuid);
              return {
                timestamp: h.timestamp,
                status: groupVerdict,
                scanUuid: actualScanUuid,
                opaDetails: groupDetails,
              };
            });
          }
        }

        return [
          {
            timestamp: h.timestamp,
            status: h.status,
            scanUuid: pointScanUuid,
            opaDetails: details,
          },
        ];
      });

      return {
        controlId: c.controlId,
        controlTitle: c.controlTitle,
        controlStatement: c.controlStatement,
        parts: c.parts,
        currentstatus: c.currentstatus ?? c.currentStatus ?? 'unknown',
        currentStatus: c.currentStatus ?? c.currentstatus ?? 'unknown',
        driftCount: c.driftCount,
        longestFailedStreak: c.longestFailedStreak,
        worstStreak: c.worstStreak,
        ...(typeof c.totalScans === 'number'
          ? {
            totalScans: c.totalScans,
            passedScans: c.passedScans ?? 0,
            failedScans: c.failedScans ?? 0,
            unknownScans: c.unknownScans ?? 0,
          }
          : deriveScanCountsFromHistory(normalizedHistory)),
        history: normalizedHistory,
      };
    }),
  };
}

export async function postDrift(payload: {
  planId: string;
  startDate: string;
  endDate: string;
  page?: number;
  size?: number;
}): Promise<ApiEnvelope<DriftResponse>> {
  const page = payload.page ?? 0;
  const size = payload.size ?? 12;
  const bodyPayload: Record<string, any> = {
    planId: payload.planId,
    startDate: payload.startDate,
    endDate: payload.endDate,
  };
  const res = await apiFetch<ApiEnvelope<DriftWireResponse> | DriftWireResponse>(
    `/drift/${page}/${size}`,
    {
      method: 'POST',
      body: JSON.stringify(bodyPayload),
    },
  );
  const wire = (res as ApiEnvelope<DriftWireResponse>)?.data ?? (res as DriftWireResponse);
  const data = normalizeDriftResponse(wire);
  return { ...(res as ApiEnvelope<DriftWireResponse>), data } as ApiEnvelope<DriftResponse>;
}

export async function fetchAssessmentPlansBySsp(
  sspId: string,
): Promise<ApiAssessmentPlan[]> {
  const res = await apiFetch<{ status: string; data: ApiAssessmentPlan[] }>(
    `/assessment-plans/by-ssp/${sspId}`,
  );
  return res?.data ?? [];
}

export async function postDriftByPlan(payload: {
  planId: string;
  startDate: string;
  endDate: string;
  page?: number;
  size?: number;
}): Promise<ApiEnvelope<DriftResponse>> {
  const page = payload.page ?? 0;
  const size = payload.size ?? 12;
  const bodyPayload: Record<string, any> = {
    planId: payload.planId,
    startDate: payload.startDate,
    endDate: payload.endDate,
  };
  const res = await apiFetch<ApiEnvelope<DriftWireResponse> | DriftWireResponse>(
    `/drift/plan/${page}/${size}`,
    {
      method: 'POST',
      body: JSON.stringify(bodyPayload),
    },
  );
  const wire = (res as ApiEnvelope<DriftWireResponse>)?.data ?? (res as DriftWireResponse);
  const data = normalizeDriftResponse(wire);
  return { ...(res as ApiEnvelope<DriftWireResponse>), data } as ApiEnvelope<DriftResponse>;
}

export async function postDriftBySsp(
  sspId: string,
  params?: {
    startDate?: string;
    endDate?: string;
    page?: number;
    size?: number;
  },
): Promise<ApiEnvelope<DriftResponse>> {
  const page = params?.page ?? 0;
  const size = params?.size ?? 12;
  const bodyPayload: Record<string, any> = {};
  if (params?.startDate) bodyPayload.startDate = params.startDate;
  if (params?.endDate) bodyPayload.endDate = params.endDate;
  const hasBody = Object.keys(bodyPayload).length > 0;
  const res = await apiFetch<ApiEnvelope<DriftWireResponse> | DriftWireResponse>(
    `/drift/ssp/${encodeURIComponent(sspId)}/${page}/${size}`,
    {
      method: 'POST',
      ...(hasBody ? { body: JSON.stringify(bodyPayload) } : {}),
    },
  );
  const wire = (res as ApiEnvelope<DriftWireResponse>)?.data ?? (res as DriftWireResponse);
  const data = normalizeDriftResponse(wire);
  return { ...(res as ApiEnvelope<DriftWireResponse>), data } as ApiEnvelope<DriftResponse>;
}

export interface DriftEvidenceQuery {
  assetId: string;
  timestamp: string;
  controlIds: string[];
  scanUuid?: string;
}

interface DriftEvidenceWireRow {
  controlId: string;
  status?: string;
  componentName?: string | null;
  component_name?: string | null;
  componentTitle?: string | null;
  component_title?: string | null;
  evidence?: { command?: string; data?: string } | null;
  controlTitle?: string;
  controlStatement?: string;
}
interface DriftEvidenceWireResponse {
  assetId: string;
  timestamp: string;
  scanUuid?: string;
  scan_uuid?: string;
  evidences?: DriftEvidenceWireRow[];
}

const driftEvidenceCache = new Map<string, DriftOpaDetail[]>();

export async function getDriftEvidence(
  q: DriftEvidenceQuery,
  assetName?: string,
): Promise<DriftOpaDetail[]> {
  const cacheKey = `${q.assetId}|${q.timestamp}|${q.scanUuid ?? ''}|${[...q.controlIds]
    .sort()
    .join(',')}`;
  const cached = driftEvidenceCache.get(cacheKey);
  if (cached) return cached;

  const payload: Record<string, any> = {
    assetId: q.assetId,
    timestamp: q.timestamp,
    controlIds: q.controlIds,
  };
  if (q.scanUuid) {
    payload.scanUuid = q.scanUuid;
  }

  const res = await apiFetch<
    ApiEnvelope<DriftEvidenceWireResponse> | DriftEvidenceWireResponse
  >('/drift/evidence', {
    method: 'POST',
    body: JSON.stringify(payload),
  });

  const body =
    (res as ApiEnvelope<DriftEvidenceWireResponse>)?.data ??
    (res as DriftEvidenceWireResponse);
  const rows = body?.evidences ?? [];

  const mapped: DriftOpaDetail[] = rows.map((r) => ({
    opaControlId: r.controlId,
    status: r.status ?? '',
    assetUuid: body?.assetId ?? q.assetId,
    assetName: assetName || body?.assetId || q.assetId,
    scanUuid: (body as any)?.scanUuid ?? (body as any)?.scan_uuid ?? q.scanUuid,
    componentName:
      r.componentName ??
      r.component_name ??
      r.componentTitle ??
      r.component_title ??
      inferDriftComponentName(r.controlId) ??
      inferDriftComponentName(q.controlIds[0]),
    evidence: r.evidence
      ? JSON.stringify([
        { command: r.evidence.command ?? '', data: r.evidence.data ?? '' },
      ])
      : undefined,
    controlTitle: r.controlTitle,
    controlStatement: r.controlStatement,
  }));

  driftEvidenceCache.set(cacheKey, mapped);
  return mapped;
}

export interface Permission {
  code: string;
  module: string;
  action: string;
  displayName: string;
}

export function fetchUserPermissions(): Promise<ApiEnvelope<Permission[]>> {
  return apiFetch<ApiEnvelope<Permission[]>>('/users/current/permissions', {
    method: 'GET',
  });
}

export type RuleSeverity = "ERROR" | "WARN";

export interface ValidationRule {
  id: string;
  name: string;
  validator: string;
  target: string;
  params: Record<string, unknown>;
  severity: RuleSeverity;
  applicable_doc_types: string[];
  remediation: string;
  intent: string | null;
  expected_evidence: string | null;
  enabled: boolean;
}

export type ValidationStatus = "PASS" | "FAIL" | "WARN" | "SKIPPED";
export type SkipReason = "NO_INPUT" | "NOT_APPLICABLE" | "NO_DATA" | null;

export interface DocValidationResult {
  ruleId: string;
  name: string;
  validator: string;
  severity: RuleSeverity;
  status: ValidationStatus;
  evidence: string;
  remediation: string;
  skipReason: SkipReason;
}

export interface DocValidationResponse {
  filename: string;
  tenant_id: string;
  doc_type: string;
  results: DocValidationResult[];
  summary: {
    pass: number;
    fail: number;
    warn: number;
    skipped: number;
    total: number;
  };
  duration_ms: number;
}

export interface DocValidationMeta {
  brand: string;
  title: string;
  version: string;
  owner_role?: string;
  approver?: string;
  section_number?: string;
  classification?: string;
  doc_type: string;
}

export function getValidationRules(): Promise<ValidationRule[]> {
  return apiFetch<ValidationRule[]>('/content-registry/validation/rules');
}

export function validateDocument(
  file: File,
  meta: DocValidationMeta,
): Promise<DocValidationResponse> {
  const formData = new FormData();
  formData.append('file', file);
  Object.entries(meta).forEach(([k, v]) => {
    if (v !== undefined && v !== '') formData.append(k, v);
  });
  return apiFetch<DocValidationResponse>('/content-registry/validation/validate', {
    method: 'POST',
    headers: {},
    body: formData,
  });
}

export interface ValidateDocControlPayload {
  orgId: string;
  documentIds: string[];
  control: {
    controlId: string;
    controlTitle: string;
    controlStatement: string;
  };
}

export interface ValidatedDocChunk {
  chunkId: string;
  sectionTitle: string;
  pageRange: string;
}

export interface ValidatedSupportingDocument {
  documentId: string;
  documentName: string;
  validationStatus: string;
  confidence: {
    score: number;
    level: string;
  };
  found: string[];
  chunks: ValidatedDocChunk[];
  schedulingPresent: boolean;
}

export interface ValidateDocumentsResult {
  controlId: string;
  controlTitle: string;
  controlStatement: string;
  supportingDocuments: ValidatedSupportingDocument[];
  recommendation: {
    priority: string;
    summary: string;
    actions: string[];
  };
}

export function validateDocumentsForControl(
  payload: ValidateDocControlPayload,
): Promise<ApiEnvelope<ValidateDocumentsResult>> {
  return apiFetch('/content-registry/docengine/v2/validate/documents', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error.response?.status;
    const errorMessage = error.response?.data?.message || error.message;
    const method = String(error.config?.method ?? "get").toUpperCase();
    const requestUrl: string = error.config?.url || "";
    const responseUrl: string = error.request?.responseURL || "";
    const redirectNote =
      responseUrl && responseUrl !== requestUrl ? ` (responded from ${responseUrl})` : "";

    console.error(
      `[API Error] ${method} ${requestUrl} -> ${status ?? "no response"}${redirectNote}: ${errorMessage}`,
    );

    const notifyAccessDenied = () => {
      const firstErr = extractApiErrors(error)[0];
      const title = firstErr?.title || "Access Denied";
      const detail =
        firstErr?.detail ||
        firstErr?.title ||
        error.response?.data?.message ||
        "You don't have permission to perform this action.";
      const message = /contact/i.test(detail)
        ? detail
        : `${detail}. Please contact your administrator.`.replace(/\.\./g, ".");
      useToastStore.getState().showError(title, message);
      window.dispatchEvent(new CustomEvent("access-denied-error", {
        detail: { message }
      }));
    };

    if (status === 401) {
      // Login or refresh endpoint failures are not candidate for retry.
      if (requestUrl.includes(API_URL.AUTH) || requestUrl.includes(API_URL.REFRESH)) {
        return Promise.reject(error);
      }

      // Avoid infinite retry loop
      if (error.config?._retry) {
        console.warn(`[401] Unauthorized retry failed on ${requestUrl} - session expired`);
        handleSessionExpiration();
        return Promise.reject(error);
      }

      // Try to refresh the token if we have a refresh token
      const currentRefreshToken = useAuthStore.getState().getRefreshToken();
      if (currentRefreshToken && !isRefreshTokenExpired()) {
        console.warn(`[401] Attempting token refresh for ${requestUrl}`);
        try {
          const newToken = await refreshAccessToken();
          if (newToken) {
            const originalConfig = { ...error.config, _retry: true };
            originalConfig.headers = {
              ...(originalConfig.headers || {}),
              Authorization: `Bearer ${newToken}`,
            };
            return axios(originalConfig);
          }
        } catch (refreshError: any) {
          console.error("[refreshToken] Refresh failed, logging out:", refreshError.message);
          handleSessionExpiration();
          useAuthStore.getState().logout();
          return Promise.reject(refreshError);
        }
      }

      console.warn(`[401] Unauthorized on ${requestUrl} - session expired`);
      handleSessionExpiration();
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

axios.interceptors.request.use(
  (config) => {
    if (typeof navigator !== "undefined" && !navigator.onLine) {
      console.warn("[Request] Device is offline");
      return Promise.reject({ isOffline: true });
    }
    config.withCredentials = true;
    const isRefreshRequest = config.url && config.url.includes(API_URL.REFRESH);
    const token = useAuthStore.getState().getToken();
    if (token && !isRefreshRequest) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    console.error("[Request] Error preparing request:", error.message);
    return Promise.reject(error);
  }
);

const GCM_IV_BYTES = 12;
let gcmKeyPromise: Promise<CryptoKey> | null = null;
let gcmKeySecret: string | undefined;

const getGcmKey = (): Promise<CryptoKey> => {
  const secret = window.ENCRYPTION_SECRET;
  if (!secret) {
    return Promise.reject(
      new Error(
        "Encryption is not configured (missing window.ENCRYPTION_SECRET in appConfig.js).",
      ),
    );
  }
  const keyBytes = new TextEncoder().encode(secret);
  if (![16, 24, 32].includes(keyBytes.length)) {
    return Promise.reject(
      new Error("window.ENCRYPTION_SECRET must be 16, 24, or 32 characters."),
    );
  }
  if (!gcmKeyPromise || gcmKeySecret !== secret) {
    gcmKeySecret = secret;
    gcmKeyPromise = crypto.subtle.importKey(
      "raw",
      keyBytes,
      { name: "AES-GCM" },
      false,
      ["encrypt", "decrypt"],
    );
  }
  return gcmKeyPromise;
};

const encryptCipher = async (plaintext: string): Promise<string> => {
  const key = await getGcmKey();
  const iv = crypto.getRandomValues(new Uint8Array(GCM_IV_BYTES));
  const cipher = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    key,
    new TextEncoder().encode(plaintext),
  );
  const cipherBytes = new Uint8Array(cipher);
  const combined = new Uint8Array(iv.length + cipherBytes.length);
  combined.set(iv, 0);
  combined.set(cipherBytes, iv.length);
  let binary = "";
  for (let i = 0; i < combined.length; i++) {
    binary += String.fromCharCode(combined[i]);
  }
  return btoa(binary);
};

const decryptCipher = async (base64: string): Promise<string> => {
  const key = await getGcmKey();
  const binary = atob(base64);
  const combined = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) combined[i] = binary.charCodeAt(i);
  if (combined.length <= GCM_IV_BYTES) {
    throw new Error("Ciphertext too short to contain an iv.");
  }
  const iv = combined.slice(0, GCM_IV_BYTES);
  const cipherBytes = combined.slice(GCM_IV_BYTES);
  const plainBuf = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv },
    key,
    cipherBytes,
  );
  return new TextDecoder().decode(plainBuf);
};

export const authentication = async (
  username: string,
  password: string,
  rememberMe = false
): Promise<any> => {
  const config = {
    headers: { "Content-Type": "application/json" },
    withCredentials: true,
  };

  try {
    const response = await axios.post(
      API_URL.AUTH,
      { username, password },
      config
    );
    console.log("Authentication response:", response);
    console.log("Authentication response data:", response.data);

    // The backend sets the HttpOnly session cookie automatically.
    // We only persist user profile metadata (never tokens) for UI/RBAC use.
    const userData = response.data?.data;
    if (userData?.userId) {
      resetSessionExpiredAlert();
      const token = userData.accessToken || response.data?.accessToken || response.data?.token;
      const refreshToken = userData.refreshToken || response.data?.refreshToken;
      useAuthStore.getState().login({
        ...userData,
        ...(token ? { accessToken: token } : {}),
        ...(refreshToken ? { refreshToken } : {}),
      });
      await new Promise(resolve => setTimeout(resolve, 0));
    } else {
      console.error("Login response missing user data:", response.data);
      throw new Error("Login failed: No user data received");
    }
    return response.data;
  } catch (error: any) {
    throw error;
  }
};

export const refreshToken = async (): Promise<void> => {
  const token = await refreshAccessToken();
  if (!token) {
    throw new Error("Failed to refresh token");
  }
};

export const logoutUser = async (): Promise<void> => {
  resetSessionExpiredAlert();
  useAuthStore.getState().logout();
};

export const getAPI = async <T = any>(apiUrl: string, timeoutMs?: number): Promise<T> => {
  try {
    const config =
      timeoutMs === 0
        ? { timeout: 0, noTimeout: true }
        : timeoutMs
          ? { timeout: timeoutMs }
          : undefined;
    const response = await axios.get<T>(apiUrl, config);
    return response.data;
  } catch (error: any) {
    if (error?.isOffline) {
      throw new Error("OFFLINE");
    }
    throw error;
  }
};

export const postBlob = async (
  url: string,
  data: unknown,
  headers?: Record<string, string>,
): Promise<Blob> => {
  const response = await axios.post(url, data, {
    responseType: "blob",
    headers: { "Content-Type": "application/json", ...headers },
  });
  return response.data;
};

export const downloadFile = async (apiUrl: string, timeoutMs?: number) => {
  try {
    return await axios.get(apiUrl, {
      responseType: "blob",
      ...(timeoutMs ? { timeout: timeoutMs } : {}),
    });
  } catch (error: any) {
    if (error?.isOffline) {
      throw new Error("OFFLINE");
    }
    throw error;
  }
};

export const postAPI = async (apiUrl: string, data: any) => {
  try {
    const response = await axios.post(apiUrl, data, {
      headers: { "Content-Type": "application/json" },
    });
    return response.data;
  } catch (error: any) {
    if (error?.isOffline) {
      throw new Error("OFFLINE");
    }
    throw error;
  }
};

export const postMultipartAPI = async (apiUrl: string, data: any) => {
  try {
    const response = await axios.post(apiUrl, data, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  } catch (error: any) {
    if (error?.isOffline) {
      throw new Error("OFFLINE");
    }
    throw error;
  }
};

export const uploadSemanticRulePack = async (file: File) => {
  const formData = new FormData();
  formData.append("file", file);
  return postMultipartAPI(API_URL.SEMANTIC_RULE_PACKS, formData);
};

export const publishSemanticRulePack = async (id: string) => {
  return postAPI(SEMANTIC_RULE_PACKS_URL.publish(id), {});
};

export const getSemanticRulePacks = async (params?: Record<string, string>) => {
  const query = params ? "?" + new URLSearchParams(params).toString() : "";
  return getAPI(`${API_URL.SEMANTIC_RULE_PACKS}${query}`);
};

export const deleteSemanticRulePack = async (id: string) => {
  return deleteAPI(`${API_URL.SEMANTIC_RULE_PACKS}/${id}`);
};

export const getSemanticRules = async (params?: Record<string, string>) => {
  const query = params ? "?" + new URLSearchParams(params).toString() : "";
  return getAPI(`${API_URL.SEMANTIC_RULES}${query}`);
};

export const putAPI = async (apiUrl: string, data: any) => {
  try {
    const response = await axios.put(apiUrl, data, {
      headers: { "Content-Type": "application/json" },
    });
    return response.data;
  } catch (error: any) {
    if (error?.isOffline) {
      throw new Error("OFFLINE");
    }
    throw error;
  }
};

export const patchAPI = async (apiUrl: string, data: any) => {
  try {
    const response = await axios.patch(apiUrl, data, {
      headers: { "Content-Type": "application/json" },
    });
    return response.data;
  } catch (error: any) {
    if (error?.isOffline) {
      throw new Error("OFFLINE");
    }
    throw error;
  }
};

export const deleteAPI = async (apiUrl: string, data?: any) => {
  try {
    const response = await axios.delete(apiUrl, data !== undefined ? { data } : undefined);
    return response.data;
  } catch (error: any) {
    if (error?.isOffline) {
      throw new Error("OFFLINE");
    }
    throw error;
  }
};

export const fetchQuadrantRiskMap = async (parentId?: string) => {
  try {
    const url = parentId
      ? `${API_URL.QUADRANT_RISK_MAP}?parentId=${parentId}`
      : API_URL.QUADRANT_RISK_MAP;
    const response = await axios.get(url);
    return response.data?.data || response.data;
  } catch (error: any) {
    if (error?.isOffline) {
      throw new Error("OFFLINE");
    }
    throw error;
  }
};

export const fetchQuadrantEntityDetails = async (
  applicationId: string,
  options: { sourceType?: string; range?: string } = {},
) => {
  const sourceType = options.sourceType ?? "APPLICATION";
  const range = options.range ?? "week";
  try {
    const url = `${API_URL.QUADRANT_ENTITY_DETAILS}/${applicationId}/drill-down?source_type=${sourceType}&range=${range}`;
    const response = await axios.get(url);
    return response.data?.data || response.data;
  } catch (error: any) {
    if (error?.isOffline) {
      throw new Error("OFFLINE");
    }
    throw error;
  }
};

export interface ItComplianceScopeNode {
  id: string;
  name: string;
  type: "org" | "department" | "division" | "application" | "asset" | "group" | "cloud";
  appCode?: string | null;
  environments?: string[];
  assetNames?: string[];
  children?: ItComplianceScopeNode[];
}

export interface InheritedDevice {
  id: string;
  orgId?: string;
  name: string;
  type: string;
  status: string;
  ipAddress?: string;
  hostname?: string;
  osName?: string;
  osVersion?: string;
  cpuCores?: number;
  ramGb?: number;
  diskGb?: number;
  createdAt?: string;
  updatedAt?: string;
  [key: string]: unknown;
}

export interface InheritedComponent {
  componentId: string;
  title: string;
  componentType: string;
  devices: InheritedDevice[];
  cloudAccounts?: unknown[];
  awsResources?: unknown[];
  services?: InheritedService[];
}

export interface InheritedSspRef {
  sspId: string;
  title: string;
}

export interface InheritedService {
  serviceId: string;
  orgId?: string;
  name: string;
  serviceType: string;
  status: string;
  description?: string;
  components: InheritedComponent[];
  isUsed?: boolean;
  usedBySsps?: InheritedSspRef[];
}

export interface InheritedApplication {
  applicationId: string;
  applicationName: string;
  appCode: string;
  components: InheritedComponent[];
  services: InheritedService[];
}

export interface InheritanceEnvelope {
  status: string;
  apiVersion: string;
  requestId: string;
  timestamp: string;
  data: InheritedApplication[];
}

export type InheritanceMode = "application" | "service";

export type InheritanceOrigination = "inherited" | "implemented";

export interface InheritedComponentRow {
  id: string;
  title: string;
  componentType: string;
  status: string;
  description: string;
  provider: string;
  origination: InheritanceOrigination;
}

export interface InheritedInventoryRow {
  id: string;
  description: string;
  component: string;
  ip: string;
  status: string;
  provider: string;
  origination: InheritanceOrigination;
}

export interface InheritedControlRow {
  id: string;
  controlId: string;
  title: string;
  statement: string;
  component: string;
  provider: string;
  status: string;
  origination: InheritanceOrigination;
}

export interface SspInheritance {
  applications: InheritedApplication[];
  components: InheritedComponentRow[];
  inventory: InheritedInventoryRow[];
  controls: InheritedControlRow[];
}

async function fetchRawEnvelope<T>(path: string): Promise<T[]> {
  const res = await fetch(`${BASE_URL}${path}`, {
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...getAuthHeaders(),
    },
  });
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    const err = new Error(
      `Inheritance fetch failed: ${res.status} ${body || res.statusText}`,
    );
    (err as Error & { status?: number }).status = res.status;
    throw err;
  }
  const envelope = (await res.json()) as { data?: T[] };
  return envelope.data ?? [];
}

export function fetchApplicationsWithAssets(): Promise<InheritedApplication[]> {
  return fetchRawEnvelope<InheritedApplication>(
    "/ssps/applications/components-with-assets",
  );
}

export async function fetchServicesWithAssets(): Promise<InheritedApplication[]> {
  const services = await fetchRawEnvelope<InheritedService>(
    "/ssps/services/components-with-assets",
  );
  return [
    {
      applicationId: "services-root",
      applicationName: "Services",
      appCode: "SERVICES",
      components: [],
      services,
    },
  ];
}

export function fetchInheritanceByMode(
  mode: InheritanceMode,
): Promise<InheritedApplication[]> {
  return mode === "application"
    ? fetchApplicationsWithAssets()
    : fetchServicesWithAssets();
}

export interface TopologyAsset {
  assetId: string;
  ciType: string;
  status: string | null;
  role: string | null;
  arn: string | null;
  resourceId: string;
  resourceName: string;
  region: string;
}

export interface TopologyComponent {
  componentType: string;
  componentName: string;
  assets: TopologyAsset[];
}

export interface TopologyCloudAccount {
  cloudProviderId: string;
  provider: string;
  accountId: string;
  accountName: string;
  role: string;
  status: string;
  regions: string[];
  components: TopologyComponent[];
}

export interface TopologyData {
  applicationId: string;
  applicationName: string;
  cloudAccounts: TopologyCloudAccount[];
}

export interface ArchApplicationListItem {
  applicationId: string;
  applicationName: string;
  description: string | null;
  cloudAccountCount: number;
  providers: string[];
}

export function fetchArchApplications(): Promise<{ status: string; data: ArchApplicationListItem[] }> {
  return apiFetch(`/dashboard/architecture-compliance/applications`);
}

export function fetchTopologyByApplication(
  applicationId: string,
): Promise<{ status: string; data: TopologyData }> {
  return apiFetch(`/dashboard/architecture-compliance/topology?application_id=${applicationId}`);
}

export function fetchControlsByAccount(
  accountId: string,
): Promise<{ status: string; data: unknown }> {
  return apiFetch(`/dashboard/architecture-compliance/assets/${accountId}/controls`);
}

export function flattenInheritance(
  apps: InheritedApplication[],
): SspInheritance {
  const components: InheritedComponentRow[] = [];
  const inventory: InheritedInventoryRow[] = [];

  const seenComponentIds = new Set<string>();

  const walkComponent = (
    comp: InheritedComponent,
    provider: string,
  ): void => {
    if (!seenComponentIds.has(comp.componentId)) {
      seenComponentIds.add(comp.componentId);
      components.push({
        id: comp.componentId,
        title: comp.title,
        componentType: comp.componentType?.toLowerCase() || "software",
        status: "operational",
        description: deriveComponentDescription(comp, provider),
        provider,
        origination: "inherited",
      });
    }

    for (const dev of comp.devices ?? []) {
      inventory.push({
        id: dev.id,
        description: dev.name,
        component: comp.title,
        ip: dev.ipAddress || "—",
        status: _titleCase(dev.status || "Active"),
        provider,
        origination: "inherited",
      });
    }

    for (const svc of comp.services ?? []) {
      walkService(svc, provider);
    }
  };

  const walkService = (
    svc: InheritedService,
    parentProvider: string,
  ): void => {
    const providerLabel = svc.name ? `${svc.name} SSP` : parentProvider;
    for (const comp of svc.components ?? []) {
      walkComponent(comp, providerLabel);
    }
  };

  for (const app of apps) {
    const appProvider = `${app.applicationName} SSP`;
    for (const comp of app.components ?? []) {
      walkComponent(comp, appProvider);
    }
    for (const svc of app.services ?? []) {
      walkService(svc, appProvider);
    }
  }

  return { applications: apps, components, inventory, controls: [] };
}

function deriveComponentDescription(
  comp: InheritedComponent,
  provider: string,
): string {
  const deviceCount = (comp.devices ?? []).length;
  if (deviceCount > 0) {
    return `Inherited from ${provider}. Backed by ${deviceCount} device${deviceCount === 1 ? "" : "s"
      }.`;
  }
  return `Inherited from ${provider}.`;
}

function _titleCase(s: string): string {
  if (!s) return s;
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

export interface AuditLogItem {
  id: string;
  userId: string | null;
  displayName: string | null;
  module: string;
  actionType: string;
  changeSourceDetail: string;
  createdAt: string;
}

export interface AuditLogResponse {
  success: boolean;
  data?: {
    content: AuditLogItem[];
    totalElements: number;
    totalPages: number;
    number: number;
    size: number;
    numberOfElements: number;
    first: boolean;
    last: boolean;
    empty: boolean;
    pageable: {
      offset: number;
      pageNumber: number;
      pageSize: number;
    };
  };
  error?: {
    code: string;
    message: string;
    details: string | null;
  };
}

export interface SchedulerContentReviewItem {
  id: string;
  orgId: string;
  orgName: string;
  moduleName: string;
  frequency: string;
  runOn: string[];
  runTime: string;
  enabled: boolean;
  lastTriggeredAt: string | null;
  scheduleSummary: string;
  createdByName: string;
  updatedByName: string;
  createdAt: string;
  updatedAt: string;
}

export interface SchedulerContentReviewListPayload {
  orgId?: string;
  keyword?: string;
  sortByColumn?: string;
  sortingOrder?: "asc" | "desc" | "";
}

export interface SchedulerContentReviewListResponse {
  status: string;
  apiVersion: string;
  requestId: string;
  timestamp: string;
  data: SchedulerContentReviewItem[];
  pagination: { pageSize: number; totalCount: number } | null;
}

export function fetchSchedulerContentReviewList(
  pageNumber: number,
  itemsPerPage: number,
  payload?: SchedulerContentReviewListPayload,
): Promise<SchedulerContentReviewListResponse> {
  return apiFetch<SchedulerContentReviewListResponse>(
    `/scheduler/content-review/list/${pageNumber}/${itemsPerPage}`,
    {
      method: "POST",
      body: JSON.stringify(payload ?? {}),
    },
  );
}

export interface SchedulerContentReviewOrgOption {
  orgId: string;
  orgName: string;
  shortName: string;
  scheduleConfigured: boolean;
}

export interface SchedulerContentReviewOrganizationsResponse {
  status: string;
  apiVersion: string;
  requestId: string;
  timestamp: string;
  data: SchedulerContentReviewOrgOption[];
}

export function fetchSchedulerContentReviewOrganizations(): Promise<SchedulerContentReviewOrganizationsResponse> {
  return apiFetch<SchedulerContentReviewOrganizationsResponse>(
    `/scheduler/content-review/organizations`,
  );
}

export interface SchedulerContentReviewModule {
  moduleName: string;
  displayName: string;
}

export interface SchedulerContentReviewModulesResponse {
  status: string;
  apiVersion: string;
  requestId: string;
  timestamp: string;
  data: SchedulerContentReviewModule[];
}

let _contentReviewModulesCache: Promise<SchedulerContentReviewModulesResponse> | null = null;

export function fetchSchedulerContentReviewModules(): Promise<SchedulerContentReviewModulesResponse> {
  if (!_contentReviewModulesCache) {
    _contentReviewModulesCache = apiFetch<SchedulerContentReviewModulesResponse>(
      `/scheduler/content-review/modules`,
    ).catch((err) => {
      _contentReviewModulesCache = null;
      throw err;
    });
  }
  return _contentReviewModulesCache;
}

export interface SchedulerContentReviewSavePayload {
  id: string | null;
  orgId: string;
  moduleName?: string;
  frequency: string;
  runOn: string[];
  runTime: string;
  enabled: boolean;
}

export interface SchedulerContentReviewSaveResponse {
  status: string;
  apiVersion: string;
  requestId: string;
  timestamp: string;
  data: SchedulerContentReviewItem;
}

export function saveSchedulerContentReview(
  payload: SchedulerContentReviewSavePayload,
): Promise<SchedulerContentReviewSaveResponse> {
  return apiFetch<SchedulerContentReviewSaveResponse>(`/scheduler/content-review/save`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export interface SchedulerContentReviewGetResponse {
  status: string;
  apiVersion: string;
  requestId: string;
  timestamp: string;
  data: SchedulerContentReviewItem;
}

export function fetchSchedulerContentReviewById(
  id: string,
  moduleName?: string,
): Promise<SchedulerContentReviewGetResponse> {
  const qs = moduleName ? `?moduleName=${encodeURIComponent(moduleName)}` : "";
  return apiFetch<SchedulerContentReviewGetResponse>(
    `/scheduler/content-review/${encodeURIComponent(id)}${qs}`,
  );
}

export interface SchedulerContentReviewDeleteResponse {
  status: string;
  apiVersion: string;
  requestId: string;
  timestamp: string;
}

export function deleteSchedulerContentReview(
  id: string,
): Promise<SchedulerContentReviewDeleteResponse> {
  return apiFetch<SchedulerContentReviewDeleteResponse>(
    `/scheduler/content-review/delete/${encodeURIComponent(id)}`,
    { method: "DELETE" },
  );
}

export interface SchedulerAlertConfigItem {
  id: string;
  orgId: string;
  orgName: string;
  documentCategory: string;
  documentCategoryLabel: string;
  documentType?: string;
  leadAmount: number;
  leadUnit: string;
  leadUnitLabel: string;
  alertSummary: string;
  enabled: boolean;
  createdByName: string;
  updatedByName: string;
  createdAt: string;
  updatedAt: string;
}

export interface SchedulerAlertConfigListPayload {
  orgId?: string;
  documentCategory?: string;
  documentType?: string;
  enabled?: boolean;
  keyword?: string;
  sortByColumn?: string;
  sortingOrder?: "asc" | "desc";
}

export interface SchedulerAlertConfigListResponse {
  status: string;
  apiVersion: string;
  requestId: string;
  timestamp: string;
  data: SchedulerAlertConfigItem[];
  pagination: { pageSize: number; totalCount: number } | null;
}

export function fetchSchedulerAlertConfigList(
  pageNumber: number,
  itemsPerPage: number,
  payload?: SchedulerAlertConfigListPayload,
): Promise<SchedulerAlertConfigListResponse> {
  return apiFetch<SchedulerAlertConfigListResponse>(
    `/scheduler/alert-config/list/${pageNumber}/${itemsPerPage}`,
    {
      method: "POST",
      body: JSON.stringify(payload ?? {}),
    },
  );
}

export interface SchedulerAlertConfigSavePayload {
  id: string | null;
  orgId?: string;
  documentCategory: string;
  documentType?: string;
  leadAmount: number;
  leadUnit: string;
  enabled?: boolean;
}

export interface SchedulerAlertConfigSaveResponse {
  status: string;
  apiVersion: string;
  requestId: string;
  timestamp: string;
  data: SchedulerAlertConfigItem;
}

export function saveSchedulerAlertConfig(
  payload: SchedulerAlertConfigSavePayload,
): Promise<SchedulerAlertConfigSaveResponse> {
  return apiFetch<SchedulerAlertConfigSaveResponse>(`/scheduler/alert-config/save`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export interface SchedulerAlertConfigDeleteResponse {
  status: string;
  apiVersion: string;
  requestId: string;
  timestamp: string;
}

export function deleteSchedulerAlertConfig(
  id: string,
): Promise<SchedulerAlertConfigDeleteResponse> {
  return apiFetch<SchedulerAlertConfigDeleteResponse>(
    `/scheduler/alert-config/delete/${encodeURIComponent(id)}`,
    { method: "DELETE" },
  );
}

export interface SchedulerAlertConfigDocumentTypeOption {
  value: string;
  label: string;
}

export interface SchedulerAlertConfigCategoryOption {
  value: string;
  label: string;
  documentTypes: SchedulerAlertConfigDocumentTypeOption[];
}

export interface SchedulerAlertConfigLeadUnitOption {
  value: string;
  label: string;
  maxAmount: number;
}

export interface SchedulerAlertConfigFormOptions {
  categories: SchedulerAlertConfigCategoryOption[];
  leadUnits: SchedulerAlertConfigLeadUnitOption[];
}

export interface SchedulerAlertConfigFormOptionsResponse {
  status: string;
  apiVersion: string;
  requestId: string;
  timestamp: string;
  data: SchedulerAlertConfigFormOptions;
}

let _alertConfigFormOptionsCache: Promise<SchedulerAlertConfigFormOptionsResponse> | null = null;

export function fetchSchedulerAlertConfigFormOptions(): Promise<SchedulerAlertConfigFormOptionsResponse> {
  if (!_alertConfigFormOptionsCache) {
    _alertConfigFormOptionsCache = apiFetch<SchedulerAlertConfigFormOptionsResponse>(
      `/scheduler/alert-config/form-options`,
    ).catch((err) => {
      _alertConfigFormOptionsCache = null;
      throw err;
    });
  }
  return _alertConfigFormOptionsCache;
}

export interface AssistantConversation {
  id: string;
  title: string;
  status: string;
  createdAt: string;
}

export interface AssistantMessageDTO {
  id: string;
  role: "USER" | "ASSISTANT";
  content: string;
  sequenceNumber: number;
  createdAt: string;
}

export interface AssistantConversationMessage {
  id: string;
  role: "USER" | "ASSISTANT" | "TOOL" | "SYSTEM" | string;
  content: string | null;
  sequenceNumber?: number;
  createdAt: string;
}

export interface AssistantConversationSummary {
  id: string;
  title: string | null;
  status?: "ACTIVE" | "ARCHIVED";
  lastMessageAt: string | null;
  createdAt: string;
}

export interface AssistantConversationDetail extends AssistantConversationSummary {
  messages: AssistantConversationMessage[];
}

export interface AssistantUsage {
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
}

export interface AssistantIterationEvent {
  iteration: number;
}

export interface AssistantToolEvent {
  toolName: string;
  input: unknown;
  isError: boolean;
  durationMs: number;
  resultSizeBytes: number;
}

export interface AssistantTerminalEvent {
  conversationId: string;
  message: AssistantMessageDTO;
  stopReason: "END_TURN" | "MAX_TOKENS" | "STOP_SEQUENCE" | "UNKNOWN" | string;
  usage?: AssistantUsage;
  suggestedQuestions?: string[];
  toolCalls?: AssistantToolEvent[];
}

export interface AssistantTextDeltaEvent {
  text: string;
}

export interface AssistantErrorEvent {
  code: string;
  message: string;
}

export interface AssistantDoneEvent {
  conversationId: string;
}

export interface AssistantStreamHandlers {
  onIterationStart?: (e: AssistantIterationEvent) => void;
  onToolDispatched?: (e: AssistantToolEvent) => void;
  onTextDelta?: (e: AssistantTextDeltaEvent) => void;
  onAssistant?: (e: AssistantTerminalEvent) => void;
  onError?: (e: AssistantErrorEvent) => void;
  onDone?: (e: AssistantDoneEvent) => void;
}

export const createAssistantConversation = async (
  title: string,
): Promise<AssistantConversation> => {
  try {
    const response = await axios.post<ApiEnvelope<AssistantConversation>>(
      API_URL.AI_COMPANION_CONVERSATIONS,
      { title },
      { headers: { "Content-Type": "application/json" } },
    );
    return response.data.data;
  } catch (error: any) {
    if (error?.isOffline) {
      throw new Error("OFFLINE");
    }
    throw error;
  }
};

export const listAssistantConversations = async (): Promise<AssistantConversationSummary[]> => {
  try {
    const response = await axios.get<ApiEnvelope<AssistantConversationSummary[]>>(
      API_URL.AI_COMPANION_CONVERSATIONS,
    );
    const payload = response.data?.data as
      | AssistantConversationSummary[]
      | { items?: AssistantConversationSummary[]; conversations?: AssistantConversationSummary[] }
      | undefined;
    if (Array.isArray(payload)) return payload;
    return payload?.items ?? payload?.conversations ?? [];
  } catch (error: any) {
    if (error?.isOffline) {
      throw new Error("OFFLINE");
    }
    throw error;
  }
};

export const getAssistantConversation = async (
  id: string,
): Promise<AssistantConversationDetail> => {
  try {
    const response = await axios.get<ApiEnvelope<AssistantConversationDetail>>(
      `${API_URL.AI_COMPANION_CONVERSATIONS}/${id}`,
    );
    const detail = response.data?.data;
    return { ...detail, messages: detail?.messages ?? [] };
  } catch (error: any) {
    if (error?.isOffline) {
      throw new Error("OFFLINE");
    }
    throw error;
  }
};

export interface AssistantSuggestion {
  id: string;
  text: string;
  kind: "PAGE" | "DISCOVERY";
  module: string;
  screen: string;
  route: string | null;
  explored: boolean;
}

export interface AssistantSuggestionsResponse {
  route: string | null;
  screen: string | null;
  screenTitle: string | null;
  module: string | null;
  available: boolean;
  unavailableReason: string | null;
  source: "BASE" | "ENRICHED";
  enrichmentPending: boolean;
  suggestions: AssistantSuggestion[];
}

export interface AssistantSuggestionOrigin {
  id: string;
  kind: "PAGE" | "DISCOVERY";
  source: "BASE" | "ENRICHED";
}

export interface AssistantSelectedItem {
  id: string;
  name: string;
  parentName?: string;
}

export const fetchAssistantSuggestions = async (
  route: string | null,
  limit?: number,
  selectedItem?: AssistantSelectedItem | null,
  variant?: number,
  view?: string | null,
  screen?: string | null,
): Promise<AssistantSuggestionsResponse> => {
  try {
    const params = new URLSearchParams();
    if (limit) params.set("limit", String(limit));
    if (variant !== undefined && variant !== null) params.set("variant", String(variant));
    const query = params.toString();
    const url = query
      ? `${API_URL.AI_COMPANION_SUGGESTIONS}?${query}`
      : API_URL.AI_COMPANION_SUGGESTIONS;
    const current = {
      ...(route ? { route } : {}),
      ...(view ? { view } : {}),
      ...(screen ? { screen: screen.slice(0, 200) } : {}),
      ...(selectedItem ? { selectedItem } : {}),
    };
    const response = await axios.post<ApiEnvelope<AssistantSuggestionsResponse>>(
      url,
      Object.keys(current).length > 0 ? { current } : {},
      { headers: { "Content-Type": "application/json" } },
    );
    const payload = response.data?.data;
    return { ...payload, suggestions: payload?.suggestions ?? [] };
  } catch (error: any) {
    if (error?.isOffline) {
      throw new Error("OFFLINE");
    }
    throw error;
  }
};

function dispatchAssistantEvent(block: string, h: AssistantStreamHandlers) {
  let eventName = "message";
  const dataLines: string[] = [];
  for (const line of block.split("\n")) {
    if (!line || line.startsWith(":")) continue;
    if (line.startsWith("event:")) {
      eventName = line.slice(6).trim();
    } else if (line.startsWith("data:")) {
      const v = line.slice(5);
      dataLines.push(v.startsWith(" ") ? v.slice(1) : v);
    }
  }
  if (!dataLines.length) return;
  let payload: unknown;
  try {
    payload = JSON.parse(dataLines.join("\n"));
  } catch {
    return;
  }
  switch (eventName) {
    case "iteration_start":
      h.onIterationStart?.(payload as AssistantIterationEvent);
      break;
    case "tool_dispatched":
      h.onToolDispatched?.(payload as AssistantToolEvent);
      break;
    case "text_delta":
      h.onTextDelta?.(payload as AssistantTextDeltaEvent);
      break;
    case "assistant":
      h.onAssistant?.(payload as AssistantTerminalEvent);
      break;
    case "error":
      h.onError?.(payload as AssistantErrorEvent);
      break;
    case "done":
      h.onDone?.(payload as AssistantDoneEvent);
      break;
  }
}

export const streamAssistantMessage = async (
  conversationId: string,
  content: string,
  handlers: AssistantStreamHandlers,
  signal?: AbortSignal,
  route?: string | null,
  origin?: AssistantSuggestionOrigin | null,
  selectedItem?: AssistantSelectedItem | null,
  view?: string | null,
): Promise<void> => {
  const url = `${API_URL.AI_COMPANION_CONVERSATIONS}/${conversationId}/messages:stream`;
  const res = await fetch(url, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      Accept: "text/event-stream",
      ...getAuthHeaders(),
    },
    body: JSON.stringify({
      content,
      ...(route || view || selectedItem
        ? {
          context: {
            current: {
              ...(route ? { route } : {}),
              ...(view ? { view } : {}),
              ...(selectedItem ? { selectedItem } : {}),
            },
          },
        }
        : {}),
      ...(origin ? { origin } : {}),
    }),
    signal,
  });

  // In cookie auth, any 401 triggers session expiration
  if (res.status === 401) {
    handleSessionExpiration();
    const err = new Error("Session expired. Please login again.");
    err.name = "Session Expired";
    throw err;
  }
  if (!res.ok || !res.body) {
    const body = await res.text().catch(() => "");
    throw new Error(briefHttpError(res.status, body, res.statusText));
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder("utf-8");
  let buffer = "";

  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      buffer = buffer.replace(/\r\n/g, "\n");
      let idx: number;
      while ((idx = buffer.indexOf("\n\n")) !== -1) {
        const block = buffer.slice(0, idx);
        buffer = buffer.slice(idx + 2);
        if (block.trim()) dispatchAssistantEvent(block, handlers);
      }
    }
    if (buffer.trim()) dispatchAssistantEvent(buffer, handlers);
  } finally {
    try {
      reader.releaseLock();
    } catch {
    }
  }
};

/* ══════════════════════════════════════════════════════════════════════════
 * Configuration — Tools & Schedulers (port of g360's Administration module)
 *
 * These endpoints live on dedicated backend gateways whose paths already embed
 * their own version segment (/scheduler/api/v1/..., /administration/api/v1/...)
 * — the same convention the IT Compliance services use — so they are built off
 * the raw origin (window.BE_URL), NOT this app's uniform /api/v1 prefix. Auth is
 * attached automatically by the shared axios request interceptor above.
 * ════════════════════════════════════════════════════════════════════════ */

const SCHEDULER_PATH = (window.BE_URL ?? "") + "/stocApp/scheduler/api/v1/complianceScheduler";
const ALERT_CONFIG_PATH = (window.BE_URL ?? "") + "/administration/api/v1/alertConfig";
const TOOLS_PATH = (window.BE_URL ?? "") + "/api/v1/cmdb/tools";
const TOOL_CATALOG_PATH = (window.BE_URL ?? "") + "/api/v1/cmdb/tools/catalog";

export const configCurrentUser = (): string =>
  useAuthStore.getState().user?.username ?? "admin";

export interface ConfigResponseEnvelope<T = string> {
  headers?: Record<string, unknown>;
  body: T;
  statusCode?: string;
  statusCodeValue?: number;
}

export const isConfigApiSuccess = (res: ConfigResponseEnvelope<unknown>): boolean =>
  res?.statusCodeValue == null ||
  (res.statusCodeValue >= 200 && res.statusCodeValue < 300);

export interface DataImportItem {
  id: number;
  moduleName: string;
  time: string;
  alertDays: number;
  tool: string;
  instanceName?: string;
  environment?: string;
  lastTriggeredDateAndTime: string | null;
  importOn: string;
  importFrequency: string;
  status: boolean;
  createBy: string | null;
  updateBy: string | null;
  createdDt: string | null;
  updateDt: string | null;
}

export interface DataImportListResponse {
  data: DataImportItem[];
  totalCount: number;
}

export interface DataImportListPayload {
  keyword?: string;
  sortByColumn?: string;
  sortingOrder?: string;
}

export interface SchedulerSavePayload {
  id: number;
  moduleName: string;
  tool: string;
  instanceName: string;
  environment: string;
  importFrequency: string;
  importOn: string;
  time: string;
  alertDays: number;
  status: boolean;
  lastTriggeredDateAndTime?: string | null;
  createBy: string;
  createdDt?: string | null;
  updateBy: string;
  updateDt?: string | null;
}

export const fetchDataImportList = async (
  pageNumber = 0,
  itemsPerPage = 10,
  payload: DataImportListPayload = {},
): Promise<DataImportListResponse> => {
  const res = await postAPI(`${SCHEDULER_PATH}/list/${pageNumber}/${itemsPerPage}`, {
    keyword: payload.keyword || "",
    sortByColumn: payload.sortByColumn || "",
    sortingOrder: payload.sortingOrder || "",
  });
  return {
    data: res?.data ?? [],
    totalCount: res?.totalCount ?? 0,
  };
};

export const saveScheduler = async (
  username: string,
  payload: SchedulerSavePayload,
): Promise<ConfigResponseEnvelope> =>
  postAPI(`${SCHEDULER_PATH}/save/${encodeURIComponent(username)}`, payload);

export const updateSchedulerStatus = async (
  id: number,
  status: boolean,
  username: string,
): Promise<ConfigResponseEnvelope> =>
  putAPI(
    `${SCHEDULER_PATH}/status/${encodeURIComponent(String(id))}/${status}/${encodeURIComponent(username)}`,
    {},
  );

export const deleteScheduler = async (id: number): Promise<ConfigResponseEnvelope> =>
  deleteAPI(`${SCHEDULER_PATH}/delete/${encodeURIComponent(String(id))}`);

export type AlertConfigModule = "VM" | "CM" | "CMDB" | "STOC";
export type AlertConfigAlertType =
  | "TARGET_REMINDER"
  | "TARGET_EXPIRY"
  | "UNMAPPED_ASSET"
  | "SLA_BREACH"
  | "STOC_NOT_ONBOARDED"
  | "STOC_NOT_REPORTING"
  | "STOC_AGENT_FAILURE"
  | "STOC_CONFIG_DRIFT"
  | "STOC_POLICY_EXPIRED"
  | "STOC_VERSION_MISMATCH"
  | "STOC_MISSING_CONTROL";
export type AlertConfigDirection = "BEFORE" | "AFTER";
export type AlertConfigFrequency = "DAILY" | "WEEKLY" | "MONTHLY";

export interface AlertScheduleConfig {
  time: string;
  frequency: AlertConfigFrequency;
  interval: number | "";
  importOn: string;
  weeklyDays: string[] | string;
  monthlyDay: number | null;
}

export interface AlertStartOffset {
  value: string;
  direction: AlertConfigDirection | "";
  unit: string;
}

export interface AlertRuleConfig {
  startOffsetDays: AlertStartOffset;
  repeatDays: string;
  alertFrequency: string;
  alertInterval: string;
}

export interface AlertConfigCreatePayload {
  Module: AlertConfigModule;
  alertType: AlertConfigAlertType;
  subject: string;
  message: string;
  schedule: AlertScheduleConfig;
  alertConfig: AlertRuleConfig;
  isActive: boolean;
}

export interface AlertConfigItem extends AlertConfigCreatePayload {
  id?: number | string;
  module?: AlertConfigModule;
  organizationId?: string;
  createdBy?: string | null;
  createBy?: string | null;
  updatedBy?: string | null;
  updateBy?: string | null;
  createdDt?: string | null;
  updateDt?: string | null;
  createdDate?: string | null;
  updatedDate?: string | null;
}

export interface AlertConfigListResponse {
  data: AlertConfigItem[];
  totalCount: number;
}

export type AlertConfigInputType = "ALERT_BEFORE" | "INTERVAL_FREQUENCY" | "CADENCE";

export interface AlertConfigMetadataAlertType {
  code: AlertConfigAlertType;
  label: string;
  inputType: AlertConfigInputType;
  defaultSubject: string;
  defaultMessage: string;
}

export interface AlertConfigMetadataModule {
  code: AlertConfigModule;
  label: string;
  alertTypes: AlertConfigMetadataAlertType[];
}

export interface AlertConfigMetadata {
  modules: AlertConfigMetadataModule[];
  units: string[];
  frequencies: string[];
}

export const fetchAlertConfigMetadata = async (): Promise<AlertConfigMetadata> => {
  const res = await getAPI(`${ALERT_CONFIG_PATH}/metadata`);
  return {
    modules: res?.modules ?? [],
    units: res?.units ?? [],
    frequencies: res?.frequencies ?? [],
  };
};

const normalizeAlertConfigList = (data: unknown): AlertConfigItem[] => {
  if (Array.isArray(data)) return data as AlertConfigItem[];
  if (data && typeof data === "object") {
    const body = data as Record<string, unknown>;
    const candidates = [body.content, body.data, body.body, body.alertConfigs, body.alertConfig];
    for (const candidate of candidates) {
      if (Array.isArray(candidate)) return candidate as AlertConfigItem[];
    }
    if (body.id || body.alertType || body.Module || body.module) {
      return [body as unknown as AlertConfigItem];
    }
  }
  return [];
};

export const fetchEmailAlertConfigs = async (
  organizationId = "DEFAULT",
): Promise<AlertConfigListResponse> => {
  const res = await getAPI(`${ALERT_CONFIG_PATH}/${encodeURIComponent(organizationId)}`);
  const data = normalizeAlertConfigList(res);
  return { data, totalCount: data.length };
};

export const createEmailAlertConfig = async (
  organizationId: string,
  payload: AlertConfigCreatePayload,
): Promise<unknown> =>
  postAPI(`${ALERT_CONFIG_PATH}/${encodeURIComponent(organizationId)}`, payload);

export const activateEmailAlertConfig = async (
  organizationId: string,
  module: AlertConfigModule,
  alertType: AlertConfigAlertType,
): Promise<unknown> =>
  postAPI(
    `${ALERT_CONFIG_PATH}/${encodeURIComponent(organizationId)}/${encodeURIComponent(module)}/${encodeURIComponent(alertType)}/activate`,
    {},
  );

export const deactivateEmailAlertConfig = async (
  organizationId: string,
  module: AlertConfigModule,
  alertType: AlertConfigAlertType,
): Promise<unknown> =>
  postAPI(
    `${ALERT_CONFIG_PATH}/${encodeURIComponent(organizationId)}/${encodeURIComponent(module)}/${encodeURIComponent(alertType)}/deactivate`,
    {},
  );

export const deleteEmailAlertConfig = async (
  organizationId: string,
  module: AlertConfigModule,
  alertType: AlertConfigAlertType,
): Promise<unknown> =>
  deleteAPI(
    `${ALERT_CONFIG_PATH}/${encodeURIComponent(organizationId)}/${encodeURIComponent(module)}/${encodeURIComponent(alertType)}`,
  );

export type ToolStatus = "ACTIVE" | "INACTIVE";

export type ToolFieldKey =
  | "userName"
  | "password"
  | "url"
  | "apiToken"
  | "accessKey"
  | "secretAccessKey"
  | "model";

export interface ToolApiRecord {
  id: number;
  toolName: string;
  type: string;
  userName: string;
  password: string;
  toolUrl: string;
  apiToken: string;
  sslValue: boolean;
  toolStatus: ToolStatus;
  serverConfig: string;
  portNumber: number;
  accessKey: string;
  secretAccessKey: string;
  config: boolean;
  instanceName?: string;
  environment?: string;
  version?: string;
}

export interface ToolApiDetail extends Omit<ToolApiRecord, "toolUrl" | "config"> {
  toolUrl?: string;
  url?: string;
  createBy?: string;
  updateBy?: string;
  createdDt?: string;
  updateDt?: string;
}

export interface ToolInstanceSavePayload {
  id?: number;
  orgId?: string;
  toolName?: string;
  instanceName?: string;
  version?: string;
  environment?: string;
  sslValue?: boolean;
  userName?: string;
  password?: string;
  toolUrl?: string;
  apiToken?: string;
  accessKey?: string;
  secretAccessKey?: string;
  model?: string;
}

export interface ToolListResponse {
  data: ToolApiRecord[];
  totalCount: number;
}

export interface TestToolConnectionPayload {
  toolName: string;
  toolUrl: string;
  userName?: string | null;
  password?: string | null;
  apiToken?: string | null;
  accessKey?: string | null;
  secretAccessKey?: string | null;
  sslValue: boolean;
}

export interface TestToolConnectionResult {
  success: boolean;
  message: string;
}

export const fetchToolsList = async (
  pageNumber: number,
  itemsPerPage: number,
  keyword = "",
): Promise<ToolListResponse> => {
  const res = await postAPI(
    `${TOOLS_PATH}/${pageNumber}/${itemsPerPage}`,
    { keyword },
  );
  return {
    data: res?.data ?? res?.content ?? [],
    totalCount: res?.pagination?.totalCount ?? res?.totalCount ?? res?.totalElements ?? 0,
  };
};

export interface ToolInstanceListRequest {
  keyword?: string;
  toolName?: string;
  environment?: string;
  sortByColumn?: string;
  sortingOrder?: "asc" | "desc";
  organizationId?: string;
}

export const fetchToolAccessibleOrgs = async (): Promise<OrgUnit[]> => {
  const res = await getAPI(
    `${API_URL.ACCESSIBLE_ORGS}?module=cmdb&action=view&org_type=ORGANIZATION&org_type=GROUP`,
  );
  return res?.data?.organizations ?? [];
};

export const fetchToolInstances = async (
  pageNumber: number,
  itemsPerPage: number,
  body: ToolInstanceListRequest,
): Promise<ToolListResponse> => {
  const res = await postAPI(`${TOOLS_PATH}/${pageNumber}/${itemsPerPage}`, body);
  return {
    data: res?.data ?? res?.content ?? [],
    totalCount: res?.pagination?.totalCount ?? res?.totalCount ?? res?.totalElements ?? 0,
  };
};

export const saveToolInstance = async (
  payload: ToolInstanceSavePayload,
): Promise<ToolApiRecord> => {
  const res = await postAPI(`${TOOLS_PATH}/save`, payload);
  return res?.data ?? res;
};

export const deleteTool = async (id: number): Promise<void> => {
  await deleteAPI(`${TOOLS_PATH}/${encodeURIComponent(String(id))}`);
};

export const updateToolStatus = async (
  id: number,
  status: ToolStatus,
): Promise<ToolApiRecord> => {
  const res = await putAPI(`${TOOLS_PATH}/${encodeURIComponent(String(id))}/status/${status}`, {});
  return res?.data ?? res;
};

export interface ToolCardSummary {
  id: number;
  type: string;
  toolName: string;
  instanceCount: number;
  organizationId: string;
}

export const fetchToolCardSummary = async (keyword = ""): Promise<ToolCardSummary[]> => {
  const res = await getAPI(
    `${TOOLS_PATH}/plugin-details${keyword ? `?keyword=${encodeURIComponent(keyword)}` : ""}`,
  );
  return res?.data ?? [];
};

export const deleteToolPluginDetails = async (id: number): Promise<number> => {
  const res = await deleteAPI(`${TOOLS_PATH}/plugin-details/${encodeURIComponent(String(id))}`);
  return typeof res?.data === "number" ? res.data : 0;
};

export interface ToolCatalogFieldDef {
  name: ToolFieldKey;
  required: boolean;
}

export interface ToolCatalogDetail {
  category: string;
  name: string;
  fields: ToolCatalogFieldDef[];
  config: boolean;
}

export const fetchToolCatalogByName = async (toolName: string): Promise<ToolCatalogDetail> => {
  const res = await getAPI(`${TOOL_CATALOG_PATH}?toolName=${encodeURIComponent(toolName)}`);
  return res?.data ?? res;
};

export const fetchToolCatalogNames = async (): Promise<string[]> => {
  const res = await getAPI(`${TOOL_CATALOG_PATH}/names`);
  return Array.isArray(res) ? res : (res?.data ?? []);
};

export interface ToolCatalogSaveResult {
  category: string;
  name: string;
  config: boolean;
  fields: { name: ToolFieldKey; required: boolean }[];
}

export const saveToolCatalog = async (
  body: { toolName: string; previousToolName?: string },
): Promise<ToolCatalogSaveResult> => {
  const res = await postAPI(`${TOOL_CATALOG_PATH}`, body);
  return res?.data ?? res;
};

export interface PluginDetailsSaveResult {
  id: string;
  organizationId: string;
  toolName: string;
  type: string;
  config: boolean;
  instanceCount: number;
}

export const savePluginDetails = async (
  body: { toolName?: string; organizationId?: string; id?: string | number; type?: string; config?: boolean },
): Promise<PluginDetailsSaveResult> => {
  const res = await postAPI(`${TOOLS_PATH}/plugin-details/save`, body);
  return res?.data ?? res;
};

export const fetchToolNames = async (toolType: string): Promise<string[]> =>
  getAPI(`${TOOLS_PATH}/getToolsName/${encodeURIComponent(toolType)}`);

export const testToolConnection = async (
  payload: TestToolConnectionPayload,
): Promise<TestToolConnectionResult> => {
  const res = await postAPI(`${TOOLS_PATH}/connection/check`, payload);
  return res?.data ?? res;
};

const SEVERITY_COLOR_PATH = (window.BE_URL ?? "") + "/administration/api/v1/severityColorConfig";
export const SEVERITY_COLOR_APP_NODE_ID = "2df8139f-86c7-4eb8-b981-5da95235bc6c";
export const SEVERITY_DEFAULT_PALETTE_ID = "default";

export type SeverityColorKey = "critical" | "high" | "medium" | "low" | "notAvailable";

export const SEVERITY_COLOR_KEYS: readonly SeverityColorKey[] = [
  "critical",
  "high",
  "medium",
  "low",
  "notAvailable",
];

export interface SeverityColorEntry {
  key: SeverityColorKey;
  color: string;
}

export type SeverityColorMap = Record<SeverityColorKey, string>;

export interface SeverityColorConfig {
  applicationNodeId: string;
  activePaletteId: string | null;
  colors: SeverityColorEntry[];
  updatedBy: string | null;
  updatedDt: string | null;
}

export interface SeverityColorSavePayload {
  applicationNodeId: string;
  activePaletteId: string | null;
  colors: SeverityColorEntry[];
}

export interface SeverityColorSaveResponse {
  message?: string;
  status?: number;
  statusCodeValue?: number;
}

export const DEFAULT_SEVERITY_COLOR_MAP: SeverityColorMap = {
  critical: "#EF4444",
  high: "#F97316",
  medium: "#FFB84D",
  low: "#00B8D4",
  notAvailable: "#94A3B8",
};

export const severityColorForKey = (map: SeverityColorMap, key: SeverityColorKey): string => {
  switch (key) {
    case "critical":
      return map.critical;
    case "high":
      return map.high;
    case "medium":
      return map.medium;
    case "low":
      return map.low;
    case "notAvailable":
      return map.notAvailable;
    default:
      return "#000000";
  }
};

export const severityEntriesToMap = (entries: SeverityColorEntry[]): SeverityColorMap => {
  const find = (key: SeverityColorKey) =>
    entries.find((e) => e.key === key)?.color ?? severityColorForKey(DEFAULT_SEVERITY_COLOR_MAP, key);
  return {
    critical: find("critical"),
    high: find("high"),
    medium: find("medium"),
    low: find("low"),
    notAvailable: find("notAvailable"),
  };
};

export const severityMapToEntries = (map: SeverityColorMap): SeverityColorEntry[] =>
  SEVERITY_COLOR_KEYS.map((key) => ({ key, color: severityColorForKey(map, key) }));

export const fetchSeverityColorConfig = async (
  applicationNodeId: string = SEVERITY_COLOR_APP_NODE_ID,
): Promise<SeverityColorConfig> => {
  try {
    return await getAPI(`${SEVERITY_COLOR_PATH}?applicationNodeId=${encodeURIComponent(applicationNodeId)}`);
  } catch {
    return {
      applicationNodeId,
      activePaletteId: SEVERITY_DEFAULT_PALETTE_ID,
      colors: severityMapToEntries(DEFAULT_SEVERITY_COLOR_MAP),
      updatedBy: null,
      updatedDt: null,
    };
  }
};

export const saveSeverityColorConfig = async (
  payload: SeverityColorSavePayload,
): Promise<SeverityColorSaveResponse> => {
  return await postAPI(`${SEVERITY_COLOR_PATH}/save`, payload);
};

export const resetSeverityColorConfig = async (
  applicationNodeId: string = SEVERITY_COLOR_APP_NODE_ID,
  activePaletteId: string | null = SEVERITY_DEFAULT_PALETTE_ID,
): Promise<SeverityColorSaveResponse> =>
  saveSeverityColorConfig({
    applicationNodeId,
    activePaletteId: activePaletteId ?? SEVERITY_DEFAULT_PALETTE_ID,
    colors: severityMapToEntries(DEFAULT_SEVERITY_COLOR_MAP),
  });

export interface AlertCalendarFilters {
  orgId?: string;
  orgName?: string;
  documentCategory?: string;
  documentType?: string;
  keyword?: string;
}

export async function fetchAlertCalendarMonth(
  year: number,
  month: number,
  filters: AlertCalendarFilters = {},
) {
  return await postAPI(`${API_URL.ALERT_CALENDAR}/month/${year}/${month}`, filters);
}

export async function fetchAlertCalendarWeek(
  date: string,
  filters: AlertCalendarFilters = {},
) {
  return await postAPI(`${API_URL.ALERT_CALENDAR}/week/${date}`, filters);
}

export async function fetchAlertCalendarDay(
  date: string,
  filters: AlertCalendarFilters = {},
) {
  return await postAPI(`${API_URL.ALERT_CALENDAR}/day/${date}`, filters);
}

export async function fetchAlertCalendarEntry(documentId: string, date: string) {
  return await getAPI(`${API_URL.ALERT_CALENDAR}/entry/${documentId}/${date}`);
}

export type DocumentReviewStatus =
  | "OVERDUE_REVIEW_IDENTIFIED"
  | "REVIEW_IN_PROGRESS"
  | "REVIEW_ON_HOLD"
  | "EXCEPTION_APPROVED"
  | "VALIDATION_AND_APPROVAL"
  | "CLOSED";

export interface DocumentReviewCard {
  id: string;
  itemKey: string;
  title: string;
  documentId: string;
  documentCategory: string;
  documentType: string;
  categoryLabel: string;
  documentVersion: number;
  orgId: string;
  orgName: string;
  status: DocumentReviewStatus;
  statusLabel: string;
  reviewEndDate: string;
  daysOverdue: number;
  badge: string;
  exposureControlCount: number;
  exposureSspCount: number;
  exposureLabel: string;
  ownerId: string | null;
  ownerName: string | null;
  statusChangedAt: string;
  daysInStage: number;
  holdReason?: string | null;
  resumeByDate?: string | null;
  exceptionExpiresOn?: string | null;
  closedOutcome?: string | null;
}

export interface DocumentReviewFilters {
  orgId?: string | null;
  status?: DocumentReviewStatus | null;
  documentCategory?: string | null;
  documentType?: string | null;
  ownerId?: string | null;
  keyword?: string | null;
  sortByColumn?: string;
  sortingOrder?: "asc" | "desc";
}

export interface DocumentReviewColumn {
  status: DocumentReviewStatus;
  label: string;
  count: number;
  cards: DocumentReviewCard[];
}

export interface DocumentReviewBoardResponse {
  status: string;
  apiVersion: string;
  requestId: string;
  timestamp: string;
  data: { totalCards: number; columns: DocumentReviewColumn[] };
}

export async function fetchDocumentReviewBoard(
  filters: DocumentReviewFilters = {},
): Promise<DocumentReviewBoardResponse> {
  return await postAPI(`${API_URL.DOCUMENT_REVIEW}/board`, filters);
}

export interface DocumentReviewListResponse {
  status: string;
  apiVersion: string;
  requestId: string;
  timestamp: string;
  data: DocumentReviewCard[];
  pagination: { pageSize: number; totalCount: number } | null;
}

export async function fetchDocumentReviewList(
  pageNumber: number,
  itemsPerPage: number,
  filters: DocumentReviewFilters = {},
): Promise<DocumentReviewListResponse> {
  return await postAPI(
    `${API_URL.DOCUMENT_REVIEW}/list/${pageNumber}/${itemsPerPage}`,
    filters,
  );
}

export async function fetchDocumentReviewById(id: string) {
  return await getAPI(`${API_URL.DOCUMENT_REVIEW}/${encodeURIComponent(id)}`);
}

export interface DocumentReviewStagePayload {
  id: string;
  status: DocumentReviewStatus;
  reason?: string;
  resumeByDate?: string | null;
  expiresOn?: string | null;
  approverId?: string | null;
  submittedVersion?: string | null;
}

export interface DocumentReviewStageResponse {
  status: string;
  apiVersion: string;
  requestId: string;
  timestamp: string;
  data: DocumentReviewCard;
}

export async function moveDocumentReviewStage(
  payload: DocumentReviewStagePayload,
): Promise<DocumentReviewStageResponse> {
  return await postAPI(`${API_URL.DOCUMENT_REVIEW}/stage`, payload);
}

export { normalizeDriftDetailStatus, deriveScanCountsFromHistory } from "../app/pages/grc/driftNormalization";
